import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import PdfGoruntuleyici from '../components/Common/PdfGoruntuleyici';
import MuvekkilGorunumu from '../components/Common/MuvekkilGorunumu';
import WhatsAppGonder from '../components/Common/WhatsAppGonder';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { caseFileAPI, documentAPI, masrafAPI, masrafRequestAPI, taskAPI, archiveRequestAPI, vekaletAPI, userAPI, noteAPI, jobAPI } from '../api/api';
import { belgeMetniCikar, metniPdfOlarakYazdir, resmiPdfOlarakYazdir } from '../utils/belgeOnizleme';
import { CASE_FILE_STATUS, CASE_FILE_STATUS_LABELS, DAVA_TURU, DAVA_TURU_LABELS, davaTuruEtiketi, DOCUMENT_TYPE, DOCUMENT_TYPE_LABELS, getStatusLabel, getSortedDavaTuruEntries, TASK_STATUS, TASK_STATUS_LABELS, ICRA_KONUSU_OPTIONS } from '../utils/constants';
import { toISODateOrNull, formatDateForDisplay, formatJobDueDateForDisplay, formatDateForDateInput, formatDateOnly, getTodayDateString } from '../utils/dateUtils';
import { downloadBlob } from '../utils/fileDownload';
import MasrafForm from '../components/Masraflar/MasrafForm';
import HesapDetayi from '../components/Masraflar/HesapDetayi';
import IcraHesap from '../components/Masraflar/IcraHesap';
import CaseFileForm from '../components/CaseFiles/CaseFileForm';
import ArabuluculukBolumu from '../components/CaseFiles/ArabuluculukBolumu';
import TaskForm from '../components/Tasks/TaskForm';
import NoteForm from '../components/Notes/NoteForm';
import JobForm from '../components/Jobs/JobForm';
import Icon from '../components/Common/Icon';
import ActionIcon from '../components/Common/ActionIcon';
import { canAccessOzelDosyalar } from '../utils/ozelDosyaAccess';
import { enqueueDocumentUploads } from '../utils/backgroundUploads';
import { isNativeApp, markNativeFilePicker } from '../utils/nativePicker';
import { kameraVarMi, evrakFotografiCek } from '../utils/kamera';
import YatayKaydirma from '../components/Common/YatayKaydirma';
import KayanSecici from '../components/Common/KayanSecici';
import BosDurum from '../components/Common/BosDurum';
import { YENI_TASARIM } from '../utils/yeniTasarim';
import { onayla } from '../utils/onayla';
import { BelgeAlaniSekmeleri, OdemeSekmeleri, OdemeListesi, BelgeHedefSecici, paraYaz } from '../components/FaturaOdemeler/FaturaOdemeler';
import { odemeleriAyir, toplamTutar, odemeBelgesiSekmesi, aramaMetni } from '../utils/odemeSiniflandir';
import { makbuzOku } from '../utils/makbuzOku';
import './CaseFileDetail.css';

/* Dokunmatik ekranda (telefon, tablet) satirlar suruklenebilir yapilmaz.
   iPhone'da suruklenebilir satirin ustundeki parmak hareketi kaydirma
   yerine surukleme sayiliyordu; belge tablosu yana kaydirilamiyor,
   sagdaki "Muvekkil" kutusuna ulasilamiyordu. Surukleyerek siralama
   zaten yalnizca fareyle calisiyor. */
const dokunmatikEkran = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

/* "Masraflar ve Ödemeler" sekmelerinde duran belge türleri (belge listesinde görünmez). */
const ODEME_BELGE_SEKMESI = {
  [DOCUMENT_TYPE.MasrafBelgesi]: 'masraf',
  [DOCUMENT_TYPE.GelenOdemeDekontu]: 'gelen',
  [DOCUMENT_TYPE.MuvekkileOdemeDekontu]: 'muvekkile',
  [DOCUMENT_TYPE.Makbuz]: 'makbuz',
};
/* Sekmeye eklenen belgenin türü. */
const SEKME_BELGE_TURU = {
  masraf: DOCUMENT_TYPE.MasrafBelgesi,
  gelen: DOCUMENT_TYPE.GelenOdemeDekontu,
  muvekkile: DOCUMENT_TYPE.MuvekkileOdemeDekontu,
  makbuz: DOCUMENT_TYPE.Makbuz,
};
const belgeTuruOf = (d) => d?.DocumentType ?? d?.documentType ?? 0;
const odemeSekmesiOf = (d) => ODEME_BELGE_SEKMESI[belgeTuruOf(d)] || null;
const turunSekmesi = (tur) => ODEME_BELGE_SEKMESI[tur] || null;
const makbuzBelgesi = (d) => belgeTuruOf(d) === DOCUMENT_TYPE.Makbuz;
const SEKME_ADI = { belgeler: 'Belgeler', masraf: 'Masraflar', gelen: 'Gelen Ödemeler', muvekkile: 'Müvekkile Ödenen', makbuz: 'Makbuzlar' };
/* Belgeler alanının kendi türleri ("Nereye Eklensin?" → Belgeler seçilince). */
const GENEL_BELGE_TURLERI = [DOCUMENT_TYPE.Other, DOCUMENT_TYPE.Vekaletname, DOCUMENT_TYPE.KazaTespitTutanagi, DOCUMENT_TYPE.IbranameMuvafakatname];

/* Seçilen dosyanın kimliği: listede sırası değişse ya da biri çıkarılsa da
   makbuzdan okunan tarih ve tutar o dosyaya bağlı kalsın. */
const dosyaAnahtari = (f) => `${f?.name}|${f?.size}|${f?.lastModified}`;

/* Makbuz PDF'i okunurken ve okunduktan sonra dosyanın altında yazan satır. */
const MakbuzOkumaDurumu = ({ bilgi }) => {
  const b = bilgi || {};
  if (!b.durum) return null;
  if (b.durum === 'okunuyor') {
    return <p className="mk-durum"><span className="mk-donen" aria-hidden="true" /> Makbuz okunuyor…</p>;
  }
  const uyarilar = (b.uyarilar || []).join(' ');
  if (b.durum === 'tamam') {
    return (
      <p className="mk-durum tamam">
        ✓ Makbuzdan okundu{b.tarihEtiketi ? ` (${b.tarihEtiketi}, ${b.tutarEtiketi})` : ''}. Kontrol edin. {uyarilar}
      </p>
    );
  }
  const eksik = b.okunanTarih
    ? 'Makbuzdan yalnız tarih okunabildi; net tutarı girin.'
    : b.okunanTutar
      ? 'Makbuzdan yalnız net tutar okunabildi; tarihi girin.'
      : 'Makbuzda tarih ve net tutar bulunamadı; elle girin.';
  const metin = {
    eksik,
    metinYok: 'Bu PDF\'te yazı yok (taranmış olabilir); tarih ve net tutarı elle girin.',
    pdfDegil: 'Fotoğraf ya da resim makbuz okunamıyor; tarih ve net tutarı elle girin.',
    hata: 'Makbuz okunamadı; tarih ve net tutarı elle girin.',
  }[b.durum];
  return <p className="mk-durum uyari">{metin} {uyarilar}</p>;
};

const hasDetailValue = (v) => {
  if (v === null || v === undefined || v === false) return false;
  if (typeof v === 'number') return Number.isFinite(v) && v !== 0;
  const s = String(v).trim();
  return s !== '' && s !== '-' && s !== 'Seçiniz' && s !== 'N/A' && s !== '------';
};


/**
 * Dosya alanlarının okunur adları.
 * "Diğer Bilgiler" bölümünde, başka hiçbir yerde gösterilmeyen dolu
 * alanları adlandırmak için kullanılır.
 */
const ALAN_ETIKETLERI = {
  FoyNo: 'Föy No',
  Muvekkil: 'Müvekkil',
  MuvekkilTC: 'TC / Vergi No',
  MuvekkilTelefon: 'Müvekkil Telefon',
  MuvekkilAdres: 'Müvekkil Adres',
  MuvekkilIBAN: 'Müvekkil IBAN',
  MuvekkilBabaAdi: 'Baba Adı',
  MuvekkilAnaAdi: 'Ana Adı',
  MuvekkilDogumYeri: 'Doğum Yeri',
  MuvekkilDogumTarihi: 'Doğum Tarihi',
  SirketYetkilisiAdi: 'Şirket Yetkilisi',
  SirketYetkilisiTC: 'Yetkili TC',
  MuvekkilPlaka: 'Müvekkil Plaka',
  MuvekkilAracRolu: 'Müvekkil Araç Rolü',
  MuvekkilAracSigortaSirketi: 'Müvekkil Araç Sigorta Şirketi',
  KarsiTaraf1: 'Karşı Taraf 1',
  KarsiTaraf2: 'Karşı Taraf 2',
  KarsiTarafPlaka: 'Karşı Taraf Plaka',
  KarsiTarafSigortaSirketi: 'Karşı Taraf Sigorta Şirketi',
  KarsiTarafSigortaSirketiVekili: 'Karşı Vekil',
  KazaTarihi: 'Kaza Tarihi',
  KazaSehri: 'Kaza Şehri',
  MahkemaBilgisi: 'Mahkeme Bilgisi',
  MahkemeEsas: 'Esas No',
  DurusmaTarihi: 'Duruşma Tarihi ve Saati',
  IcraDosyaNo: 'İcra Dosya No',
  TahkimBasvuruNo: 'Tahkim Başvuru No',
  TahkimBasvuruTarihi: 'Tahkim Başvuru Tarihi',
  ItirazBasvuruNo: 'İtiraz Başvuru No',
  SavcilikCezaDosyaNo: 'Savcılık / Ceza Dosya No',
  SigortaBasvuruTarihi: 'Sigorta Başvuru Tarihi',
  DavaAcilisTarihi: 'Dava Açılış Tarihi',
  DosyaGelisTarihi: 'Dosya Geliş Tarihi',
  RaporTarihi: 'Rapor Tarihi',
  KesinlesmeTarihi: 'Kesinleşme Tarihi',
  IslahTarihi: 'Islah Tarihi',
  BilirkisiTarihi: 'Bilirkişi Tarihi',
  ArabuluculukTarihi: 'Arabuluculuk Başvuru Tarihi',
  ArabuluculukSonucu: 'Arabuluculuk Sonucu',
  ArabuluculukDosyaNo: 'Arabuluculuk Dosya No',
  SonTutanakTarihi: 'Son Tutanak Tarihi',
  IscilikDavaTarihi: 'İşçilik Dava Tarihi',
  IsverenFirma: 'İşveren / Şirket',
  IsverenTcvkn: 'İşveren T.C / VKN',
  IsverenYetkilisi: 'İşveren Yetkilisi',
  IsverenAdresi: 'İşveren Adresi',
  SgkSicilNo: 'SGK Sicil No',
  IseGirisTarihi: 'İşe Giriş Tarihi',
  IstenCikisTarihi: 'İşten Çıkış Tarihi',
  CikisNedeni: 'Çıkış Nedeni',
  SonUcret: 'Son Ücret',
  EsAdSoyad: 'Eş Ad Soyad',
  EsTC: 'Eş TC',
  EvlilikTarihi: 'Evlilik Tarihi',
  AyrilikTarihi: 'Ayrılık Tarihi',
  SucTuru: 'Suç Türü',
  SucYeri: 'Suç Yeri',
  TckMaddesi: 'TCK Maddesi',
  SulhDurumu: 'Sulh Durumu',
  IbraAsamasi: 'İbra Aşaması',
  GelenOdemeDurumu: 'Gelen Ödeme Durumu',
  GidenOdemeDurumu: 'Giden Ödeme Durumu',
  SMM: 'SMM',
  SozlesmeOrani: 'Sözleşme Oranı',
  TakipAcilisMiktari: 'Takip Açılış Miktarı',
  IcraVekaletUcretiManuel: 'İcra Vekalet Ücreti (elle)',
  Aciklama: 'Açıklama',
  Notlar: 'Notlar',
};

/** Ekranda gösterilmeyecek sistem alanları. */
const GIZLI_ALANLAR = new Set([
  'Id', 'IsDeleted', 'IsOzel', 'CreatedDate', 'UpdatedDate', 'SorumluId',
  'ReferansId', 'Status', 'DavaTuru', 'DavaTuruDiger', 'DetayAlanGoster',
  'CocuklarJson', 'TaraflarJson', 'IcraHesapAyar', 'OdemeBilgileriGoster',
  'DurusmaGirildi', 'ArsivNo', 'SorumluAdi', 'ReferansAdi', 'User', 'Referans',
]);

const parseDetayAlanGoster = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  try {
    const o = JSON.parse(raw);
    return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
};

const DetailField = ({ label, show, always, full, className, children }) => {
  if (!always && !show) return null;
  return (
    <div className={`detail-item${full ? ' detail-item-full' : ''}${className ? ` ${className}` : ''}`}>
      {label ? <label>{label}</label> : null}
      {children}
    </div>
  );
};

const DetailSection = ({ title, header, headerExtra, children, force }) => {
  return (
    <div className={`detail-section${force ? ' detail-section-force' : ''}`}>
      {header || (
        headerExtra ? (
          <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>{title}</h2>
            {headerExtra}
          </div>
        ) : (
          <h2>{title}</h2>
        )
      )}
      <div className="detail-grid">{children}</div>
    </div>
  );
};

const CaseFileDetail = ({ caseFileId = null, embedded = false, ozelMode = false } = {}) => {
  const { id: routeIdParam } = useParams();
  // Embedded (sekme) modda prop olarak gelen id kullanılır, aksi halde route param
  const idParam = caseFileId != null ? caseFileId : routeIdParam;
  // ID'den sadece sayısal kısmı al (eğer "93:1" gibi bir değer varsa sadece "93" al)
  const id = idParam ? String(idParam).split(':')[0].split('/')[0] : null;
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [caseFile, setCaseFile] = useState(null);
  const isOzelFile = ozelMode || !!(caseFile?.IsOzel ?? caseFile?.isOzel);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Detay / Belgeler kolon genişliği (sürüklenebilir, localStorage'da saklanır)
  const DETAY_MIN_PCT = 18;
  const DETAY_MAX_PCT = 78;
  const [detayPct, setDetayPct] = useState(() => {
    const saved = parseFloat(localStorage.getItem('caseDetailLeftPct'));
    return Number.isFinite(saved) && saved >= DETAY_MIN_PCT && saved <= DETAY_MAX_PCT ? saved : 40;
  });
  const splitWrapperRef = useRef(null);
  const splitDraggingRef = useRef(false);

  const applySplitFromClientX = (clientX) => {
    const el = splitWrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(DETAY_MIN_PCT, Math.min(DETAY_MAX_PCT, pct));
    setDetayPct(pct);
  };

  const handleSplitMouseDown = (e) => {
    splitDraggingRef.current = true;
    e.preventDefault();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    applySplitFromClientX(e.clientX);
  };

  useEffect(() => {
    localStorage.setItem('caseDetailLeftPct', String(detayPct));
  }, [detayPct]);

  useEffect(() => {
    const onMove = (e) => {
      if (!splitDraggingRef.current) return;
      applySplitFromClientX(e.clientX);
    };
    const onUp = () => {
      if (splitDraggingRef.current) {
        splitDraggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);
  const [documents, setDocuments] = useState([]);

  /**
   * Dar ekranda gorunen sekme. Genis ekranda bir etkisi yok — orada
   * butun bolumler bugunku gibi ayni anda gorunur.
   */
  const [aktifSekme, setAktifSekme] = useState('detay');
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState({});
  const [uploading, setUploading] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [fileDescriptions, setFileDescriptions] = useState({});
  const [selectedDocuments, setSelectedDocuments] = useState([]); // Toplu indirme için
  const [dragActive, setDragActive] = useState({}); // Drag & drop için
  const [masraflar, setMasraflar] = useState([]);
  const [loadingMasraflar, setLoadingMasraflar] = useState(false);
  const [showMasrafForm, setShowMasrafForm] = useState(false);
  const [editingMasraf, setEditingMasraf] = useState(null);
  const [masrafTalepleri, setMasrafTalepleri] = useState([]);
  const [loadingMasrafTalepleri, setLoadingMasrafTalepleri] = useState(false);
  const [masrafTab, setMasrafTab] = useState('masraflar'); // 'masraflar' | 'hesap'
  const [editingMasrafRequest, setEditingMasrafRequest] = useState(null);
  const [muvekkilGorunumuAcik, setMuvekkilGorunumuAcik] = useState(false);
  // Göz düğmesindeki bildirim rozetleri: müvekkil belge yükledi mi,
  // paylaşılan belgeleri inceledi mi?
  const [muvekkilOzet, setMuvekkilOzet] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Word/UDF gibi türlerde çıkarılan metin önizlemesi
  const [previewText, setPreviewText] = useState(null); // {tur, metin, hata}
  const [editingDocTypeId, setEditingDocTypeId] = useState(null);
  const [docTypeDraft, setDocTypeDraft] = useState(DOCUMENT_TYPE.Other);
  const [docTypeMakbuzTarihi, setDocTypeMakbuzTarihi] = useState('');
  const [docTypeMakbuzTutar, setDocTypeMakbuzTutar] = useState('');
  const [savingDocType, setSavingDocType] = useState(false);
  // Belge adını yerinde düzenleme
  const [editingDocNameId, setEditingDocNameId] = useState(null);
  const [docNameDraft, setDocNameDraft] = useState('');
  const [savingDocName, setSavingDocName] = useState(false);
  // Müvekkil portalı görünürlük onayı — kaydedilmekte olan belge id'si
  const [muvekkilOnaySaving, setMuvekkilOnaySaving] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [hasTaskForThisFile, setHasTaskForThisFile] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [inlineEditingField, setInlineEditingField] = useState(null); // 'status' | 'davaTuru' | null
  const [inlineSaving, setInlineSaving] = useState(false);
  const [generatingSigortaBasvuru, setGeneratingSigortaBasvuru] = useState(false);
  const [generatingTahkimBasvuru, setGeneratingTahkimBasvuru] = useState(false);
  const [vekalet, setVekalet] = useState(null);
  const [loadingVekalet, setLoadingVekalet] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTask, setLoadingTask] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [completeModalJob, setCompleteModalJob] = useState(null);
  const [completeComment, setCompleteComment] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingTaskMode, setEditingTaskMode] = useState('complete');
  const [rejectingTask, setRejectingTask] = useState(null);
  const [rejectTaskForm, setRejectTaskForm] = useState({ rejectionReason: '', newDueDate: '' });
  const [documentSortColumn, setDocumentSortColumn] = useState(null);
  const [documentSortDirection, setDocumentSortDirection] = useState('asc');
  const [draggingDocId, setDraggingDocId] = useState(null);
  const [docReorderSaving, setDocReorderSaving] = useState(false);
  const [documentFilters, setDocumentFilters] = useState({
    fileName: '',
    description: '',
    documentType: '',
    size: '',
    uploadDate: '',
    uploadedBy: ''
  });
  // Makbuz ekleme: her dosyanın kendi tarihi ve net tutarı (PDF'ten okunur, düzeltilebilir).
  // Anahtar dosyaAnahtari(dosya): { durum, tarih, tutar, okunanTarih, okunanTutar, ... }
  const [makbuzBilgileri, setMakbuzBilgileri] = useState({});
  const makbuzOkunanlar = useRef(new Set());
  // Belgeler bölümü iki alan: 'belgeler' | 'odemeler' (Masraflar ve Ödemeler) ve onun sekmesi
  const [belgeAlani, setBelgeAlani] = useState('belgeler');
  const [odemeSekmesi, setOdemeSekmesi] = useState('masraf'); // 'masraf' | 'gelen' | 'muvekkile' | 'makbuz'
  // Belgeler'den açılan yükleme formunda "Nereye Eklensin?" seçilmeden yüklenmez.
  const [belgeHedefiSecildi, setBelgeHedefiSecildi] = useState(true);
  const [topluTasiniyor, setTopluTasiniyor] = useState(false);
  const [documentSearchTerm, setDocumentSearchTerm] = useState(''); // Belge arama için

  // Makbuz türünde seçilen PDF'ler okunur; tarih ve net tutar kendiliğinden dolar.
  // Kullanıcının girdiği değerin üzerine yazılmaz.
  useEffect(() => {
    const secili = selectedFiles[DOCUMENT_TYPE.Makbuz];
    const dosyalar = Array.isArray(secili) ? secili : (secili ? [secili] : []);
    dosyalar.forEach((dosya) => {
      const k = dosyaAnahtari(dosya);
      if (makbuzOkunanlar.current.has(k)) return;
      makbuzOkunanlar.current.add(k);
      setMakbuzBilgileri((o) => ({ ...o, [k]: { ...(o[k] || {}), durum: 'okunuyor' } }));
      makbuzOku(dosya).then((s) => {
        setMakbuzBilgileri((o) => {
          const eski = o[k] || {};
          return {
            ...o,
            [k]: {
              ...eski,
              durum: s.durum,
              tarih: eski.tarih || s.tarih || '',
              tutar: eski.tutar || (s.netTutar != null ? String(s.netTutar) : ''),
              kdv: eski.kdv || (s.odenecekKdv != null ? String(s.odenecekKdv) : ''),
              brut: s.brutUcret ?? null,
              okunanTarih: s.tarih,
              okunanTutar: s.netTutar,
              okunanKdv: s.odenecekKdv,
              tarihEtiketi: s.tarihEtiketi,
              tutarEtiketi: s.tutarEtiketi,
              uyarilar: s.uyarilar || [],
            },
          };
        });
      });
    });
  }, [selectedFiles]);

  // checkEditPermission fonksiyonunu useEffect'ten önce tanımla
  const checkEditPermission = useCallback(async () => {
    if (!user || !caseFile) {
      setCanEdit(false);
      return;
    }

    // Dosya düzenleme: Tüm giriş yapmış kullanıcılar kullanabilsin
    setCanEdit(true);
    setHasTaskForThisFile(false);
  }, [user, caseFile]);

  useEffect(() => {
    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      navigate('/login');
      return;
    }

    if (id && !isNaN(parseInt(id))) {
      loadCaseFile();
      setTimeout(() => {
        loadDocuments();
        loadVekalet();
        loadNotes();
        loadJobs();
        loadTasks();
      }, 500);
    }
  }, [token, id, ozelMode]);

  /* Masraflar ve masraf talepleri kullanıcı bilgisi HAZIR olunca yüklenir.
     Önceden yukarıdaki gecikmeli yüklemenin içindeydiler: dosya doğrudan
     açılınca (sayfa yenileme, bildirim, yeni sekme) ya da sunucu yavaşken
     kullanıcı bilgisi o anda henüz gelmemiş oluyordu; "kullanıcı yoksa
     bekle" koşulu yüklemeyi atlıyor, bir daha da denenmiyordu. Dosyanın
     Masraflar bölümü boş görünüyordu (2026-10-07: "masraflar gözükmüyor"). */
  const kullaniciHazir = !!user;
  useEffect(() => {
    if (!kullaniciHazir || ozelMode || !id || isNaN(parseInt(id))) return;
    loadMasraflar();
    loadMasrafTalepleri();
  }, [kullaniciHazir, token, id, ozelMode]);

  useEffect(() => {
    if (user && (user.Role === 'Admin' || user.role === 'Admin')) {
      loadUsers();
    }
  }, [user]);
  
  // id değiştiğinde belgeleri yeniden yükle
  useEffect(() => {
    if (id) {
      const timer = setTimeout(() => {
        loadDocuments();
      }, 2000); // 2 saniye bekle (belgelerin yüklenmesi için)
      return () => clearTimeout(timer);
    }
  }, [id]);

  // Jobs listesini otomatik yenile (sayfa focus olduğunda ve belirli aralıklarla)
  useEffect(() => {
    if (!id) return;

    // Sayfa focus olduğunda jobs'ı yeniden yükle
    const handleFocus = () => {
      loadJobs();
    };

    // Her 30 saniyede bir jobs'ı yeniden yükle
    const interval = setInterval(() => {
      loadJobs();
    }, 30000);

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [id]);

  // Custom event dinleyicisi - Dashboard'dan iş tamamlandığında jobs'ı yeniden yükle
  useEffect(() => {
    if (!id) return;

    const handleJobUpdated = (event) => {
      const updatedJobCaseFileId = event.detail?.caseFileId;
      if (updatedJobCaseFileId && parseInt(updatedJobCaseFileId) === parseInt(id)) {
        loadJobs();
      }
    };

    window.addEventListener('jobUpdated', handleJobUpdated);

    return () => {
      window.removeEventListener('jobUpdated', handleJobUpdated);
    };
  }, [id]);

  // Görev güncellendiğinde (Kontrole Gönder, Onayla, Reddet) tasks'ı yeniden yükle
  useEffect(() => {
    if (!id) return;

    const handleTaskUpdated = () => loadTasks();

    window.addEventListener('taskUpdated', handleTaskUpdated);

    return () => {
      window.removeEventListener('taskUpdated', handleTaskUpdated);
    };
  }, [id]);

  // Klavye kısayolları için event listener (önizleme modal'ı açıkken)
  useEffect(() => {
    if (!previewDocument) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePreviousDocument();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextDocument();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closePreview();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [previewDocument, documents]);

  useEffect(() => {
    if (caseFile && user) {
      checkEditPermission();
    } else {
      setCanEdit(false);
    }
  }, [caseFile, user, checkEditPermission]);

  const loadCaseFile = async () => {
    // Token kontrolü - localStorage'dan da kontrol et
    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      navigate('/login');
      return;
    }

    const currentId = parseInt(id);
    const existingId = caseFile?.Id ?? caseFile?.id;
    if (!existingId || parseInt(existingId) !== currentId) {
      setLoading(true);
    }
    setError(null);
    try {
      // getById kullan - arşivlenmiş dosyalar da dahil
      const file = await caseFileAPI.getById(parseInt(id));
      // Boş dizi / geçersiz yanıtı dosya sanma (demo middleware vb.)
      if (file && !Array.isArray(file) && (file.Id != null || file.id != null)) {
        setCaseFile(file);
      } else {
        setError('Dosya bulunamadı');
      }
    } catch (err) {
      console.error('Error loading case file:', err);
      
      // 401 hatası durumunda interceptor zaten login'e yönlendirecek
      if (err.response?.status === 401) {
        return;
      }
      
      // Diğer hatalar için mesaj göster
      setError('Dosya yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    if (!id) return;
    
    // Token kontrolü - localStorage'dan da kontrol et
    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      return; // Token yoksa belgeleri yükleme
    }
    
    setLoadingDocuments(true);
    try {
      const docs = await documentAPI.getByCaseFile(parseInt(id));
      setDocuments(docs || []);
    } catch (err) {
      console.error('Belgeler yüklenirken hata:', err);
      // 401 hatası durumunda sessizce geç
      // Interceptor'ın login'e yönlendirmesini engellemek için
      // error'u tekrar throw etmiyoruz
      if (err.response?.status === 401) {
        // Token geçersiz, ama belgeler opsiyonel olduğu için
        // sayfayı bozmadan devam et
        setDocuments([]);
        return;
      }
      // Diğer hatalar için de sessizce devam et
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Arka plan yükleme bitince belge listesini yenile. Dosya da yenilenir:
  // eklenen belge arabuluculuk evrakıysa (UDF son tutanak vb.) sunucu
  // Arabuluculuk bölümünün boş alanlarını doldurmuş olabilir.
  const [sonBelgeYukleme, setSonBelgeYukleme] = useState(0);
  useEffect(() => {
    const onUploaded = (evt) => {
      const uploadedCaseId = evt?.detail?.caseFileId;
      if (!id) return;
      if (uploadedCaseId && parseInt(uploadedCaseId, 10) !== parseInt(id, 10)) return;
      loadDocuments();
      setSonBelgeYukleme(Date.now());
      // Sessiz yenileme: loadCaseFile burada ilk çizimin kopyası (dosya henüz
      // yokken) olduğundan "yükleniyor" ekranı açıp kaydırma yerini bozardı.
      caseFileAPI.getById(parseInt(id, 10))
        .then((f) => { if (f && !Array.isArray(f) && (f.Id != null || f.id != null)) setCaseFile(f); })
        .catch(() => {});
    };
    window.addEventListener('documents-uploaded', onUploaded);
    return () => window.removeEventListener('documents-uploaded', onUploaded);
  }, [id, token]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const usersData = await userAPI.getAll();
      setUsers(usersData);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadNotes = async () => {
    if (!id) return;
    
    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      return;
    }
    
    try {
      setLoadingNotes(true);
      const notesData = await noteAPI.getByCaseFileId(parseInt(id));
      setNotes(notesData || []);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotes([]);
        return;
      }
      console.error('Error loading notes:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadJobs = async () => {
    if (!id) return;
    
    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      return;
    }
    
    try {
      setLoadingJobs(true);
      const allJobs = await jobAPI.getAll();
      // Sadece bu dosyaya ait işleri filtrele
      const fileJobs = allJobs.filter(job => {
        const jobCaseFileId = job.CaseFileId ?? job.caseFileId;
        return jobCaseFileId === parseInt(id);
      });
      setJobs(fileJobs || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleCompleteWithComment = async () => {
    if (!completeModalJob) return;
    const jobId = completeModalJob.Id ?? completeModalJob.id;
    try {
      await jobAPI.update({
        JobId: jobId,
        IsCompleted: true,
        CompletedComment: completeComment.trim() || null
      });
      await loadJobs();
      setCompleteModalJob(null);
      setCompleteComment('');
      window.dispatchEvent(new CustomEvent('jobUpdated', { detail: { caseFileId: id } }));
      alert('İş tamamlandı. Onaylamak için Onayla demeniz gerekir.');
    } catch (error) {
      alert('İş güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const loadTasks = async () => {
    if (!id) return;
    
    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      return;
    }
    
    try {
      setLoadingTasks(true);
      const tasksData = await taskAPI.getByCaseFileId(parseInt(id));
      setTasks(tasksData || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const isAdminForTask = () => user && (user.Role === 'Admin' || user.role === 'Admin');

  const handleTaskUpdate = async (taskData) => {
    setLoadingTask(true);
    try {
      await taskAPI.update(taskData);
      await loadTasks();
      setEditingTask(null);
      setEditingTaskMode('complete');
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: taskData.TaskId } }));
      if (taskData.Status === TASK_STATUS.Completed) {
        alert('Görev tamamlandı olarak işaretlendi.');
      } else if (taskData.Response) {
        alert('Görev kontrole gönderildi');
      } else {
        alert('Görev güncellendi.');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingTask(false);
    }
  };

  const handleApproveTask = async (task) => {
    if (!await onayla('Bu görevi onaylayıp tamamlandı olarak işaretlemek istediğinizden emin misiniz?')) return;
    setLoadingTask(true);
    try {
      await taskAPI.update({ TaskId: task.Id ?? task.id, Status: TASK_STATUS.Completed });
      await loadTasks();
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: task.Id ?? task.id } }));
      alert('Görev başarıyla onaylandı');
    } catch (error) {
      console.error('Error approving task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingTask(false);
    }
  };

  const handleDeleteTask = async (task) => {
    if (!await onayla('Bu görevi silmek istediğinize emin misiniz?')) return;
    setLoadingTask(true);
    try {
      await taskAPI.delete(task.Id ?? task.id);
      await loadTasks();
      setEditingTask(null);
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: task.Id ?? task.id } }));
      alert('Görev silindi.');
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingTask(false);
    }
  };

  const handleRejectTask = async () => {
    if (!rejectTaskForm.rejectionReason.trim()) {
      alert('Lütfen reddedilme sebebini giriniz.');
      return;
    }
    if (!rejectingTask) return;
    setLoadingTask(true);
    try {
      const newDueDateValue = rejectTaskForm.newDueDate && /^\d{4}-\d{2}-\d{2}$/.test(rejectTaskForm.newDueDate)
        ? `${rejectTaskForm.newDueDate}T23:59`
        : rejectTaskForm.newDueDate;
      await taskAPI.reject(
        rejectingTask.Id ?? rejectingTask.id,
        rejectTaskForm.rejectionReason,
        toISODateOrNull(newDueDateValue)
      );
      await loadTasks();
      setRejectingTask(null);
      setRejectTaskForm({ rejectionReason: '', newDueDate: '' });
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: rejectingTask.Id ?? rejectingTask.id } }));
      alert('Görev reddedildi ve yeni görev atandı');
    } catch (error) {
      console.error('Error rejecting task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingTask(false);
    }
  };

  const loadVekalet = async () => {
    if (!id) return;
    
    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      return;
    }
    
    try {
      setLoadingVekalet(true);
      const vekaletData = await vekaletAPI.getByCaseFileId(parseInt(id));
      // vekaletAPI.getByCaseFileId 404 durumunda null döndürür, hata fırlatmaz
      setVekalet(vekaletData);
    } catch (error) {
      // 404 hatası normal (vekalet kaydı yok), sessizce geç
      if (error.response?.status === 404 || error.silent) {
        setVekalet(null);
        return;
      }
      // Eğer hala bir hata varsa (404 dışında), logla
      if (error.response?.status !== 404) {
        console.error('Vekalet yüklenirken hata:', error);
      }
      setVekalet(null);
    } finally {
      setLoadingVekalet(false);
    }
  };

  const loadMasraflar = async () => {
    if (!id) return;
    
    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      return;
    }
    
    // user henüz yüklenmemişse bekle
    if (!user) {
      return;
    }
    
    setLoadingMasraflar(true);
    try {
      const masrafData = await masrafAPI.getByCaseFileId(parseInt(id));
      // Yeni eklenen masraflar en altta görünsün (ID'ye göre ascending sırala)
      const sortedData = (masrafData || []).sort((a, b) => {
        const idA = a.Id ?? a.id ?? 0;
        const idB = b.Id ?? b.id ?? 0;
        return idA - idB; // Ascending order (küçükten büyüğe)
      });
      setMasraflar(sortedData);
    } catch (err) {
      if (err.response?.status === 401) {
        setMasraflar([]);
        return;
      }
      setMasraflar([]);
    } finally {
      setLoadingMasraflar(false);
    }
  };

  const loadMasrafTalepleri = async () => {
    if (!id) return;
    
    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      return;
    }
    
    // user henüz yüklenmemişse bekle
    if (!user) {
      return;
    }
    
    setLoadingMasrafTalepleri(true);
    try {
      // Kullanıcılar için sadece kendi taleplerini al, adminler için tüm talepleri al
      const isAdmin = user && (user.Role === 'Admin' || user.role === 'Admin');
      let allRequests = [];
      
      if (isAdmin) {
        allRequests = await masrafRequestAPI.getAll();
      } else {
        // Kullanıcılar için sadece kendi taleplerini al
        allRequests = await masrafRequestAPI.getMyRequests();
      }
      
      // Bu dosyaya ait olanları filtrele
      const fileRequests = (allRequests || []).filter(req => {
        const reqCaseFileId = req.caseFileId || req.CaseFileId;
        return reqCaseFileId === parseInt(id);
      });
      // Tarihe göre sırala (en yeni en üstte)
      fileRequests.sort((a, b) => {
        const dateA = new Date(a.createdDate || a.CreatedDate || 0);
        const dateB = new Date(b.createdDate || b.CreatedDate || 0);
        return dateB - dateA;
      });
      setMasrafTalepleri(fileRequests);
    } catch (err) {
      console.error('Masraf talepleri yüklenirken hata:', err);
      setMasrafTalepleri([]);
    } finally {
      setLoadingMasrafTalepleri(false);
    }
  };

  const processSelectedFiles = (documentType, files) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    // Her zaman dizi olarak tut (çoklu sırada sürüklemek için)
    setSelectedFiles(prev => ({ ...prev, [documentType]: fileArray }));
    const descriptions = {};
    fileArray.forEach((_, index) => {
      descriptions[index] = '';
    });
    setFileDescriptions(prev => ({ ...prev, [documentType]: descriptions }));
  };

  const movePendingUploadFile = (documentType, fromIndex, toIndex) => {
    setSelectedFiles((prev) => {
      const list = Array.isArray(prev[documentType])
        ? [...prev[documentType]]
        : (prev[documentType] ? [prev[documentType]] : []);
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) return prev;
      const [item] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, item);
      return { ...prev, [documentType]: list };
    });
    setFileDescriptions((prev) => {
      const current = prev[documentType];
      const map = (typeof current === 'object' && current !== null && !Array.isArray(current))
        ? { ...current }
        : { 0: typeof current === 'string' ? current : '' };
      const keys = Object.keys(map).map(Number).sort((a, b) => a - b);
      const values = keys.map((k) => map[k] ?? '');
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= values.length || toIndex >= values.length) return prev;
      const [item] = values.splice(fromIndex, 1);
      values.splice(toIndex, 0, item);
      const next = {};
      values.forEach((v, i) => { next[i] = v; });
      return { ...prev, [documentType]: next };
    });
  };

  const removePendingUploadFile = (documentType, index) => {
    setSelectedFiles((prev) => {
      const list = Array.isArray(prev[documentType])
        ? [...prev[documentType]]
        : (prev[documentType] ? [prev[documentType]] : []);
      list.splice(index, 1);
      return { ...prev, [documentType]: list.length ? list : null };
    });
    setFileDescriptions((prev) => {
      const current = prev[documentType];
      const map = (typeof current === 'object' && current !== null && !Array.isArray(current))
        ? { ...current }
        : {};
      const values = Object.keys(map).map(Number).sort((a, b) => a - b).map((k) => map[k] ?? '');
      values.splice(index, 1);
      const next = {};
      values.forEach((v, i) => { next[i] = v; });
      return { ...prev, [documentType]: next };
    });
  };

  const handleFileSelect = (documentType, e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processSelectedFiles(documentType, files);
    }
    if (e.target) e.target.value = '';
  };

  /* Uygulamada evrak fotografi: tarayicinin dosya secicisi yerine yerli
     kamera aciliyor. Secici acilirken iOS WebView'i bosaltip uygulamayi
     bastan yukleyebiliyordu; yerli kamera uygulamanin icinde kaliyor. */
  const fotografCek = async (documentType) => {
    const foto = await evrakFotografiCek();
    if (foto) processSelectedFiles(documentType, [foto]);
  };

  const handleDrag = (documentType, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(prev => ({ ...prev, [documentType]: true }));
    } else if (e.type === 'dragleave') {
      setDragActive(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const handleDrop = (documentType, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [documentType]: false }));
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processSelectedFiles(documentType, files);
    }
  };

  // Formda tür ya da "Nereye Eklensin?" değişince seçilen dosyalar ve açıklamalar yeni türe geçer.
  const belgeTuruDegistir = (eski, yeni) => {
    if (eski === yeni) return;
    setShowUploadForm({ [yeni]: true });
    setSelectedFiles((prev) => {
      const y = { ...prev };
      if (prev[eski]) { y[yeni] = prev[eski]; delete y[eski]; }
      return y;
    });
    setFileDescriptions((prev) => {
      const y = { ...prev };
      if (prev[eski]) { y[yeni] = prev[eski]; delete y[eski]; }
      return y;
    });
  };

  const handleUpload = async (documentType, e) => {
    e.preventDefault();
    const files = selectedFiles[documentType];
    if (!files || !id) {
      alert('Lütfen en az bir dosya seçin!');
      return;
    }

    const fileArray = Array.isArray(files) ? files : [files];
    if (fileArray.length === 0) {
      alert('Lütfen en az bir dosya seçin!');
      return;
    }
    if (!isOzelFile && !belgeHedefiSecildi) {
      alert('Belgenin nereye ekleneceğini seçin (Belgeler, Masraflar, Gelen Ödemeler…).');
      return;
    }

    const localToken = localStorage.getItem('token');
    if (!token && !localToken) {
      alert('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
      navigate('/login');
      return;
    }

    // Makbuzda her dosyanın kendi tarihi ve net tutarı olur.
    let dosyaBasina = null;
    if (documentType === DOCUMENT_TYPE.Makbuz) {
      const eksik = fileArray.findIndex((f) => {
        const b = makbuzBilgileri[dosyaAnahtari(f)] || {};
        return !b.tarih || !(parseFloat(b.tutar) > 0);
      });
      if (eksik >= 0) {
        alert(fileArray.length > 1
          ? `${eksik + 1}. makbuzun tarihi ve net tutarı girilmeli.`
          : 'Makbuz tarihi ve net tutarı girilmeli.');
        return;
      }
      dosyaBasina = {};
      fileArray.forEach((f, i) => {
        const b = makbuzBilgileri[dosyaAnahtari(f)];
        const kdvSayi = parseFloat(b.kdv);
        dosyaBasina[i] = {
          faturaTarihi: toISODateOrNull(b.tarih),
          tutar: Math.round(parseFloat(b.tutar) * 100) / 100,
          // Boş bırakılırsa Kasa'daki KDV hesabı nete göre tahmin eder
          odenecekKdv: b.kdv !== '' && b.kdv != null && Number.isFinite(kdvSayi) && kdvSayi >= 0
            ? Math.round(kdvSayi * 100) / 100 : null,
          brutUcret: Number(b.brut) > 0 ? b.brut : null,
        };
      });
    }

    const descriptions = fileDescriptions[documentType] || {};

    // Formu hemen kapat; yükleme arka planda devam eder
    setSelectedFiles(prev => ({ ...prev, [documentType]: null }));
    setFileDescriptions(prev => ({ ...prev, [documentType]: {} }));
    if (documentType === DOCUMENT_TYPE.Makbuz) {
      const anahtarlar = fileArray.map(dosyaAnahtari);
      anahtarlar.forEach((k) => makbuzOkunanlar.current.delete(k));
      setMakbuzBilgileri((o) => {
        const y = { ...o };
        anahtarlar.forEach((k) => { delete y[k]; });
        return y;
      });
    }
    setShowUploadForm(prev => ({ ...prev, [documentType]: false }));
    setUploading(prev => ({ ...prev, [documentType]: false }));
    // Yüklenen belge hangi alanda görünecekse oraya geç (dekont → Gelen Ödemeler vb.).
    if (!isOzelFile) {
      const sekme = turunSekmesi(documentType);
      if (sekme) {
        setBelgeAlani('odemeler');
        setOdemeSekmesi(sekme);
      } else {
        setBelgeAlani('belgeler');
      }
    }

    enqueueDocumentUploads({
      caseFileId: parseInt(id, 10),
      files: fileArray,
      descriptions,
      documentType,
      dosyaBasina,
      onSuccess: () => {
        window.dispatchEvent(new CustomEvent('documents-uploaded', {
          detail: { caseFileId: parseInt(id, 10) }
        }));
      },
      onBatchComplete: ({ successCount, failCount }) => {
        if (successCount > 0) {
          window.dispatchEvent(new CustomEvent('documents-uploaded', {
            detail: { caseFileId: parseInt(id, 10) }
          }));
        }
        if (failCount > 0 && successCount === 0) {
          // Hatalar panelde görünür; ekstra alert yok
        }
      }
    });
  };

  // Toplu indirme için checkbox handler
  const handleDocumentSelect = (documentId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  const handleSelectAll = (typeDocuments) => {
    const typeDocumentIds = typeDocuments.map(doc => doc.Id ?? doc.id);
    const allSelected = typeDocumentIds.every(id => selectedDocuments.includes(id));
    
    if (allSelected) {
      // Tümünü kaldır
      setSelectedDocuments(prev => prev.filter(id => !typeDocumentIds.includes(id)));
    } else {
      // Tümünü ekle
      setSelectedDocuments(prev => {
        const newSelection = [...prev];
        typeDocumentIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleBulkDownload = async () => {
    if (selectedDocuments.length === 0) {
      alert('Lütfen indirmek istediğiniz dosyaları seçin!');
      return;
    }

    try {
      // Her dosyayı ayrı ayrı indir
      for (const docId of selectedDocuments) {
        const doc = documents.find(d => (d.Id ?? d.id) === docId);
        if (doc) {
          try {
            const result = await documentAPI.download(docId);
            const blob = result?.blob ?? result;
            await downloadBlob(
              blob,
              result?.fileName || doc.FileName || doc.fileName || 'belge',
              result?.contentType || doc.ContentType || doc.contentType || null
            );
            // Kısa bir gecikme ekle (tarayıcı çoklu indirmeyi engelleyebilir)
            await new Promise(resolve => setTimeout(resolve, 200));
          } catch (err) {
            console.error(`Error downloading ${doc.FileName}:`, err);
          }
        }
      }
      alert(`${selectedDocuments.length} dosya indiriliyor...`);
      setSelectedDocuments([]);
    } catch (err) {
      console.error('Error in bulk download:', err);
      alert('Dosyalar indirilirken hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.length === 0) {
      alert('Lütfen silmek istediğiniz dosyaları seçin!');
      return;
    }

    if (!await onayla(`${selectedDocuments.length} dosyayı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      return;
    }

    try {
      let deletedCount = 0;
      let failedCount = 0;
      
      // Her dosyayı ayrı ayrı sil
      for (const docId of selectedDocuments) {
        const doc = documents.find(d => (d.Id ?? d.id) === docId);
        if (doc) {
          try {
            await documentAPI.delete(docId);
            deletedCount++;
          } catch (err) {
            console.error(`Error deleting ${doc.FileName ?? doc.fileName}:`, err);
            failedCount++;
          }
        }
      }
      
      if (deletedCount > 0) {
        alert(`${deletedCount} dosya başarıyla silindi${failedCount > 0 ? `, ${failedCount} dosya silinemedi` : ''}`);
        loadDocuments();
        setSelectedDocuments([]);
      } else {
        alert('Hiçbir dosya silinemedi. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      console.error('Error deleting files:', err);
      alert('Dosyalar silinirken hata oluştu: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSendArchiveRequest = async () => {
    if (!await onayla('Bu dosya için arşiv isteği göndermek istediğinizden emin misiniz?')) {
      return;
    }
    
    setLoading(true);
    try {
      await archiveRequestAPI.sendRequest(parseInt(id));
      alert('Arşiv isteği başarıyla gönderildi. Admin onayı bekleniyor.');
      await loadCaseFile(); // Reload to update status
      setShowEditForm(false); // Close edit form
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!await onayla('Bu dosyayı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }
    
    setLoading(true);
    try {
      const fileId = caseFile?.Id || caseFile?.id || parseInt(id);
      await caseFileAPI.delete(fileId);
      alert('Dosya başarıyla silindi');
      navigate('/case-files');
    } catch (error) {
      console.error('Error deleting case file:', error);
      alert('Dosya silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInlineStatusDavaTuruUpdate = async (field, value) => {
    if (!canEdit || !caseFile || inlineSaving) return;
    const fileId = caseFile.Id ?? caseFile.id ?? parseInt(id, 10);
    const currentStatus = caseFile.Status ?? caseFile.status;
    const currentDavaTuru = caseFile.DavaTuru ?? caseFile.davaTuru;
    const currentIcraKonusu = caseFile.IcraKonusu ?? caseFile.icraKonusu ?? '';

    if (field === 'icraKonusu') {
      const nextKonu = value || '';
      if (nextKonu === currentIcraKonusu) {
        setInlineEditingField(null);
        return;
      }
      setInlineSaving(true);
      try {
        const updated = await caseFileAPI.updateStatusDavaTuru(fileId, { IcraKonusu: nextKonu });
        setCaseFile(updated);
        setInlineEditingField(null);
      } catch (error) {
        console.error('Inline güncelleme hatası:', error);
        alert('Güncelleme başarısız: ' + (error.response?.data?.message || error.message));
        setInlineEditingField(null);
      } finally {
        setInlineSaving(false);
      }
      return;
    }

    if (field === 'sozlesmeOraniKdvDahil') {
      const nextKdv = !!value;
      const currentKdv = !!(caseFile.SozlesmeOraniKdvDahil ?? caseFile.sozlesmeOraniKdvDahil);
      if (nextKdv === currentKdv) return;
      setInlineSaving(true);
      try {
        const updated = await caseFileAPI.updateStatusDavaTuru(fileId, { SozlesmeOraniKdvDahil: nextKdv });
        setCaseFile(updated);
      } catch (error) {
        console.error('Inline güncelleme hatası:', error);
        alert('Güncelleme başarısız: ' + (error.response?.data?.message || error.message));
      } finally {
        setInlineSaving(false);
      }
      return;
    }

    const nextValue = parseInt(value, 10);
    if (Number.isNaN(nextValue)) {
      setInlineEditingField(null);
      return;
    }
    if (field === 'status' && nextValue === parseInt(currentStatus, 10)) {
      setInlineEditingField(null);
      return;
    }
    if (field === 'davaTuru' && nextValue === parseInt(currentDavaTuru, 10)) {
      setInlineEditingField(null);
      return;
    }

    setInlineSaving(true);
    try {
      const payload = field === 'status'
        ? { Status: nextValue }
        : { DavaTuru: nextValue };
      const updated = await caseFileAPI.updateStatusDavaTuru(fileId, payload);
      setCaseFile(updated);
      setInlineEditingField(null);
      if (field === 'status' && (nextValue === 25 || nextValue === 33) && !ozelMode) {
        await loadJobs();
        if (nextValue === 25) await loadMasrafTalepleri();
      }
    } catch (error) {
      console.error('Inline güncelleme hatası:', error);
      alert('Güncelleme başarısız: ' + (error.response?.data?.message || error.message));
      setInlineEditingField(null);
    } finally {
      setInlineSaving(false);
    }
  };

  const handleKesinlesmeTarihiSave = async (value) => {
    if (!canEdit || !caseFile || inlineSaving) return;
    const current = formatDateForDateInput(caseFile.KesinlesmeTarihi ?? caseFile.kesinlesmeTarihi) || '';
    if ((value || '') === current) return;
    const fileId = caseFile.Id ?? caseFile.id ?? parseInt(id, 10);
    setInlineSaving(true);
    try {
      const updated = await caseFileAPI.updateKesinlesmeTarihi(fileId, toISODateOrNull(value));
      if (updated) setCaseFile(updated);
      await loadJobs();
    } catch (error) {
      alert('Kesinleşme tarihi kaydedilemedi: ' + (error.response?.data?.message || error.message));
    } finally {
      setInlineSaving(false);
    }
  };

  const handleUpdate = async (caseFileData) => {
    if (!user || !caseFile) {
      alert('Bu dosyada değişiklik yapma yetkiniz yok.');
      return null;
    }

    const toNullIfEmpty = (v) => (v != null && String(v).trim() !== '' ? v : null);
    const toNumberOrNull = (v) => {
      if (v === '' || v == null) return null;
      const n = typeof v === 'number' ? v : parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };
    const toIntOrNull = (v) => {
      if (v === '' || v == null) return null;
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : null;
    };

    try {
      // Spread yok: formdan gelen boş string / NaN alanlar JSON bind hatası üretmesin
      const cleanedData = {
        CaseFileId: caseFileData.CaseFileId || caseFileData.Id || caseFileData.id || parseInt(id, 10),
        FoyNo: parseInt(caseFileData.FoyNo, 10) || 0,
        Status: parseInt(caseFileData.Status, 10) || 1,
        DavaTuru: parseInt(caseFileData.DavaTuru, 10) || 0,
        ReferansId: caseFileData.ReferansId ? (typeof caseFileData.ReferansId === 'number' ? caseFileData.ReferansId : parseInt(caseFileData.ReferansId, 10)) : null,
        SorumluId: (caseFileData.SorumluId && Number(caseFileData.SorumluId) > 0)
          ? parseInt(caseFileData.SorumluId, 10)
          : (caseFile?.SorumluId || caseFile?.sorumluId || 0),
        Muvekkil: caseFileData.Muvekkil || '',
        MuvekkilTC: toNullIfEmpty(caseFileData.MuvekkilTC),
        IsSirket: !!caseFileData.IsSirket,
        MuvekkilTelefon: toNullIfEmpty(caseFileData.MuvekkilTelefon),
        MuvekkilIBAN: toNullIfEmpty(caseFileData.MuvekkilIBAN),
        MuvekkilPlaka: toNullIfEmpty(caseFileData.MuvekkilPlaka),
        MuvekkilAracRolu: toNullIfEmpty(caseFileData.MuvekkilAracRolu),
        MuvekkilAracSigortaSirketi: toNullIfEmpty(caseFileData.MuvekkilAracSigortaSirketi),
        KarsiTarafSigortaSirketi: toNullIfEmpty(caseFileData.KarsiTarafSigortaSirketi),
        KarsiTarafPlaka: toNullIfEmpty(caseFileData.KarsiTarafPlaka),
        KarsiTaraf1: toNullIfEmpty(caseFileData.KarsiTaraf1),
        KarsiTaraf2: toNullIfEmpty(caseFileData.KarsiTaraf2),
        KarsiTarafSigortaSirketiVekili: toNullIfEmpty(caseFileData.KarsiTarafSigortaSirketiVekili),
        KazaSehri: toNullIfEmpty(caseFileData.KazaSehri),
        SulhDurumu: toNullIfEmpty(caseFileData.SulhDurumu),
        SozlesmeOrani: toNumberOrNull(caseFileData.SozlesmeOrani),
        SozlesmeOraniKdvDahil: !!(caseFileData.SozlesmeOraniKdvDahil ?? caseFileData.sozlesmeOraniKdvDahil),
        MahkemeEsas: toNullIfEmpty(caseFileData.MahkemeEsas),
        IcraDosyaNo: toNullIfEmpty(caseFileData.IcraDosyaNo),
        MahkemaBilgisi: toNullIfEmpty(caseFileData.MahkemaBilgisi),
        SavcilikCezaDosyaNo: toNullIfEmpty(caseFileData.SavcilikCezaDosyaNo),
        TahkimBasvuruNo: toNullIfEmpty(caseFileData.TahkimBasvuruNo),
        ItirazBasvuruNo: toNullIfEmpty(caseFileData.ItirazBasvuruNo),
        IbraAsamasi: toNullIfEmpty(caseFileData.IbraAsamasi),
        GelenOdemeDurumu: toNullIfEmpty(caseFileData.GelenOdemeDurumu),
        GidenOdemeDurumu: toNullIfEmpty(caseFileData.GidenOdemeDurumu),
        SMM: toNullIfEmpty(caseFileData.SMM),
        KazaTarihi: toISODateOrNull(caseFileData.KazaTarihi),
        DosyaGelisTarihi: toISODateOrNull(caseFileData.DosyaGelisTarihi),
        TahkimBasvuruTarihi: toISODateOrNull(caseFileData.TahkimBasvuruTarihi),
        RaporTarihi: toISODateOrNull(caseFileData.RaporTarihi),
        ZamanAsimiTarihi: toISODateOrNull(caseFileData.ZamanAsimiTarihi),
        DurusmaTarihi: toISODateOrNull(caseFileData.DurusmaTarihi),
        SigortaBasvuruTarihi: toISODateOrNull(caseFileData.SigortaBasvuruTarihi),
        DavaAcilisTarihi: toISODateOrNull(caseFileData.DavaAcilisTarihi),
        KesinlesmeTarihi: toISODateOrNull(caseFileData.KesinlesmeTarihi),
        IcraHesapAyar: toNullIfEmpty(caseFileData.IcraHesapAyar),
        FizikiDosyaMevcut: !!(caseFileData.FizikiDosyaMevcut ?? caseFileData.fizikiDosyaMevcut),
        TakipAcilisMiktari: toNumberOrNull(caseFileData.TakipAcilisMiktari),
        TakipTarihi: toISODateOrNull(caseFileData.TakipTarihi),
        IcraFaizTuru: toNullIfEmpty(caseFileData.IcraFaizTuru),
        IcraFaizOrani: toNumberOrNull(caseFileData.IcraFaizOrani),
        IcraFaizBaslangicTarihi: toISODateOrNull(caseFileData.IcraFaizBaslangicTarihi),
        IcraTahsilAsamasi: toNullIfEmpty(caseFileData.IcraTahsilAsamasi),
        IcraVekaletUcretiManuel: toNumberOrNull(caseFileData.IcraVekaletUcretiManuel),
        IcraKonusu: toNullIfEmpty(caseFileData.IcraKonusu),
        MuvekkilDavaci: !!caseFileData.MuvekkilDavaci,
        MuvekkilDavali: !!caseFileData.MuvekkilDavali,
        EsAdSoyad: toNullIfEmpty(caseFileData.EsAdSoyad),
        EsTC: toNullIfEmpty(caseFileData.EsTC),
        EsCalisiyor: !!caseFileData.EsCalisiyor,
        EvlilikTarihi: toISODateOrNull(caseFileData.EvlilikTarihi),
        AyrilikTarihi: toISODateOrNull(caseFileData.AyrilikTarihi),
        BosanmaSebebi: toNullIfEmpty(caseFileData.BosanmaSebebi),
        CocukSayisi: toIntOrNull(caseFileData.CocukSayisi),
        CocuklarJson: toNullIfEmpty(caseFileData.CocuklarJson),
        VelayetTalebi: !!caseFileData.VelayetTalebi,
        TalepNafaka: !!caseFileData.TalepNafaka,
        TalepTedbirNafaka: !!caseFileData.TalepTedbirNafaka,
        TalepIstirakNafaka: !!caseFileData.TalepIstirakNafaka,
        TalepYoksullukNafaka: !!caseFileData.TalepYoksullukNafaka,
        TalepMaddiTazminat: !!caseFileData.TalepMaddiTazminat,
        TalepManeviTazminat: !!caseFileData.TalepManeviTazminat,
        OdemeBilgileriGoster: !!caseFileData.OdemeBilgileriGoster,
        DetayAlanGoster: typeof caseFileData.DetayAlanGoster === 'string'
          ? (caseFileData.DetayAlanGoster || null)
          : (caseFileData.DetayAlanGoster && Object.keys(caseFileData.DetayAlanGoster).length
            ? JSON.stringify(caseFileData.DetayAlanGoster)
            : null),
        MuvekkilSanik: !!caseFileData.MuvekkilSanik,
        MuvekkilMusteki: !!caseFileData.MuvekkilMusteki,
        MuvekkilSupheli: !!caseFileData.MuvekkilSupheli,
        SucTuru: toNullIfEmpty(caseFileData.SucTuru),
        SucTarihi: toISODateOrNull(caseFileData.SucTarihi),
        SucYeri: toNullIfEmpty(caseFileData.SucYeri),
        TckMaddesi: toNullIfEmpty(caseFileData.TckMaddesi),
        TedbirTutuklu: !!caseFileData.TedbirTutuklu,
        TedbirAdliKontrol: !!caseFileData.TedbirAdliKontrol,
        TedbirYakalamaKarari: !!caseFileData.TedbirYakalamaKarari,
        KovusturmayaYerOlmadigiKarari: !!caseFileData.KovusturmayaYerOlmadigiKarari,
        IddianameTarihi: toISODateOrNull(caseFileData.IddianameTarihi),
        TarafSayisi: toIntOrNull(caseFileData.TarafSayisi),
        TaraflarJson: toNullIfEmpty(caseFileData.TaraflarJson),
        IsverenFirma: toNullIfEmpty(caseFileData.IsverenFirma),
        IsverenTcvkn: toNullIfEmpty(caseFileData.IsverenTcvkn),
        IsverenYetkilisi: toNullIfEmpty(caseFileData.IsverenYetkilisi),
        IsverenAdresi: toNullIfEmpty(caseFileData.IsverenAdresi),
        SgkSicilNo: toNullIfEmpty(caseFileData.SgkSicilNo),
        IseGirisTarihi: toISODateOrNull(caseFileData.IseGirisTarihi),
        IstenCikisTarihi: toISODateOrNull(caseFileData.IstenCikisTarihi),
        CikisNedeni: toNullIfEmpty(caseFileData.CikisNedeni),
        SonUcret: toNumberOrNull(caseFileData.SonUcret),
        TalepKidem: !!caseFileData.TalepKidem,
        TalepIhbar: !!caseFileData.TalepIhbar,
        TalepFazlaMesai: !!caseFileData.TalepFazlaMesai,
        TalepUbgt: !!caseFileData.TalepUbgt,
        TalepHaftaTatili: !!caseFileData.TalepHaftaTatili,
        TalepYillikIzin: !!caseFileData.TalepYillikIzin,
        TalepAgi: !!caseFileData.TalepAgi,
        TalepMaasAlacagi: !!caseFileData.TalepMaasAlacagi,
        ArabuluculukTarihi: toISODateOrNull(caseFileData.ArabuluculukTarihi),
        SonTutanakTarihi: toISODateOrNull(caseFileData.SonTutanakTarihi),
        ArabuluculukSonucu: toIntOrNull(caseFileData.ArabuluculukSonucu),
        ArabuluculukDosyaNo: toNullIfEmpty(caseFileData.ArabuluculukDosyaNo),
        IscilikDavaTarihi: toISODateOrNull(caseFileData.IscilikDavaTarihi),
        BilirkisiTarihi: toISODateOrNull(caseFileData.BilirkisiTarihi),
        IslahTarihi: toISODateOrNull(caseFileData.IslahTarihi),
      };

      const updatedFile = await caseFileAPI.update(cleanedData);
      alert('Dosya başarıyla güncellendi!');
      setShowEditForm(false);
      await loadCaseFile();
      await loadVekalet();
      await loadJobs();
      return updatedFile;
    } catch (err) {
      console.error('Error updating case file:', err);
      let msg = err.response?.data?.message || err.message || 'Bilinmeyen hata';
      const errors = err.response?.data?.errors;
      if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        const list = Object.keys(errors).map((k) => `${k}: ${(errors[k] || []).join(', ')}`).join('\n');
        if (list) msg += '\n\n' + list;
      }
      alert('Dosya güncellenirken hata oluştu: ' + msg);
      return null;
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!await onayla('Bu dosyayı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await documentAPI.delete(documentId);
      alert('Dosya silindi!');
      loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Dosya silinirken hata oluştu: ' + (err.response?.data?.message || err.message));
    }
  };

  /**
   * Belgeyi PDF olarak indirir.
   *  - Zaten PDF ise doğrudan indirilir.
   *  - Word / UDF / düz metin ise içeriği çıkarılıp yazdırma penceresi
   *    açılır; kullanıcı "PDF olarak kaydet"i seçer. Tarayıcının yazdırma
   *    hattı sistem fontlarını kullandığı için Türkçe kusursuz çıkar
   *    (jsPDF'in gömülü fontları ğ, ş, İ, ı desteklemiyor).
   *  - Diğer türlerde dönüştürme yapılamaz, kullanıcı bilgilendirilir.
   */
  const handleDownloadAsPdf = async (doc) => {
    const docId = doc.Id ?? doc.id;
    const ad = doc.FileName ?? doc.fileName ?? 'belge';
    const uz = getFileExtension(ad);

    if (uz === 'pdf') {
      await handleDownload(docId, ad, doc.ContentType ?? doc.contentType);
      return;
    }

    const ct = doc.ContentType ?? doc.contentType ?? '';
    const resimMi =
      ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(uz) || ct.startsWith('image/');

    try {
      const result = await documentAPI.download(docId);
      const blob = result?.blob ?? result;

      // Resimler metin çıkarıcıya gönderilemez; sayfaya yerleştirilerek yazdırılır
      if (resimMi) {
        await resmiPdfOlarakYazdir(ad, blob);
        return;
      }

      const cikti = await belgeMetniCikar(blob, ad);
      if (cikti.metin) {
        metniPdfOlarakYazdir(ad, cikti.metin);
      } else {
        alert(
          (cikti.hata || 'Bu belge PDF’e dönüştürülemiyor.') +
          '\n\nBelgeyi indirip Word/UYAP Editör ile açarak PDF’e aktarabilirsiniz.'
        );
      }
    } catch (err) {
      alert('PDF’e dönüştürülemedi: ' + (err?.response?.data?.message || err?.message || ''));
    }
  };

  /**
   * Belgeyi müvekkil portalına açar / kapatır.
   * Kutucuk anında tepki versin diye iyimser güncelleme yapılır;
   * istek başarısız olursa eski hâline döndürülür.
   */
  /* Rozet, avukatın "Müvekkil Gözünden" penceresini EN SON ne zaman açtığına
     göre hesaplanır: o andan sonraki müvekkil girişleri sayılır. Bu "son bakış"
     anı tarayıcıda saklanır; sunucuda tutmak yeni bir tablo/sütun gerektirirdi
     ve tek kullanıcılı bir ekran için gereksiz karmaşıklık olurdu. */
  const sonBakisAnahtari = `muvekkilGorunumuSonBakis_${id}`;

  const sonBakisOku = useCallback(() => {
    try { return localStorage.getItem(sonBakisAnahtari) || null; } catch (_) { return null; }
  }, [sonBakisAnahtari]);

  const ozetiTazele = useCallback(async () => {
    if (!id) return;
    try {
      const d = await caseFileAPI.muvekkilOzet(parseInt(id), sonBakisOku());
      setMuvekkilOzet(d);
    } catch (_) {
      setMuvekkilOzet(null);
    }
  }, [id, sonBakisOku]);

  useEffect(() => { ozetiTazele(); }, [ozetiTazele]);

  /** Pencere açılırken "son bakış" anını kaydeder, böylece rozet sıfırlanır. */
  const muvekkilGorunumunuAc = () => {
    try { localStorage.setItem(sonBakisAnahtari, new Date().toISOString()); } catch (_) { /* yoksay */ }
    setMuvekkilGorunumuAcik(true);
  };

  const muvekkilGorunumunuKapat = () => {
    setMuvekkilGorunumuAcik(false);
    ozetiTazele();
  };

  const toggleMuvekkilGorunurluk = async (docId, gorebilir) => {
    setMuvekkilOnaySaving(docId);
    const uygula = (deger) => setDocuments((prev) => prev.map((d) => {
      const id = d.Id ?? d.id;
      return id === docId ? { ...d, MuvekkilGorebilir: deger, muvekkilGorebilir: deger } : d;
    }));

    uygula(gorebilir);
    try {
      await documentAPI.setMuvekkilGorunurluk(docId, gorebilir);
    } catch (err) {
      uygula(!gorebilir);
      alert('Müvekkil görünürlüğü değiştirilemedi: ' + (err?.response?.data?.message || err?.message || ''));
    } finally {
      setMuvekkilOnaySaving(null);
    }
  };

  /* ---- Belge adını değiştirme ---- */
  const startEditDocumentName = (doc) => {
    if (!canEdit) return;
    setEditingDocNameId(doc.Id ?? doc.id);
    setDocNameDraft(doc.FileName ?? doc.fileName ?? '');
  };

  const cancelEditDocumentName = () => {
    setEditingDocNameId(null);
    setDocNameDraft('');
  };

  const saveDocumentName = async (docId) => {
    const ad = (docNameDraft || '').trim();
    if (!ad) { alert('Belge adı boş olamaz'); return; }
    setSavingDocName(true);
    try {
      const guncel = await documentAPI.rename(docId, ad);
      // Sunucu uzantıyı koruyabilir; dönen adı esas alıyoruz.
      const yeniAd = guncel?.FileName ?? guncel?.fileName ?? ad;
      setDocuments((prev) => prev.map((d) => {
        const id = d.Id ?? d.id;
        if (id !== docId) return d;
        return { ...d, FileName: yeniAd, fileName: yeniAd };
      }));
      cancelEditDocumentName();
    } catch (err) {
      alert('Belge adı değiştirilemedi: ' + (err?.response?.data?.message || err?.message || ''));
    } finally {
      setSavingDocName(false);
    }
  };

  const startEditDocumentType = (doc) => {
    if (!canEdit) return;
    const docId = doc.Id ?? doc.id;
    const currentType = doc.DocumentType ?? doc.documentType ?? DOCUMENT_TYPE.Other;
    setEditingDocTypeId(docId);
    setDocTypeDraft(currentType);
    setDocTypeMakbuzTarihi(formatDateForDateInput(doc.FaturaTarihi ?? doc.faturaTarihi) || '');
    setDocTypeMakbuzTutar(
      doc.Tutar != null || doc.tutar != null
        ? String(doc.Tutar ?? doc.tutar)
        : ''
    );
  };

  const cancelEditDocumentType = () => {
    setEditingDocTypeId(null);
    setDocTypeDraft(DOCUMENT_TYPE.Other);
    setDocTypeMakbuzTarihi('');
    setDocTypeMakbuzTutar('');
  };

  const saveDocumentType = async (docId) => {
    const nextType = parseInt(docTypeDraft, 10);
    if (Number.isNaN(nextType)) {
      alert('Geçersiz belge türü');
      return;
    }
    if (nextType === DOCUMENT_TYPE.Makbuz && isOzelFile) {
      alert('Özel dosyalara makbuz seçilemez.');
      return;
    }
    if (nextType === DOCUMENT_TYPE.Makbuz) {
      if (!docTypeMakbuzTarihi) {
        alert('Makbuz tarihi zorunludur.');
        return;
      }
      if (docTypeMakbuzTutar === '' || docTypeMakbuzTutar == null) {
        alert('Makbuz tutarı zorunludur.');
        return;
      }
    }

    setSavingDocType(true);
    try {
      await documentAPI.updateType(docId, {
        documentType: nextType,
        faturaTarihi: nextType === DOCUMENT_TYPE.Makbuz ? toISODateOrNull(docTypeMakbuzTarihi) : null,
        tutar: nextType === DOCUMENT_TYPE.Makbuz ? Number(docTypeMakbuzTutar) : null
      });
      cancelEditDocumentType();
      await loadDocuments();
    } catch (err) {
      console.error('Belge türü güncellenemedi:', err);
      alert('Belge türü güncellenemedi: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingDocType(false);
    }
  };

  const handleDownload = async (documentId, fileName, contentType) => {
    try {
      const result = await documentAPI.download(documentId);
      const blob = result?.blob ?? result;
      const name = result?.fileName || fileName || 'belge';
      const mime = result?.contentType || contentType || null;
      await downloadBlob(blob, name, mime);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Dosya indirilirken hata oluştu: ' + (err.response?.data?.message || err.message));
    }
  };

  const handlePreview = async (doc) => {
    const docId = doc.Id ?? doc.id;
    
    try {
      // Önce eski previewUrl'i temizle (memory leak önleme)
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
      
      // Önce modalı hemen aç - belge adı gösterilsin, loading gösterilsin
      setPreviewDocument(doc);
      setPreviewUrl(null);
      setPreviewText(null);
      setPreviewLoading(true);

      // Sonra belgeyi indir ve URL oluştur (arka planda)
      const result = await documentAPI.download(docId);
      const blob = result?.blob ?? result;
      const mime = result?.contentType || doc.ContentType || doc.contentType || blob.type;
      const typed = mime && blob.type !== mime ? new Blob([blob], { type: mime }) : blob;
      const url = window.URL.createObjectURL(typed);

      // URL'i set et (previewDocument zaten set edildi, bu yüzden doğru belge gösterilecek)
      setPreviewUrl(url);

      // Resim/PDF dışındaki türlerde (Word, UDF, düz metin) içeriği çıkar
      const ad = doc.FileName ?? doc.fileName ?? '';
      const uz = getFileExtension(ad);
      const gorselVeyaPdf =
        ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(uz) ||
        (mime && (mime.startsWith('image/') || mime === 'application/pdf'));
      if (!gorselVeyaPdf) {
        const cikti = await belgeMetniCikar(typed, ad);
        setPreviewText(cikti);
      }
    } catch (err) {
      console.error('Error previewing file:', err);
      alert('Dosya önizlenirken hata oluştu: ' + (err.response?.data?.message || err.message));
      setPreviewDocument(null);
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviousDocument = async () => {
    if (!previewDocument || documents.length === 0) return;
    
    const currentIndex = documents.findIndex(d => (d.Id ?? d.id) === (previewDocument.Id ?? previewDocument.id));
    if (currentIndex <= 0) return; // İlk dosyada
    
    const previousDoc = documents[currentIndex - 1];
    // handlePreview zaten eski URL'i temizliyor, bu yüzden burada tekrar temizlemeye gerek yok
    await handlePreview(previousDoc);
  };

  const handleNextDocument = async () => {
    if (!previewDocument || documents.length === 0) return;
    
    const currentIndex = documents.findIndex(d => (d.Id ?? d.id) === (previewDocument.Id ?? previewDocument.id));
    if (currentIndex >= documents.length - 1) return; // Son dosyada
    
    const nextDoc = documents[currentIndex + 1];
    // handlePreview zaten eski URL'i temizliyor, bu yüzden burada tekrar temizlemeye gerek yok
    await handlePreview(nextDoc);
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewDocument(null);
    setPreviewUrl(null);
  };

  const getFileExtension = (fileName) => {
    if (!fileName) return '';
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  };

  const canPreview = (fileName, contentType) => {
    const ext = getFileExtension(fileName);
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const pdfExt = 'pdf';
    return imageExts.includes(ext) || ext === pdfExt || contentType?.startsWith('image/') || contentType === 'application/pdf';
  };

  const formatDate = (date) => {
    if (!date) return 'Belirtilmemiş';
    // Sadece tarih göster (saat yok)
    return formatDateOnly(date);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const masrafCaseFiles = useMemo(() => (caseFile ? [caseFile] : []), [caseFile]);

  // Masraflar ve Ödemeler: makbuzlar belge listesinden ayrı durur; gelen ödemeler,
  // müvekkile ödenenler ve masraflar masraf türüne göre ayrılır (özel dosyalarda bu alan yok).
  const odemeAlaniVar = !isOzelFile;
  const kullaniciAdmin = !!user && (user.Role === 'Admin' || user.role === 'Admin');
  const makbuzlar = useMemo(() => documents.filter(makbuzBelgesi), [documents]);
  // Belgeler alanında dekont / makbuz / masraf belgesi yok; onlar kendi sekmelerinde.
  const alanBelgeleri = useMemo(() => {
    if (!odemeAlaniVar) return documents;
    return belgeAlani === 'odemeler'
      ? documents.filter((d) => odemeSekmesiOf(d) === odemeSekmesi)
      : documents.filter((d) => !odemeSekmesiOf(d));
  }, [documents, belgeAlani, odemeSekmesi, odemeAlaniVar]);
  const odemeGruplari = useMemo(
    () => odemeleriAyir(masraflar, { avukat: !kullaniciAdmin }),
    [masraflar, kullaniciAdmin]
  );
  /* Belgeler'de adında arabuluculuk geçen evrak: dosya türü ne olursa olsun
     (icra dahil) Arabuluculuk bölümü görünür ve boşsa kendiliğinden okunur. */
  const arabuluculukBelgeleri = useMemo(
    () => documents.filter((d) => belgeTuruOf(d) === DOCUMENT_TYPE.Other
      && /ARABULUC|SON[\s_.-]*TUTANA|ANLASMA[\s_.-]*(BELGES|TUTANA)/.test(
        aramaMetni(`${d.FileName ?? d.fileName ?? ''} ${d.Description ?? d.description ?? ''}`))),
    [documents]
  );
  // Belgeler'de kalmış dekont / masraf belgeleri ("müvekkile yapılan ödeme dekontu", "sigortadan
  // gelen ödeme", "tahkim başvuru ücreti"…): adından tanınır, Belgeler'in üstünde kendi sekmesiyle
  // önerilir; kullanıcı onaylarsa taşınır. "Gösterme" denenler bu dosyada gizli kalır.
  const [gizliOdemeOnerileri, setGizliOdemeOnerileri] = useState([]);
  useEffect(() => {
    try {
      setGizliOdemeOnerileri(JSON.parse(localStorage.getItem(`odemeOnerisiGizli:${id}`) || '[]'));
    } catch (e) {
      setGizliOdemeOnerileri([]);
    }
  }, [id]);
  const odemeOnerileri = useMemo(() => {
    if (!odemeAlaniVar) return [];
    return documents
      .filter((d) => belgeTuruOf(d) === DOCUMENT_TYPE.Other && !gizliOdemeOnerileri.includes(d.Id ?? d.id))
      .map((d) => ({ belge: d, sekme: odemeBelgesiSekmesi(`${d.FileName ?? d.fileName ?? ''} ${d.Description ?? d.description ?? ''}`) }))
      // Avukat hesapları Müvekkile Ödenen sekmesini görmez; o belgeler onlara önerilmez.
      .filter((o) => o.sekme && (kullaniciAdmin || o.sekme !== 'muvekkile'));
  }, [documents, gizliOdemeOnerileri, odemeAlaniVar, kullaniciAdmin]);

  // Belgeleri sırala ve filtrele - Hook'lar erken return'lerden önce olmalı
  const sortedDocuments = useMemo(() => {
    let filtered = alanBelgeleri;

    // Arama filtresi
    if (documentSearchTerm.trim()) {
      // Türkçe harf, büyük/küçük ve Mac'ten yüklenen adların yazılış farkı gözetilmez
      const aranan = aramaMetni(documentSearchTerm);
      filtered = alanBelgeleri.filter(doc => {
        const fileName = aramaMetni(doc.FileName ?? doc.fileName);
        const description = aramaMetni(doc.Description ?? doc.description);
        const docType = aramaMetni(DOCUMENT_TYPE_LABELS[doc.DocumentType ?? doc.documentType ?? 0]);
        const uploadedBy = aramaMetni(doc.UploadedByUserName ?? doc.uploadedByUserName);

        return fileName.includes(aranan) ||
               description.includes(aranan) ||
               docType.includes(aranan) ||
               uploadedBy.includes(aranan);
      });
    }
    
    // Sütun sıralaması yoksa API SortOrder sırası korunur
    if (!documentSortColumn) {
      return [...filtered].sort((a, b) => {
        const sa = a.SortOrder ?? a.sortOrder ?? 0;
        const sb = b.SortOrder ?? b.sortOrder ?? 0;
        if (sa !== sb) return sa - sb;
        return (a.Id ?? a.id ?? 0) - (b.Id ?? b.id ?? 0);
      });
    }
    
    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;
      
      if (documentSortColumn === 'fileName') {
        aValue = (a.FileName ?? a.fileName ?? '').toLowerCase();
        bValue = (b.FileName ?? b.fileName ?? '').toLowerCase();
      } else if (documentSortColumn === 'uploadDate') {
        aValue = a.CreatedDate ?? a.createdDate ?? '';
        bValue = b.CreatedDate ?? b.createdDate ?? '';
      } else {
        return 0;
      }
      
      if (documentSortColumn === 'uploadDate') {
        // Tarih karşılaştırması
        const dateA = aValue ? new Date(aValue).getTime() : 0;
        const dateB = bValue ? new Date(bValue).getTime() : 0;
        return documentSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      } else {
        // String karşılaştırması
        if (aValue < bValue) return documentSortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return documentSortDirection === 'asc' ? 1 : -1;
        return 0;
      }
    });
    
    return sorted;
  }, [alanBelgeleri, documentSortColumn, documentSortDirection, documentSearchTerm]);

  /* Görünen listede (alan ya da arama süzgeci) değişen sırayı bütün listeye
     yerleştirir: görünmeyen belgelerin yeri değişmez, sunucuya tam sıra gider. */
  const tumSirayaYerlestir = (gorunenYeniSira) => {
    const sira = (d) => d.SortOrder ?? d.sortOrder ?? 0;
    const tum = [...documents].sort((a, b) => (sira(a) - sira(b)) || ((a.Id ?? a.id ?? 0) - (b.Id ?? b.id ?? 0)));
    const gorunen = new Set(gorunenYeniSira.map((d) => d.Id ?? d.id));
    let k = 0;
    return tum.map((d) => (gorunen.has(d.Id ?? d.id) ? gorunenYeniSira[k++] : d));
  };

  const odemeSekmesinde = odemeAlaniVar && belgeAlani === 'odemeler';
  // Her sekmede o sekmenin belgeleri (dekont, makbuz…) ve Masraflar bölümündeki kayıtlar.
  const sekmeBelgeSayisi = (sekme) => documents.filter((d) => odemeSekmesiOf(d) === sekme).length;
  const sekmeTanimi = (anahtar, ad, kayitlar) => {
    const belge = sekmeBelgeSayisi(anahtar);
    return { anahtar, ad, belge, kayit: kayitlar.length, sayi: belge + kayitlar.length, toplam: toplamTutar(kayitlar) };
  };
  const odemeSekmeleri = [
    sekmeTanimi('masraf', 'Masraflar', odemeGruplari.masraf),
    sekmeTanimi('gelen', 'Gelen Ödemeler', odemeGruplari.gelen),
    // Avukat hesapları müvekkile yapılan ödemeleri Masraflar bölümünde de görmez.
    ...(kullaniciAdmin ? [sekmeTanimi('muvekkile', 'Müvekkile Ödenen', odemeGruplari.muvekkile)] : []),
    {
      anahtar: 'makbuz', ad: 'Makbuzlar', belge: makbuzlar.length, kayit: 0, sayi: makbuzlar.length,
      toplam: toplamTutar(makbuzlar.map((d) => ({ tutar: d.Tutar ?? d.tutar }))),
    },
  ];
  const belgeAlaniDegistir = (alan) => {
    setBelgeAlani(alan);
    setSelectedDocuments([]);
  };
  // "+ … Ekle": Belgeler'den açılırsa "Nereye Eklensin?" seçilmeden yüklenmez;
  // bir sekmeden açılırsa o sekme seçili gelir.
  const ekleDugmesiAdi = odemeSekmesinde
    ? { masraf: '+ Masraf Belgesi Ekle', gelen: '+ Dekont Ekle', muvekkile: '+ Dekont Ekle', makbuz: '+ Makbuz Ekle' }[odemeSekmesi]
    : '+ Yeni Belge Ekle';
  const belgeFormunuAcKapat = () => {
    if (Object.values(showUploadForm).some(Boolean)) {
      setShowUploadForm({});
      return;
    }
    const sekmeTuru = odemeSekmesinde ? SEKME_BELGE_TURU[odemeSekmesi] : null;
    setBelgeHedefiSecildi(!odemeAlaniVar || sekmeTuru != null);
    setShowUploadForm({ [sekmeTuru ?? DOCUMENT_TYPE.Other]: true });
  };
  // Seçili belgeleri başka bir bölüme taşır (eski dekontları sekmelerine almak için).
  // Makbuz tarihi ve tutarı ister; makbuza tek tek, "Belge Türü" hücresinden taşınır.
  // tasimalar: [{ id, hedef }] — her belge kendi hedefine (sekmesine ya da Belgeler'e) taşınır.
  const belgeleriTasi = async (tasimalar, soru) => {
    const gecerli = tasimalar.filter((t) => t.hedef === 'belgeler' || SEKME_BELGE_TURU[t.hedef] != null);
    if (!gecerli.length) return;
    if (!(await onayla(soru, { onay: 'Taşı' }))) return;
    setTopluTasiniyor(true);
    let hata = 0;
    for (const { id: belgeId, hedef } of gecerli) {
      const yeniTur = hedef === 'belgeler' ? DOCUMENT_TYPE.Other : SEKME_BELGE_TURU[hedef];
      try {
        await documentAPI.updateType(belgeId, { documentType: yeniTur, faturaTarihi: null, tutar: null });
      } catch (err) {
        hata += 1;
      }
    }
    setTopluTasiniyor(false);
    setSelectedDocuments([]);
    await loadDocuments();
    if (hata) alert(`${hata} belge taşınamadı; tekrar deneyin.`);
  };
  const seciliBelgeleriTasi = (hedef) => {
    const idler = sortedDocuments.map((d) => d.Id ?? d.id).filter((i) => selectedDocuments.includes(i));
    return belgeleriTasi(idler.map((i) => ({ id: i, hedef })), `${idler.length} belge "${SEKME_ADI[hedef]}" bölümüne taşınsın mı?`);
  };
  const odemeOnerileriniTasi = () => belgeleriTasi(
    odemeOnerileri.map((o) => ({ id: o.belge.Id ?? o.belge.id, hedef: o.sekme })),
    `${odemeOnerileri.length} belge kendi sekmesine taşınsın mı?`
  );
  const odemeOnerileriniGizle = () => {
    const yeni = [...new Set([...gizliOdemeOnerileri, ...odemeOnerileri.map((o) => o.belge.Id ?? o.belge.id)])];
    setGizliOdemeOnerileri(yeni);
    try {
      localStorage.setItem(`odemeOnerisiGizli:${id}`, JSON.stringify(yeni));
    } catch (e) {
      // tarayıcı saklamaya izin vermiyorsa yalnız bu açılışta gizli kalır
    }
  };
  // Ödeme kayıtları Masraflar bölümünden eklenir / düzenlenir; telefonda o sekmeye geçer.
  const masraflaraGit = () => {
    setAktifSekme('masraflar');
    setMasrafTab('masraflar');
    setTimeout(() => {
      document.querySelector('.cd-panel-masraflar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const persistDocumentOrder = async (orderedDocs) => {
    const fileId = parseInt(id, 10);
    if (!fileId || !orderedDocs?.length) return;
    const ids = orderedDocs.map((d) => d.Id ?? d.id).filter(Boolean);
    setDocReorderSaving(true);
    try {
      await documentAPI.reorder(fileId, ids);
    } catch (err) {
      console.error('Belge sırası kaydedilemedi:', err);
      alert('Belge sırası kaydedilemedi: ' + (err.response?.data?.message || err.message));
      await loadDocuments();
    } finally {
      setDocReorderSaving(false);
    }
  };

  const handleDocDragStart = (e, docId) => {
    setDraggingDocId(docId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(docId));
  };

  const handleDocDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDocDrop = async (e, targetDocId) => {
    e.preventDefault();
    const sourceId = parseInt(e.dataTransfer.getData('text/plain') || draggingDocId, 10);
    setDraggingDocId(null);
    if (!sourceId || sourceId === targetDocId) return;
    setDocumentSortColumn(null);
    const current = [...sortedDocuments];
    const from = current.findIndex((d) => (d.Id ?? d.id) === sourceId);
    const to = current.findIndex((d) => (d.Id ?? d.id) === targetDocId);
    if (from < 0 || to < 0) return;
    const [item] = current.splice(from, 1);
    current.splice(to, 0, item);
    const withOrder = tumSirayaYerlestir(current).map((d, i) => ({ ...d, SortOrder: i + 1, sortOrder: i + 1 }));
    setDocuments((prev) => {
      const byId = new Map(withOrder.map((d) => [d.Id ?? d.id, d]));
      return prev.map((d) => byId.get(d.Id ?? d.id) || d);
    });
    await persistDocumentOrder(withOrder);
  };

  const handleDocDragEnd = () => setDraggingDocId(null);

  const moveDocumentByOffset = async (docId, offset) => {
    if (!canEdit || !offset) return;
    setDocumentSortColumn(null);
    const current = [...sortedDocuments];
    const from = current.findIndex((d) => (d.Id ?? d.id) === docId);
    if (from < 0) return;
    const to = from + offset;
    if (to < 0 || to >= current.length) return;
    const [item] = current.splice(from, 1);
    current.splice(to, 0, item);
    const withOrder = tumSirayaYerlestir(current).map((d, i) => ({ ...d, SortOrder: i + 1, sortOrder: i + 1 }));
    setDocuments((prev) => {
      const byId = new Map(withOrder.map((d) => [d.Id ?? d.id, d]));
      return prev.map((d) => byId.get(d.Id ?? d.id) || d);
    });
    await persistDocumentOrder(withOrder);
  };

  if (loading) {
    return (
      <div className="case-file-detail case-file-detail-full-width">
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="case-file-detail case-file-detail-full-width">
        <div className="error">{error}</div>
        {!embedded && (
          <button onClick={() => navigate(-1)} className="btn btn-secondary btn-sm">
            Geri Dön
          </button>
        )}
      </div>
    );
  }

  if (!caseFile) {
    return (
      <div className="case-file-detail case-file-detail-full-width">
        <div className="error">Dosya bulunamadı</div>
        {!embedded && (
          <button onClick={() => navigate(-1)} className="btn btn-secondary btn-sm">
            Geri Dön
          </button>
        )}
      </div>
    );
  }

  const formatDateTime = (date) => {
    if (!date) return '-';
    // formatDateForDisplay fonksiyonunu kullan (Dashboard ile aynı)
    return formatDateForDisplay(date);
  };

  const foyNo = caseFile.FoyNo ?? caseFile.foyNo ?? 'N/A';
  const muvekkil = caseFile.Muvekkil ?? caseFile.muvekkil ?? 'N/A';
  const isSirket = !!(caseFile.IsSirket ?? caseFile.isSirket);
  const muvekkilTC = caseFile.MuvekkilTC ?? caseFile.muvekkilTC;
  const muvekkilAracSigortaSirketi = caseFile.MuvekkilAracSigortaSirketi ?? caseFile.muvekkilAracSigortaSirketi;
  const karsiTarafSigortaSirketi = caseFile.KarsiTarafSigortaSirketi ?? caseFile.karsiTarafSigortaSirketi ?? caseFile.KarsiSigorta ?? caseFile.karsiSigorta;
  const karsiTaraf1 = caseFile.KarsiTaraf1 ?? caseFile.karsiTaraf1;
  const karsiTaraf2 = caseFile.KarsiTaraf2 ?? caseFile.karsiTaraf2;
  const muvekkilTelefon = caseFile.MuvekkilTelefon ?? caseFile.muvekkilTelefon;
  const muvekkilAdres = caseFile.MuvekkilAdres ?? caseFile.muvekkilAdres;
  const muvekkilDogumTarihi = caseFile.MuvekkilDogumTarihi ?? caseFile.muvekkilDogumTarihi;
  const muvekkilIBAN = caseFile.MuvekkilIBAN ?? caseFile.muvekkilIBAN;
  const muvekkilPlaka = caseFile.MuvekkilPlaka ?? caseFile.muvekkilPlaka;
  const karsiTarafPlaka = caseFile.KarsiTarafPlaka ?? caseFile.karsiTarafPlaka;
  const sigortaBasvuruTarihi = caseFile.SigortaBasvuruTarihi ?? caseFile.sigortaBasvuruTarihi;
  const davaAcilisTarihi = caseFile.DavaAcilisTarihi ?? caseFile.davaAcilisTarihi;
  const sozlesmeOrani = caseFile.SozlesmeOrani ?? caseFile.sozlesmeOrani;
  const sozlesmeOraniKdvDahil = !!(caseFile.SozlesmeOraniKdvDahil ?? caseFile.sozlesmeOraniKdvDahil);
  const mahkemeEsas = caseFile.MahkemeEsas ?? caseFile.mahkemeEsas;
  const kazaSehri = caseFile.KazaSehri ?? caseFile.kazaSehri;
  const kazaTarihi = caseFile.KazaTarihi ?? caseFile.kazaTarihi;
  const dosyaGelisTarihi = caseFile.DosyaGelisTarihi ?? caseFile.dosyaGelisTarihi;
  const davaTuru = caseFile.DavaTuru ?? caseFile.davaTuru ?? 0;
  const isBosanmaTuru = [DAVA_TURU.Bosanma, DAVA_TURU.MalRejimininTasfiyesi].includes(parseInt(davaTuru));
  const isCezaTuru = [DAVA_TURU.Ceza, DAVA_TURU.Sorusturma, DAVA_TURU.SavcilikSikayet, DAVA_TURU.SavcilikSorusturma].includes(parseInt(davaTuru));
  const isIscilikTuru = parseInt(davaTuru) === DAVA_TURU.IscilikAlacaklari;
  // İş kazasında davalı işverendir; karşı şirket bilgileri ayrı bölümde durur.
  const isIsKazasiTuru = [
    DAVA_TURU.IsKazasiVefat,           // 19
    DAVA_TURU.IsKazasiMaluliyet,       // 20
    DAVA_TURU.TrafikIsKazasiMaluliyet, // 21
    DAVA_TURU.TrafikIsKazasiVefat,     // 22
  ].includes(parseInt(davaTuru));
  // Bu listenin sunucu tarafindaki karsiligi SigortaBasvuruService ve
  // TahkimBasvuruService icindeki AllowedDavaTurleri'dir; ikisi birlikte
  // guncellenmeli, yoksa dugme gorunur ama istek reddedilir.
  const canSigortaBasvuruHazirla = [
    DAVA_TURU.TrafikKazasi,
    DAVA_TURU.MaddiHasar,
    DAVA_TURU.DegerKaybi,
    DAVA_TURU.MaddiHasarDegerKaybi,
    DAVA_TURU.MaddiHasarDegerKaybiAracMahrumiyet,
    DAVA_TURU.Kasko,
    DAVA_TURU.MaddiHasarImms,
    DAVA_TURU.DegerKaybiImms
  ].includes(parseInt(davaTuru));
  const canTahkimBasvuruHazirla = canSigortaBasvuruHazirla;

  const handleSigortaBasvuruHazirla = async () => {
    if (!id || generatingSigortaBasvuru) return;
    setGeneratingSigortaBasvuru(true);
    try {
      const response = await documentAPI.generateSigortaBasvuru(parseInt(id));
      const blob = response.data;
      // Hata JSON geldiyse (blob olarak)
      if (blob.type && blob.type.includes('application/json')) {
        const text = await blob.text();
        let msg = 'Sigorta başvurusu hazırlanamadı';
        try { msg = JSON.parse(text)?.message || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      // Sunucu Content-Disposition ile ad gonderiyor; bu yalnizca
      // o gelmezse kullanilan yedek. Muvekkil adiyla ayni bicimde.
      let fileName = `${(muvekkil || 'Muvekkil').trim()} - Sigorta Basvuru.docx`;
      const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
      if (disposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
        if (match?.[1]) fileName = decodeURIComponent(match[1]);
      }
      await downloadBlob(blob, fileName);
    } catch (err) {
      console.error('Sigorta başvurusu hatası:', err);
      const msg = err.response?.data?.message || err.message || 'Sigorta başvurusu hazırlanamadı';
      // blob error body
      if (err.response?.data instanceof Blob) {
        try {
          const t = await err.response.data.text();
          const j = JSON.parse(t);
          alert(j.message || msg);
          return;
        } catch { /* fallthrough */ }
      }
      alert(msg);
    } finally {
      setGeneratingSigortaBasvuru(false);
    }
  };

  const handleTahkimBasvuruHazirla = async () => {
    if (!id || generatingTahkimBasvuru) return;
    setGeneratingTahkimBasvuru(true);
    try {
      const response = await documentAPI.generateTahkimBasvuru(parseInt(id));
      const blob = response.data;
      if (blob.type && blob.type.includes('application/json')) {
        const text = await blob.text();
        let msg = 'Tahkim başvurusu hazırlanamadı';
        try { msg = JSON.parse(text)?.message || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      let fileName = `${(muvekkil || 'Muvekkil').trim()} - Tahkim Basvuru.docx`;
      const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
      if (disposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
        if (match?.[1]) fileName = decodeURIComponent(match[1]);
      }
      await downloadBlob(blob, fileName);
    } catch (err) {
      console.error('Tahkim başvurusu hatası:', err);
      const msg = err.response?.data?.message || err.message || 'Tahkim başvurusu hazırlanamadı';
      if (err.response?.data instanceof Blob) {
        try {
          const t = await err.response.data.text();
          const j = JSON.parse(t);
          alert(j.message || msg);
          return;
        } catch { /* fallthrough */ }
      }
      alert(msg);
    } finally {
      setGeneratingTahkimBasvuru(false);
    }
  };
  let bosanmaCocuklar = [];
  try {
    const raw = caseFile.CocuklarJson ?? caseFile.cocuklarJson;
    bosanmaCocuklar = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(bosanmaCocuklar)) bosanmaCocuklar = [];
  } catch { bosanmaCocuklar = []; }
  let cezaTaraflar = [];
  try {
    const rawT = caseFile.TaraflarJson ?? caseFile.taraflarJson;
    cezaTaraflar = rawT ? JSON.parse(rawT) : [];
    if (!Array.isArray(cezaTaraflar)) cezaTaraflar = [];
  } catch { cezaTaraflar = []; }
  const cezaMuvekkilRolu = [
    (caseFile.MuvekkilSanik ?? caseFile.muvekkilSanik) ? 'Sanık' : null,
    (caseFile.MuvekkilMusteki ?? caseFile.muvekkilMusteki) ? 'Müşteki' : null,
    (caseFile.MuvekkilSupheli ?? caseFile.muvekkilSupheli) ? 'Şüpheli' : null,
    (caseFile.MuvekkilDavaci ?? caseFile.muvekkilDavaci) ? 'Davacı' : null,
    (caseFile.MuvekkilDavali ?? caseFile.muvekkilDavali) ? 'Davalı' : null,
  ].filter(Boolean).join(', ');
  const cezaTedbirler = [
    (caseFile.TedbirTutuklu ?? caseFile.tedbirTutuklu) ? 'Tutuklu' : null,
    (caseFile.TedbirAdliKontrol ?? caseFile.tedbirAdliKontrol) ? 'Adli Kontrol' : null,
    (caseFile.TedbirYakalamaKarari ?? caseFile.tedbirYakalamaKarari) ? 'Yakalama Kararı' : null,
  ].filter(Boolean).join(', ');
  const referans = caseFile.ReferansAdi ?? caseFile.referansAdi ?? caseFile.Referans?.Name ?? caseFile.referans?.name;
  const raporTarihi = caseFile.RaporTarihi ?? caseFile.raporTarihi;
  const tahkimBasvuruTarihi = caseFile.TahkimBasvuruTarihi ?? caseFile.tahkimBasvuruTarihi;
  const zamanAsimiTarihi = caseFile.ZamanAsimiTarihi ?? caseFile.zamanAsimiTarihi;
  const sulhDurumu = caseFile.SulhDurumu ?? caseFile.sulhDurumu;
  const sonIslemYapanAdi = caseFile.SonIslemYapanAdi ?? caseFile.sonIslemYapanAdi;
  const durusmaTarihi = caseFile.DurusmaTarihi ?? caseFile.durusmaTarihi ?? null;
  const karsiTarafSigortaSirketiVekili = caseFile.KarsiTarafSigortaSirketiVekili ?? caseFile.karsiTarafSigortaSirketiVekili;
  const icraDosyaNo = caseFile.IcraDosyaNo ?? caseFile.icraDosyaNo;
  // Not: DB alanı "MahkemaBilgisi" (yazım böyle); olası MahkemeBilgisi typo'suna da bak
  const mahkemaBilgisi =
    caseFile.MahkemaBilgisi ??
    caseFile.mahkemaBilgisi ??
    caseFile.MahkemeBilgisi ??
    caseFile.mahkemeBilgisi ??
    '';
  const kesinlesmeTarihi = caseFile.KesinlesmeTarihi ?? caseFile.kesinlesmeTarihi;
  const icraAyarDetay = (() => {
    const raw = caseFile.IcraHesapAyar ?? caseFile.icraHesapAyar;
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
    try { return JSON.parse(raw) || {}; } catch { return {}; }
  })();
  const borclularDetay = Array.isArray(icraAyarDetay.borclular) ? icraAyarDetay.borclular : [];
  const icraIlamsizDetay = icraAyarDetay.ilamsiz !== false;
  const savcilikCezaDosyaNo = caseFile.SavcilikCezaDosyaNo ?? caseFile.savcilikCezaDosyaNo;
  const tahkimBasvuruNo = caseFile.TahkimBasvuruNo ?? caseFile.tahkimBasvuruNo;
  const isIcraTuru = [
    DAVA_TURU.IcraDava,
    DAVA_TURU.IcraTaki,
    DAVA_TURU.AracMahrumiyetBedeliIcraTakip
  ].includes(parseInt(davaTuru));
  const esasGosterilen = (() => {
    if (hasDetailValue(mahkemeEsas)) return mahkemeEsas;
    if (isIcraTuru && hasDetailValue(icraDosyaNo)) return icraDosyaNo;
    if (hasDetailValue(tahkimBasvuruNo)) return tahkimBasvuruNo;
    if (hasDetailValue(icraDosyaNo)) return icraDosyaNo;
    return mahkemeEsas || '';
  })();
  const hasBorcluBilgi = borclularDetay.some((b) =>
    String(b.adSoyad || b.AdSoyad || '').trim() ||
    String(b.tc || b.TC || b.Tc || '').trim() ||
    String(b.adres || b.Adres || '').trim()
  );
  const itirazBasvuruNo = caseFile.ItirazBasvuruNo ?? caseFile.itirazBasvuruNo;
  const ibraAsamasi = caseFile.IbraAsamasi ?? caseFile.ibraAsamasi;
  const gelenOdemeDurumu = caseFile.GelenOdemeDurumu ?? caseFile.gelenOdemeDurumu;
  const gidenOdemeDurumu = caseFile.GidenOdemeDurumu ?? caseFile.gidenOdemeDurumu;
  const smm = caseFile.SMM ?? caseFile.smm;
  const odemeDetaydaGoster = !!(caseFile.OdemeBilgileriGoster ?? caseFile.odemeBilgileriGoster);
  /* Bu render sırasında showDetay ile sorulan alan anahtarları.
     "Diğer Bilgiler" bölümü bunun dışında kalan dolu alanları listeler.

     KANCA DEĞİL, düz değişken: bu satır bileşenin erken return'lerinden
     sonra geliyor; kanca olsaydı render'lar arasında kanca sayısı
     değişir ve React çökerdi. */
  const gosterilenAnahtarlar = new Set();

  const detayAlanGoster = parseDetayAlanGoster(caseFile.DetayAlanGoster ?? caseFile.detayAlanGoster);
  const forceDetay = (key) => !!detayAlanGoster[key];
  /* showDetay her cagrildiginda anahtari not ediyoruz; "Diger Bilgiler"
     bolumu boylece hangi alanlarin zaten gosterildigini biliyor. */
  const showDetay = (key, value) => {
    gosterilenAnahtarlar.add(key);
    return hasDetailValue(value) || forceDetay(key) || (odemeDetaydaGoster && [
      'GelenOdemeDurumu', 'GidenOdemeDurumu', 'IbraAsamasi', 'SMM', 'SulhDurumu', 'KarsiTarafSigortaSirketiVekili'
    ].includes(key));
  };
  const hasOdemeAlani =
    hasDetailValue(gelenOdemeDurumu) ||
    hasDetailValue(gidenOdemeDurumu) ||
    hasDetailValue(ibraAsamasi) ||
    hasDetailValue(smm) ||
    hasDetailValue(sulhDurumu) ||
    ((isBosanmaTuru || isCezaTuru || isIscilikTuru) && hasDetailValue(karsiTarafSigortaSirketiVekili));
  const showOdemeBolumu = odemeDetaydaGoster || hasOdemeAlani
    || forceDetay('GelenOdemeDurumu') || forceDetay('GidenOdemeDurumu')
    || forceDetay('IbraAsamasi') || forceDetay('SMM') || forceDetay('SulhDurumu')
    || forceDetay('KarsiTarafSigortaSirketiVekili');

  /* Dolu olduğu hâlde ekranda hiçbir yerde gösterilmeyen alanlar.
     Görünen alanları tek tek takip etmek yerine, sayfanın gösterdiği
     anahtarları toplayıp farkını alıyoruz — yeni bir alan eklendiğinde
     bu liste kendiliğinden doğru kalır.

     FONKSİYON, kanca değil: hem erken return'lerden sonra kanca
     kullanılamaz, hem de liste ancak bütün showDetay çağrıları
     yapıldıktan SONRA doğru olur. Bu yüzden JSX'in sonunda çağrılıyor. */
  const gizliKalanAlanlar = () => {
    if (!caseFile) return [];
    const gosterilen = new Set(gosterilenAnahtarlar);
    const sonuc = [];

    for (const [anahtar, ham] of Object.entries(caseFile)) {
      if (GIZLI_ALANLAR.has(anahtar)) continue;
      if (anahtar.endsWith('Json') || anahtar.endsWith('Id')) continue;
      if (typeof ham === 'object' && ham !== null) continue;
      if (!hasDetailValue(ham)) continue;

      // PascalCase / camelCase ikilisinden biri gösterildiyse yeter.
      const buyuk = anahtar.charAt(0).toUpperCase() + anahtar.slice(1);
      const kucuk = anahtar.charAt(0).toLowerCase() + anahtar.slice(1);
      if (gosterilen.has(buyuk) || gosterilen.has(kucuk)) continue;

      const etiket = ALAN_ETIKETLERI[buyuk];
      if (!etiket) continue;   // adı bilinmeyen alanı göstermiyoruz

      let deger = ham;
      if (typeof ham === 'boolean') deger = ham ? 'Evet' : 'Hayır';
      else if (/Tarih/i.test(anahtar)) {
        deger = /Durusma/i.test(anahtar) ? formatDateForDisplay(ham) : formatDateOnly(ham);
      }

      sonuc.push({ anahtar: buyuk, etiket, deger: String(deger) });
    }

    return sonuc.sort((a, b) => a.etiket.localeCompare(b.etiket, 'tr'));
  };

  const status = caseFile.Status ?? caseFile.status ?? 1;

  /* Arabuluculuk bölümü (2026-10-07): bütün dosya türlerinde. Arabuluculuğun
     söz konusu olmadığı ceza, boşanma, idari dava ve icra dosyalarında yalnız
     bilgi girilmişse ya da dosya durumu "Arabuluculuk" ise görünür. Alanlar
     burada showDetay ile anılır; "Diğer Bilgiler" ikinci kez göstermesin. */
  const arabuluculukBilgiVar = [
    showDetay('ArabuluculukTarihi', caseFile.ArabuluculukTarihi ?? caseFile.arabuluculukTarihi),
    showDetay('SonTutanakTarihi', caseFile.SonTutanakTarihi ?? caseFile.sonTutanakTarihi),
    showDetay('ArabuluculukSonucu', caseFile.ArabuluculukSonucu ?? caseFile.arabuluculukSonucu),
    showDetay('ArabuluculukDosyaNo', caseFile.ArabuluculukDosyaNo ?? caseFile.arabuluculukDosyaNo),
    caseFile.ArabuluculukTutanakBelgeId ?? caseFile.arabuluculukTutanakBelgeId,
    caseFile.ArabuluculukAnlasmaBelgeId ?? caseFile.arabuluculukAnlasmaBelgeId,
  ].some(Boolean);
  const arabuluculukDisiTur = isCezaTuru || isBosanmaTuru || isIcraTuru || [
    DAVA_TURU.IdariDava, DAVA_TURU.IdariDavaVefat, DAVA_TURU.IdariDavaYaralanma, DAVA_TURU.Kamulastirma,
  ].includes(parseInt(davaTuru));
  const arabuluculukGoster = !isOzelFile && (arabuluculukBilgiVar || arabuluculukBelgeleri.length > 0
    || !arabuluculukDisiTur || parseInt(status) === CASE_FILE_STATUS.Arabuluculuk);
  const createdDate = caseFile?.CreatedDate ?? caseFile?.createdDate;
  const updatedDate = caseFile?.UpdatedDate ?? caseFile?.updatedDate;

  // Hesap Detayı sadece "baran" ve "beril" kullanıcıları tarafından görülebilir
  const hesapDetayiGorebilir = (() => {
    if (!user) return false;
    const izinliKullanicilar = ['baran', 'beril'];
    const normalize = (s) => String(s || '').toLocaleLowerCase('tr-TR').trim();
    const username = normalize(user.Username ?? user.UserName ?? user.username);
    const adSoyad = normalize(user.Name ?? user.name);
    return (
      izinliKullanicilar.includes(username) ||
      izinliKullanicilar.some((k) => adSoyad === k || adSoyad.startsWith(k + ' '))
    );
  })();

  if (!caseFile) {
    return (
      <div className="case-file-detail">
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="case-file-detail case-file-detail-full-width">
      <div className="detail-header" style={{ gridColumn: '1 / -1' }}>
        <div className="detail-header-left">
          {!embedded && (
            <button onClick={() => navigate(-1)} className="btn btn-secondary btn-sm">
              ← Geri Dön
            </button>
          )}
          <h1>Dosya Detayı</h1>
        </div>

        {/* Muvekkile WhatsApp'tan bilgi verme. Ayarlarda buro numarasi
            tanimli degilse bu dugme hic gorunmez. */}
        {!isOzelFile && <WhatsAppGonder dosya={caseFile} />}

        {/* Sayfanin en sag ucu, "Dosya Detayi" basligiyla ayni hizada. */}
        <button
          type="button"
          className="btn btn-secondary btn-sm muvekkil-goz-btn"
          title="Bu dosyanın müvekkil portalında nasıl göründüğünü gösterir"
          onClick={muvekkilGorunumunuAc}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
               strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span>Müvekkil Gözünden</span>
          {/* Kirmizi: giris yapti — Sari: belge goruntuledi */}
          {muvekkilOzet?.yeniGirisAdet > 0 && (
            <span
              className="muvekkil-goz-rozet kirmizi"
              title={`Son bakışınızdan beri müvekkil ${muvekkilOzet.yeniGirisAdet} kez giriş yaptı`}
            >
              {muvekkilOzet.yeniGirisAdet}
            </span>
          )}
          {muvekkilOzet?.yeniIncelemeAdet > 0 && (
            <span
              className="muvekkil-goz-rozet sari"
              title={`Son bakışınızdan beri ${muvekkilOzet.yeniIncelemeAdet} belge görüntülendi`}
            >
              {muvekkilOzet.yeniIncelemeAdet}
            </span>
          )}
        </button>
      </div>

      <div
        className="case-file-detail-content-wrapper"
        data-aktif-sekme={aktifSekme}
        ref={splitWrapperRef}
        style={{
          gridTemplateColumns: `minmax(0, ${detayPct}fr) 16px minmax(0, ${100 - detayPct}fr)`,
          gap: 0
        }}
      >
        <div className="case-file-detail-main-content">
          <div className="detail-card file-info-card">
            <div className="muvekkil-header">
              <div className="muvekkil-header-title">
                <h1>
                  {foyNo != null && foyNo !== 'N/A' ? `${foyNo} — ` : ''}
                  {muvekkil}
                </h1>
                {davaTuruEtiketi(caseFile) && davaTuruEtiketi(caseFile) !== 'Seçiniz' && (
                  <span className="muvekkil-header-dava">{davaTuruEtiketi(caseFile)}</span>
                )}
                {(caseFile.MuvekkilDavali ?? caseFile.muvekkilDavali) ? (
                  <span className="muvekkil-header-davali">Davalı</span>
                ) : null}
              </div>
              <div className="muvekkil-header-buttons">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setEditingJob(null);
                    setShowJobForm(true);
                  }}
                >
                  İş Ekle
                </button>
                {canEdit && (
                <button 
                  onClick={() => {
                    if (!user || !caseFile) return;
                    setShowEditForm(true);
                  }} 
                  className="btn btn-primary btn-sm"
                >
                  ✏️ Düzenle
                </button>
                )}
                {(user && (user.Role === 'Admin' || user.role === 'Admin')) && (
                  <button 
                    onClick={() => setShowTaskForm(true)} 
                    className="btn btn-primary btn-sm"
                  >
                    📋 Görev Ata
                  </button>
                )}
                {isOzelFile && canAccessOzelDosyalar(user) && (
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={async () => {
                      if (!caseFile) return;
                      const fileId = caseFile.Id ?? caseFile.id;
                      if (!await onayla('Bu özel dosya normal dosyalara taşınacak (belgeler ve işler dahil). Yeni föy numarası normal sıradan atanır. Devam edilsin mi?')) return;
                      try {
                        const updated = await caseFileAPI.acigaAl(fileId);
                        const newFoy = updated?.FoyNo ?? updated?.foyNo;
                        alert(newFoy != null ? `Dosya açığa alındı. Yeni föy no: ${newFoy}` : 'Dosya açığa alındı.');
                        navigate(`/case-files/${fileId}`);
                      } catch (err) {
                        alert('Açığa alma başarısız: ' + (err.response?.data?.message || err.message));
                      }
                    }}
                  >
                    Dosyayı Açığa Al
                  </button>
                )}
              </div>
            </div>
            {/* Yeni tasarim: kayan bolum secici (kisa adlar, tek satir). */}
            {YENI_TASARIM && (
              <KayanSecici
                className="cd-ks"
                etiket="Dosya bölümleri"
                secili={aktifSekme}
                onSec={(anahtar) => { setAktifSekme(anahtar); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                secenekler={[
                  { anahtar: 'detay', etiket: 'Detay' },
                  { anahtar: 'belgeler', etiket: 'Belgeler', adet: documents.length },
                  { anahtar: 'isler', etiket: 'İşler', adet: jobs.length + tasks.length },
                  { anahtar: 'notlar', etiket: 'Notlar', adet: notes.length },
                  { anahtar: 'masraflar', etiket: 'Masraf' },
                ]}
              />
            )}
            {/* Sekme cubugu — yalnizca dar ekranda gorunur (CSS ile). */}
            {!YENI_TASARIM && <nav className="cd-sekmeler" aria-label="Dosya bolumleri">
              {[
                ['detay', 'Dosya Detayı', null],
                ['belgeler', 'Belgeler', documents.length],
                ['isler', 'İş Listesi', jobs.length + tasks.length],
                ['notlar', 'Notlar', notes.length],
                ['masraflar', 'Masraflar', masraflar.length],
              ].map(([anahtar, etiket, adet]) => (
                <button
                  key={anahtar}
                  type="button"
                  className={'cd-sekme' + (aktifSekme === anahtar ? ' aktif' : '')}
                  aria-current={aktifSekme === anahtar ? 'page' : undefined}
                  onClick={() => {
                    setAktifSekme(anahtar);
                    // Sekme degisince icerigin basina don; yoksa uzun bir
                    // bolumden gelindiginde sayfa ortasinda acilmis gibi
                    // gorunuyor.
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  {etiket}
                  {adet > 0 && <span className="cd-sekme-adet">{adet}</span>}
                </button>
              ))}
            </nav>}

            <div className="cd-panel cd-panel-detay">
            <div className="detail-section">
              <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0 }}>Genel Bilgiler</h2>
                {(caseFile.FizikiDosyaMevcut ?? caseFile.fizikiDosyaMevcut) ? (
                  <span className="fiziki-dosya-badge">Fiziki Dosya Mevcut</span>
                ) : null}
              </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Foy No:</label>
              <span>{foyNo}</span>
            </div>
            {isBosanmaTuru ? (
            <DetailField label="Müvekkil Rolü:" show={!!((caseFile.MuvekkilDavaci ?? caseFile.muvekkilDavaci) || (caseFile.MuvekkilDavali ?? caseFile.muvekkilDavali))}>
              <span>
                {[
                  (caseFile.MuvekkilDavaci ?? caseFile.muvekkilDavaci) ? 'Davacı' : null,
                  (caseFile.MuvekkilDavali ?? caseFile.muvekkilDavali) ? 'Davalı' : null
                ].filter(Boolean).join(', ')}
              </span>
            </DetailField>
            ) : null}
            {isCezaTuru ? (
            <DetailField label="Müvekkil Rolü:" show={hasDetailValue(cezaMuvekkilRolu)}>
              <span>{cezaMuvekkilRolu}</span>
            </DetailField>
            ) : null}
            <div className="detail-item">
              <label>Müvekkil:</label>
              <span>{muvekkil}</span>
            </div>
            <DetailField label={isSirket ? 'Vergi No:' : 'TC:'} show={showDetay('MuvekkilTC', muvekkilTC)}>
              <span>{muvekkilTC}</span>
            </DetailField>
            <DetailField label="Sorumlu:" show={hasDetailValue(caseFile.SorumluAdi ?? caseFile.sorumluAdi)}>
              <span>{caseFile.SorumluAdi ?? caseFile.sorumluAdi}</span>
            </DetailField>
            <DetailField label="Müvekkil Telefon:" show={showDetay('MuvekkilTelefon', muvekkilTelefon)}>
              <span>{muvekkilTelefon}</span>
            </DetailField>
            <DetailField label="Doğum Tarihi:" show={showDetay('MuvekkilDogumTarihi', muvekkilDogumTarihi)}>
              <span>{formatDate(muvekkilDogumTarihi)}</span>
            </DetailField>
            <DetailField label="Müvekkil Adres:" show={showDetay('MuvekkilAdres', muvekkilAdres)}>
              <span>{muvekkilAdres}</span>
            </DetailField>
            <DetailField label="Müvekkil IBAN:" show={showDetay('MuvekkilIBAN', muvekkilIBAN)}>
              <span>{muvekkilIBAN}</span>
            </DetailField>
            {(() => {
              const linkedFiles = caseFile.LinkedCaseFiles || caseFile.linkedCaseFiles || [];
              return linkedFiles.length > 0 && (
                <div className="detail-item detail-item-full">
                  <label>Bağlantılı Dosyalar:</label>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '8px', 
                    marginTop: '8px' 
                  }}>
                    {linkedFiles.map((linkedFile) => {
                      const fileId = linkedFile.Id || linkedFile.id;
                      const foyNo = linkedFile.FoyNo || linkedFile.foyNo;
                      const muvekkil = linkedFile.Muvekkil || linkedFile.muvekkil;
                      const davaTuru = davaTuruEtiketi(linkedFile) || 'Seçiniz';
                      return (
                        <div
                          key={fileId}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#f0f0f0',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '13px',
                            transition: 'all 0.2s',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative'
                          }}
                        >
                          <span
                            onClick={() => navigate(`/case-files/${fileId}`)}
                            style={{
                              cursor: 'pointer',
                              flex: 1
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#0066cc';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'inherit';
                            }}
                          >
                            <strong>Foy No: {foyNo}</strong> - {muvekkil} - {davaTuru}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (await onayla(`${foyNo} numaralı dosya ile bağlantıyı kaldırmak istediğinize emin misiniz?`)) {
                                try {
                                  await caseFileAPI.unlink(caseFile.Id || caseFile.id, fileId);
                                  // Dosya detayını yeniden yükle
                                  const updatedCaseFile = await caseFileAPI.getById(id);
                                  setCaseFile(updatedCaseFile);
                                  alert('Bağlantı başarıyla kaldırıldı.');
                                } catch (error) {
                                  console.error('Bağlantı kaldırma hatası:', error);
                                  alert('Bağlantı kaldırılırken bir hata oluştu.');
                                }
                              }
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#dc3545',
                              cursor: 'pointer',
                              fontSize: '16px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#dc3545';
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#dc3545';
                            }}
                            title="Bağlantıyı kaldır"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Ortak mahkeme / duruşma — tüm dava türlerinde Genel Bilgiler'in hemen altında */}
        <div className="detail-section">
          <h2>Dava ve Mahkeme Bilgileri</h2>
          <div className="detail-grid">
            <DetailField label="Duruşma Tarihi ve Saati" show={showDetay('DurusmaTarihi', durusmaTarihi)} full className="bosanma-durusma-highlight">
              <span>{formatDateForDisplay(durusmaTarihi)}</span>
            </DetailField>
            {(showDetay('MahkemaBilgisi', mahkemaBilgisi) || showDetay('MahkemeEsas', esasGosterilen) || hasDetailValue(esasGosterilen)) && (
              <div className="mahkeme-esas-row">
                <DetailField label="Mahkeme Bilgisi:" show={showDetay('MahkemaBilgisi', mahkemaBilgisi) || hasDetailValue(esasGosterilen)} className="bosanma-durusma-highlight">
                  <span>{mahkemaBilgisi || '-'}</span>
                </DetailField>
                <DetailField label="Esas Numarası:" show={showDetay('MahkemeEsas', esasGosterilen) || hasDetailValue(esasGosterilen)} className="bosanma-durusma-highlight">
                  <span>{esasGosterilen || '-'}</span>
                </DetailField>
              </div>
            )}
            <div className="detail-item">
              <label>Dava Türü:</label>
              {canEdit && inlineEditingField === 'davaTuru' ? (
                <select
                  className="form-control"
                  autoFocus
                  disabled={inlineSaving}
                  defaultValue={String(davaTuru)}
                  onBlur={() => {
                    if (!inlineSaving) {
                      setTimeout(() => setInlineEditingField((f) => (f === 'davaTuru' ? null : f)), 150);
                    }
                  }}
                  onChange={(e) => handleInlineStatusDavaTuruUpdate('davaTuru', e.target.value)}
                  style={{ minWidth: '220px', maxWidth: '100%' }}
                >
                  {getSortedDavaTuruEntries().map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              ) : (
                <span
                  className={canEdit ? 'inline-editable-value' : undefined}
                  title={canEdit ? 'Değiştirmek için tıklayın' : undefined}
                  onClick={() => canEdit && setInlineEditingField('davaTuru')}
                  style={canEdit ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '3px' } : undefined}
                >
                  {davaTuruEtiketi(caseFile) || 'Seçiniz'}
                </span>
              )}
            </div>
            <div className="detail-item">
              <label>Durum:</label>
              {canEdit && inlineEditingField === 'status' ? (
                <select
                  className="form-control"
                  autoFocus
                  disabled={inlineSaving}
                  defaultValue={String(status)}
                  onBlur={() => {
                    if (!inlineSaving) {
                      setTimeout(() => setInlineEditingField((f) => (f === 'status' ? null : f)), 150);
                    }
                  }}
                  onChange={(e) => handleInlineStatusDavaTuruUpdate('status', e.target.value)}
                  style={{ minWidth: '220px', maxWidth: '100%' }}
                >
                  {Object.entries(CASE_FILE_STATUS_LABELS)
                    .sort((a, b) => {
                      if (a[0] === '0') return -1;
                      if (b[0] === '0') return 1;
                      return a[1].localeCompare(b[1], 'tr', { sensitivity: 'base' });
                    })
                    .map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                </select>
              ) : (
                <span
                  className={`status-badge status-${status}${canEdit ? ' inline-editable-value' : ''}`}
                  title={canEdit ? 'Değiştirmek için tıklayın' : undefined}
                  onClick={() => canEdit && setInlineEditingField('status')}
                  style={canEdit ? { cursor: 'pointer' } : undefined}
                >
                  {getStatusLabel(status, caseFile)}
                </span>
              )}
            </div>
            <DetailField label="Dava Açılış Tarihi:" show={showDetay('DavaAcilisTarihi', davaAcilisTarihi)}>
              <span>{formatDate(davaAcilisTarihi)}</span>
            </DetailField>
            <div className="detail-item">
              <label>Kesinleşme Tarihi:</label>
              {canEdit ? (
                <input
                  type="date"
                  className="form-control"
                  key={formatDateForDateInput(kesinlesmeTarihi) || 'kesinlesme-empty'}
                  defaultValue={formatDateForDateInput(kesinlesmeTarihi) || ''}
                  disabled={inlineSaving}
                  onChange={(e) => handleKesinlesmeTarihiSave(e.target.value)}
                  style={{ maxWidth: '220px' }}
                />
              ) : (
                <span>{kesinlesmeTarihi ? formatDate(kesinlesmeTarihi) : '-'}</span>
              )}
            </div>
            <DetailField label="Dosya Geliş Tarihi:" show={!!dosyaGelisTarihi} always>
              <span>{dosyaGelisTarihi ? formatDate(dosyaGelisTarihi) : '-'}</span>
            </DetailField>
            <DetailField label="Zaman Aşımı Tarihi:" show={showDetay('ZamanAsimiTarihi', zamanAsimiTarihi)}>
              <span>{formatDate(zamanAsimiTarihi)}</span>
            </DetailField>
            {isCezaTuru && (
              <DetailField label="Savcılık Soruşturma Numarası:" show={showDetay('SavcilikCezaDosyaNo', savcilikCezaDosyaNo)}>
                <span>{savcilikCezaDosyaNo}</span>
              </DetailField>
            )}
          </div>
        </div>

        {(hasBorcluBilgi || parseInt(davaTuru) === DAVA_TURU.IcraTaki || Object.prototype.hasOwnProperty.call(icraAyarDetay, 'ilamsiz')) && (
        <DetailSection title="İcra / Borçlu Bilgileri">
            {parseInt(davaTuru) === DAVA_TURU.IcraTaki && (
            <div className="detail-item">
              <label>İcra Konusu:</label>
              {canEdit && inlineEditingField === 'icraKonusu' ? (
                <select
                  className="form-control"
                  autoFocus
                  disabled={inlineSaving}
                  defaultValue={caseFile.IcraKonusu ?? caseFile.icraKonusu ?? ''}
                  onBlur={() => {
                    if (!inlineSaving) {
                      setTimeout(() => setInlineEditingField((f) => (f === 'icraKonusu' ? null : f)), 150);
                    }
                  }}
                  onChange={(e) => handleInlineStatusDavaTuruUpdate('icraKonusu', e.target.value)}
                  style={{ minWidth: '220px', maxWidth: '100%' }}
                >
                  <option value="">Seçiniz</option>
                  {ICRA_KONUSU_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <span
                  className={canEdit ? 'inline-editable-value' : undefined}
                  title={canEdit ? 'Değiştirmek için tıklayın' : undefined}
                  onClick={() => canEdit && setInlineEditingField('icraKonusu')}
                  style={canEdit ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '3px' } : undefined}
                >
                  {caseFile.IcraKonusu ?? caseFile.icraKonusu ?? 'Seçiniz'}
                </span>
              )}
            </div>
            )}
            {(hasBorcluBilgi || icraAyarDetay.ilamsiz === true || icraAyarDetay.ilamsiz === false) && (
              <DetailField label="Takip Türü:" show always>
                <span>{icraIlamsizDetay ? 'İlamsız' : 'İlamlı'}</span>
              </DetailField>
            )}
            {hasBorcluBilgi && (
              <>
                <DetailField label="Borçlu Sayısı:" show always>
                  <span>{icraAyarDetay.borcluSayisi || borclularDetay.length}</span>
                </DetailField>
                <div className="detail-item detail-item-full">
                  <label>Borçlu Bilgileri:</label>
                  <div className="borclu-detay-list">
                    {borclularDetay.map((b, i) => {
                      const isSirket = !!(b.isSirket || b.IsSirket);
                      const ad = b.adSoyad || b.AdSoyad || '-';
                      const tc = b.tc || b.TC || b.vergiNo || b.VergiNo || '-';
                      const adres = b.adres || b.Adres || '';
                      if (!String(ad).trim() && ad !== '-' && !String(tc).trim() && !String(adres).trim()) return null;
                      return (
                        <div className="ceza-taraf-block" key={`borclu-detay-${i}`} style={{ marginBottom: 10 }}>
                          <div><strong>Borçlu {i + 1}{isSirket ? ' (Şirket)' : ''}</strong></div>
                          <div>{isSirket ? 'Ünvan' : 'Ad Soyad'}: {ad}</div>
                          <div>{isSirket ? 'Vergi No' : 'TC'}: {tc}</div>
                          {adres ? <div>Adres: {adres}</div> : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
        </DetailSection>
        )}

        {isBosanmaTuru ? (
        <>
        <DetailSection title="Boşanma Dava Bilgileri">
            <DetailField label="Evlilik Tarihi:" show={!!(caseFile.EvlilikTarihi ?? caseFile.evlilikTarihi)}>
              <span>{formatDate(caseFile.EvlilikTarihi ?? caseFile.evlilikTarihi)}</span>
            </DetailField>
            <DetailField label="Boşanma Sebebi:" show={showDetay('BosanmaSebebi', caseFile.BosanmaSebebi ?? caseFile.bosanmaSebebi)}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{caseFile.BosanmaSebebi ?? caseFile.bosanmaSebebi}</span>
            </DetailField>
            <DetailField label="Velayet Talebi:" show={showDetay('VelayetTalebi', caseFile.VelayetTalebi ?? caseFile.velayetTalebi)}>
              <span>{(caseFile.VelayetTalebi ?? caseFile.velayetTalebi) ? 'Var' : 'Yok'}</span>
            </DetailField>
        </DetailSection>

        <DetailSection title="Eş Bilgileri">
            <DetailField label="Eşin Adı Soyadı:" show={showDetay('EsAdSoyad', caseFile.EsAdSoyad ?? caseFile.esAdSoyad)}>
              <span>{caseFile.EsAdSoyad ?? caseFile.esAdSoyad}</span>
            </DetailField>
            <DetailField label="Eş TC:" show={showDetay('EsTC', caseFile.EsTC ?? caseFile.esTC)}>
              <span>{caseFile.EsTC ?? caseFile.esTC}</span>
            </DetailField>
            <DetailField label="Çalışıyor:" show={showDetay('EsCalisiyor', caseFile.EsCalisiyor ?? caseFile.esCalisiyor)}>
              <span>{(caseFile.EsCalisiyor ?? caseFile.esCalisiyor) ? 'Evet' : 'Hayır'}</span>
            </DetailField>
        </DetailSection>

        <DetailSection title="Çocuk Bilgileri">
            <DetailField label="Çocuk Sayısı:" show={showDetay('CocukSayisi', caseFile.CocukSayisi ?? caseFile.cocukSayisi) || bosanmaCocuklar.length > 0}>
              <span>{caseFile.CocukSayisi ?? caseFile.cocukSayisi ?? bosanmaCocuklar.length}</span>
            </DetailField>
            {bosanmaCocuklar.length > 0 && (
              <div className="detail-item detail-item-full">
                <label>Çocuklar:</label>
                <span>
                  {bosanmaCocuklar.map((c, i) => {
                    const ad = c.adSoyad || c.AdSoyad || '-';
                    const dt = c.dogumTarihi || c.DogumTarihi;
                    return `${i + 1}. ${ad}${dt ? ` (${formatDate(dt)})` : ''}`;
                  }).join(' · ')}
                </span>
              </div>
            )}
        </DetailSection>

        <DetailSection title="Mali Talepler">
            <DetailField
              label="Seçilen Talepler:"
              full
              show={!![
                (caseFile.TalepNafaka ?? caseFile.talepNafaka),
                (caseFile.TalepTedbirNafaka ?? caseFile.talepTedbirNafaka),
                (caseFile.TalepIstirakNafaka ?? caseFile.talepIstirakNafaka),
                (caseFile.TalepYoksullukNafaka ?? caseFile.talepYoksullukNafaka),
                (caseFile.TalepMaddiTazminat ?? caseFile.talepMaddiTazminat),
                (caseFile.TalepManeviTazminat ?? caseFile.talepManeviTazminat),
              ].some(Boolean)}
            >
              <span>
                {[
                  (caseFile.TalepNafaka ?? caseFile.talepNafaka) && 'Nafaka Talebi',
                  (caseFile.TalepTedbirNafaka ?? caseFile.talepTedbirNafaka) && 'Tedbir Nafakası',
                  (caseFile.TalepIstirakNafaka ?? caseFile.talepIstirakNafaka) && 'İştirak Nafakası',
                  (caseFile.TalepYoksullukNafaka ?? caseFile.talepYoksullukNafaka) && 'Yoksulluk Nafakası',
                  (caseFile.TalepMaddiTazminat ?? caseFile.talepMaddiTazminat) && 'Maddi Tazminat',
                  (caseFile.TalepManeviTazminat ?? caseFile.talepManeviTazminat) && 'Manevi Tazminat',
                ].filter(Boolean).join(', ')}
              </span>
            </DetailField>
        </DetailSection>

        {showOdemeBolumu && (
        <DetailSection title="Ödeme Bilgileri">
            <DetailField label="Gelen Ödeme Durumu:" show={showDetay('GelenOdemeDurumu', gelenOdemeDurumu)}><span>{gelenOdemeDurumu}</span></DetailField>
            <DetailField label="Giden Ödeme Durumu:" show={showDetay('GidenOdemeDurumu', gidenOdemeDurumu)}><span>{gidenOdemeDurumu}</span></DetailField>
            <DetailField label="İbra Aşaması:" show={showDetay('IbraAsamasi', ibraAsamasi)}><span>{ibraAsamasi}</span></DetailField>
            <DetailField label="SMM:" show={showDetay('SMM', smm)}><span>{smm}</span></DetailField>
            <DetailField label="Karşı Vekil Bilgileri:" show={showDetay('KarsiTarafSigortaSirketiVekili', karsiTarafSigortaSirketiVekili)}><span>{karsiTarafSigortaSirketiVekili}</span></DetailField>
        </DetailSection>
        )}
        </>
        ) : isCezaTuru ? (
        <>
        <DetailSection title="Suç Bilgisi">
            <DetailField label="Suç Türü:" show={showDetay('SucTuru', caseFile.SucTuru ?? caseFile.sucTuru)}>
              <span>{caseFile.SucTuru ?? caseFile.sucTuru}</span>
            </DetailField>
            <DetailField label="Suç Tarihi:" show={showDetay('SucTarihi', caseFile.SucTarihi ?? caseFile.sucTarihi)}>
              <span>{formatDate(caseFile.SucTarihi ?? caseFile.sucTarihi)}</span>
            </DetailField>
            <DetailField label="Suç Yeri:" show={showDetay('SucYeri', caseFile.SucYeri ?? caseFile.sucYeri)}>
              <span>{caseFile.SucYeri ?? caseFile.sucYeri}</span>
            </DetailField>
            <DetailField label="TCK Maddesi:" show={showDetay('TckMaddesi', caseFile.TckMaddesi ?? caseFile.tckMaddesi)}>
              <span>{caseFile.TckMaddesi ?? caseFile.tckMaddesi}</span>
            </DetailField>
        </DetailSection>

        <DetailSection title="Taraflar">
            <DetailField label="Taraf Sayısı:" show={showDetay('TarafSayisi', caseFile.TarafSayisi ?? caseFile.tarafSayisi) || cezaTaraflar.length > 0}>
              <span>{caseFile.TarafSayisi ?? caseFile.tarafSayisi ?? cezaTaraflar.length}</span>
            </DetailField>
            {cezaTaraflar.length > 0 && (
              <div className="detail-item detail-item-full">
                <label>Taraf Listesi:</label>
                <span>
                  {cezaTaraflar.map((t, i) => {
                    const ad = t.adSoyad || t.AdSoyad || '-';
                    const tc = t.tc || t.TC || t.Tc || '';
                    const rol = t.rol || t.Rol || '-';
                    return `${i + 1}. ${ad}${tc ? ` (TC: ${tc})` : ''} — ${rol}`;
                  }).join(' · ')}
                </span>
              </div>
            )}
        </DetailSection>

        <DetailSection title="Tedbirler">
            <DetailField label="Seçilen Tedbirler:" show={hasDetailValue(cezaTedbirler)}>
              <span>{cezaTedbirler}</span>
            </DetailField>
            <DetailField label="Kovuşturmaya Yer Olmadığı Kararı:" show={!!(caseFile.KovusturmayaYerOlmadigiKarari ?? caseFile.kovusturmayaYerOlmadigiKarari)}>
              <span>Evet</span>
            </DetailField>
            <DetailField label="İddianame Tarihi:" show={showDetay('IddianameTarihi', caseFile.IddianameTarihi ?? caseFile.iddianameTarihi)}>
              <span>{formatDate(caseFile.IddianameTarihi ?? caseFile.iddianameTarihi)}</span>
            </DetailField>
        </DetailSection>

        {showOdemeBolumu && (
        <DetailSection title="Ödeme Bilgileri">
            <DetailField label="Gelen Ödeme Durumu:" show={showDetay('GelenOdemeDurumu', gelenOdemeDurumu)}><span>{gelenOdemeDurumu}</span></DetailField>
            <DetailField label="Giden Ödeme Durumu:" show={showDetay('GidenOdemeDurumu', gidenOdemeDurumu)}><span>{gidenOdemeDurumu}</span></DetailField>
            <DetailField label="İbra Aşaması:" show={showDetay('IbraAsamasi', ibraAsamasi)}><span>{ibraAsamasi}</span></DetailField>
            <DetailField label="SMM:" show={showDetay('SMM', smm)}><span>{smm}</span></DetailField>
            <DetailField label="Karşı Vekil Bilgileri:" show={showDetay('KarsiTarafSigortaSirketiVekili', karsiTarafSigortaSirketiVekili)}><span>{karsiTarafSigortaSirketiVekili}</span></DetailField>
        </DetailSection>
        )}
        </>
        ) : isIscilikTuru ? (
        <>
        <DetailSection title="İş Bilgileri">
            <DetailField label="İşveren / Firma:" show={showDetay('IsverenFirma', caseFile.IsverenFirma ?? caseFile.isverenFirma)}>
              <span>{caseFile.IsverenFirma ?? caseFile.isverenFirma}</span>
            </DetailField>
            <DetailField label="T.C / VKN:" show={showDetay('IsverenTcvkn', caseFile.IsverenTcvkn ?? caseFile.isverenTcvkn)}>
              <span>{caseFile.IsverenTcvkn ?? caseFile.isverenTcvkn}</span>
            </DetailField>
            <DetailField label="Yetkili:" show={showDetay('IsverenYetkilisi', caseFile.IsverenYetkilisi ?? caseFile.isverenYetkilisi)}>
              <span>{caseFile.IsverenYetkilisi ?? caseFile.isverenYetkilisi}</span>
            </DetailField>
            <DetailField label="Adres:" show={showDetay('IsverenAdresi', caseFile.IsverenAdresi ?? caseFile.isverenAdresi)}>
              <span>{caseFile.IsverenAdresi ?? caseFile.isverenAdresi}</span>
            </DetailField>
            <DetailField label="SGK Sicil No:" show={showDetay('SgkSicilNo', caseFile.SgkSicilNo ?? caseFile.sgkSicilNo)}>
              <span>{caseFile.SgkSicilNo ?? caseFile.sgkSicilNo}</span>
            </DetailField>
            <DetailField label="İşe Giriş Tarihi:" show={showDetay('IseGirisTarihi', caseFile.IseGirisTarihi ?? caseFile.iseGirisTarihi)}>
              <span>{formatDate(caseFile.IseGirisTarihi ?? caseFile.iseGirisTarihi)}</span>
            </DetailField>
            <DetailField label="İşten Çıkış Tarihi:" show={showDetay('IstenCikisTarihi', caseFile.IstenCikisTarihi ?? caseFile.istenCikisTarihi)}>
              <span>{formatDate(caseFile.IstenCikisTarihi ?? caseFile.istenCikisTarihi)}</span>
            </DetailField>
            <DetailField label="Çıkış Nedeni:" show={showDetay('CikisNedeni', caseFile.CikisNedeni ?? caseFile.cikisNedeni)}>
              <span>{caseFile.CikisNedeni ?? caseFile.cikisNedeni}</span>
            </DetailField>
            <DetailField label="Son Ücret:" show={showDetay('SonUcret', caseFile.SonUcret ?? caseFile.sonUcret)}>
              <span>{caseFile.SonUcret ?? caseFile.sonUcret}</span>
            </DetailField>
        </DetailSection>

        <DetailSection title="Talepler">
            <DetailField
              label="Seçilen Talepler:"
              full
              show={!![
                (caseFile.TalepKidem ?? caseFile.talepKidem),
                (caseFile.TalepIhbar ?? caseFile.talepIhbar),
                (caseFile.TalepFazlaMesai ?? caseFile.talepFazlaMesai),
                (caseFile.TalepUbgt ?? caseFile.talepUbgt),
                (caseFile.TalepHaftaTatili ?? caseFile.talepHaftaTatili),
                (caseFile.TalepYillikIzin ?? caseFile.talepYillikIzin),
                (caseFile.TalepAgi ?? caseFile.talepAgi),
                (caseFile.TalepMaasAlacagi ?? caseFile.talepMaasAlacagi),
              ].some(Boolean)}
            >
              <span>
                {[
                  (caseFile.TalepKidem ?? caseFile.talepKidem) && 'Kıdem',
                  (caseFile.TalepIhbar ?? caseFile.talepIhbar) && 'İhbar',
                  (caseFile.TalepFazlaMesai ?? caseFile.talepFazlaMesai) && 'Fazla Mesai',
                  (caseFile.TalepUbgt ?? caseFile.talepUbgt) && 'UBGT',
                  (caseFile.TalepHaftaTatili ?? caseFile.talepHaftaTatili) && 'Hafta Tatili',
                  (caseFile.TalepYillikIzin ?? caseFile.talepYillikIzin) && 'Yıllık İzin',
                  (caseFile.TalepAgi ?? caseFile.talepAgi) && 'AGİ',
                  (caseFile.TalepMaasAlacagi ?? caseFile.talepMaasAlacagi) && 'Maaş Alacağı',
                ].filter(Boolean).join(', ')}
              </span>
            </DetailField>
        </DetailSection>

        <DetailSection title="Süreç">
            {/* Arabuluculuk başvuru ve son tutanak tarihleri bütün dosya
                türlerinde ortak "Arabuluculuk" bölümünde (2026-10-07). */}
            <DetailField label="Dava Tarihi:" show={showDetay('IscilikDavaTarihi', caseFile.IscilikDavaTarihi ?? caseFile.iscilikDavaTarihi)}>
              <span>{formatDate(caseFile.IscilikDavaTarihi ?? caseFile.iscilikDavaTarihi)}</span>
            </DetailField>
            <DetailField label="Bilirkişi Tarihi:" show={showDetay('BilirkisiTarihi', caseFile.BilirkisiTarihi ?? caseFile.bilirkisiTarihi)}>
              <span>{formatDate(caseFile.BilirkisiTarihi ?? caseFile.bilirkisiTarihi)}</span>
            </DetailField>
            <DetailField label="Islah Tarihi:" show={showDetay('IslahTarihi', caseFile.IslahTarihi ?? caseFile.islahTarihi)}>
              <span>{formatDate(caseFile.IslahTarihi ?? caseFile.islahTarihi)}</span>
            </DetailField>
        </DetailSection>

        {showOdemeBolumu && (
        <DetailSection title="Ödeme Bilgileri">
            <DetailField label="Gelen Ödeme Durumu:" show={showDetay('GelenOdemeDurumu', gelenOdemeDurumu)}><span>{gelenOdemeDurumu}</span></DetailField>
            <DetailField label="Giden Ödeme Durumu:" show={showDetay('GidenOdemeDurumu', gidenOdemeDurumu)}><span>{gidenOdemeDurumu}</span></DetailField>
            <DetailField label="İbra Aşaması:" show={showDetay('IbraAsamasi', ibraAsamasi)}><span>{ibraAsamasi}</span></DetailField>
            <DetailField label="SMM:" show={showDetay('SMM', smm)}><span>{smm}</span></DetailField>
            <DetailField label="Karşı Vekil Bilgileri:" show={showDetay('KarsiTarafSigortaSirketiVekili', karsiTarafSigortaSirketiVekili)}><span>{karsiTarafSigortaSirketiVekili}</span></DetailField>
        </DetailSection>
        )}
        </>
        ) : (
        <>
        {isIsKazasiTuru && (
        <DetailSection title="Karşı Şirket Bilgileri">
            <DetailField label="İşveren / Şirket:" show={showDetay('IsverenFirma', caseFile.IsverenFirma ?? caseFile.isverenFirma)}>
              <span>{caseFile.IsverenFirma ?? caseFile.isverenFirma}</span>
            </DetailField>
            <DetailField label="T.C / VKN:" show={showDetay('IsverenTcvkn', caseFile.IsverenTcvkn ?? caseFile.isverenTcvkn)}>
              <span>{caseFile.IsverenTcvkn ?? caseFile.isverenTcvkn}</span>
            </DetailField>
            <DetailField label="Yetkili:" show={showDetay('IsverenYetkilisi', caseFile.IsverenYetkilisi ?? caseFile.isverenYetkilisi)}>
              <span>{caseFile.IsverenYetkilisi ?? caseFile.isverenYetkilisi}</span>
            </DetailField>
            <DetailField label="Adres:" show={showDetay('IsverenAdresi', caseFile.IsverenAdresi ?? caseFile.isverenAdresi)}>
              <span>{caseFile.IsverenAdresi ?? caseFile.isverenAdresi}</span>
            </DetailField>
            <DetailField label="SGK Sicil No:" show={showDetay('SgkSicilNo', caseFile.SgkSicilNo ?? caseFile.sgkSicilNo)}>
              <span>{caseFile.SgkSicilNo ?? caseFile.sgkSicilNo}</span>
            </DetailField>
            <DetailField label="İşe Giriş Tarihi:" show={showDetay('IseGirisTarihi', caseFile.IseGirisTarihi ?? caseFile.iseGirisTarihi)}>
              <span>{formatDateOnly(caseFile.IseGirisTarihi ?? caseFile.iseGirisTarihi)}</span>
            </DetailField>
        </DetailSection>
        )}
        <DetailSection title="Diğer Dava Bilgileri">
            <DetailField label="Sözleşme Oranı:" always className="sozlesme-oran-item">
              <span className="sozlesme-oran-detail">
                <span>{sozlesmeOrani != null && sozlesmeOrani !== '' ? `${sozlesmeOrani}%` : '-'}</span>
                <label className="sozlesme-kdv-check" title="Avukatlık ücretine %20 KDV eklenir">
                  <input
                    type="checkbox"
                    checked={sozlesmeOraniKdvDahil}
                    disabled={!canEdit || inlineSaving}
                    onChange={(e) => handleInlineStatusDavaTuruUpdate('sozlesmeOraniKdvDahil', e.target.checked)}
                  />
                  <span>KDV dahil</span>
                </label>
              </span>
            </DetailField>
            <DetailField label="Tahkim Başvuru Tarihi:" show={showDetay('TahkimBasvuruTarihi', tahkimBasvuruTarihi)}>
              <span>{formatDate(tahkimBasvuruTarihi)}</span>
            </DetailField>
            <DetailField label="Sigorta Başvuru Tarihi:" show={showDetay('SigortaBasvuruTarihi', sigortaBasvuruTarihi)}>
              <span>{formatDate(sigortaBasvuruTarihi)}</span>
            </DetailField>
            <DetailField label="Rapor Tarihi:" show={showDetay('RaporTarihi', raporTarihi)}>
              <span>{formatDate(raporTarihi)}</span>
            </DetailField>
        </DetailSection>

        <div className="detail-section">
          <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>Sigorta Bilgileri</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {canSigortaBasvuruHazirla && (
                <label className="sigorta-basvuru-check" title="Dosya bilgilerinden Word dilekçe oluşturur">
                  <input
                    type="checkbox"
                    checked={false}
                    disabled={generatingSigortaBasvuru}
                    onChange={(e) => {
                      e.target.checked = false;
                      handleSigortaBasvuruHazirla();
                    }}
                  />
                  <span>{generatingSigortaBasvuru ? 'Hazırlanıyor…' : 'Sigorta Başvurusu hazırla'}</span>
                </label>
              )}
              {canTahkimBasvuruHazirla && (
                <label className="sigorta-basvuru-check" title="Dosya bilgilerinden Tahkim başvuru dilekçesi oluşturur">
                  <input
                    type="checkbox"
                    checked={false}
                    disabled={generatingTahkimBasvuru}
                    onChange={(e) => {
                      e.target.checked = false;
                      handleTahkimBasvuruHazirla();
                    }}
                  />
                  <span>{generatingTahkimBasvuru ? 'Hazırlanıyor…' : 'Tahkim Başvurusu hazırla'}</span>
                </label>
              )}
            </div>
          </div>
          <div className="detail-grid">
            <DetailField label="Müvekkil Araç Sigorta Şirketi:" show={showDetay('MuvekkilAracSigortaSirketi', muvekkilAracSigortaSirketi)}>
              <span>{muvekkilAracSigortaSirketi}</span>
            </DetailField>
            <DetailField label="Karşı Taraf Sigorta Şirketi:" show={showDetay('KarsiTarafSigortaSirketi', karsiTarafSigortaSirketi)}>
              <span>{karsiTarafSigortaSirketi}</span>
            </DetailField>
            <DetailField label="Karşı Taraf 1:" show={showDetay('KarsiTaraf1', karsiTaraf1)}>
              <span>{karsiTaraf1}</span>
            </DetailField>
            <DetailField label="Karşı Taraf 2:" show={showDetay('KarsiTaraf2', karsiTaraf2)}>
              <span>{karsiTaraf2}</span>
            </DetailField>
            <DetailField label="Karşı Taraf Sigorta Şirketi Vekili:" show={showDetay('KarsiTarafSigortaSirketiVekili', karsiTarafSigortaSirketiVekili)}>
              <span>{karsiTarafSigortaSirketiVekili}</span>
            </DetailField>
          </div>
        </div>

        <DetailSection title="Plaka ve Kaza Bilgileri">
            <DetailField
              label="Müvekkil Plaka:"
              show={showDetay('MuvekkilPlaka', muvekkilPlaka) || (caseFile.MuvekkilAracRolu ?? caseFile.muvekkilAracRolu) === 'Yaya'}
            >
              {(caseFile.MuvekkilAracRolu ?? caseFile.muvekkilAracRolu) === 'Yolcu' && (
                <span style={{ display: 'block', fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>Yolcu</span>
              )}
              <span>
                {(caseFile.MuvekkilAracRolu ?? caseFile.muvekkilAracRolu) === 'Yaya'
                  ? 'Yaya'
                  : muvekkilPlaka}
              </span>
            </DetailField>
            <DetailField label="Karşı Taraf Plaka:" show={showDetay('KarsiTarafPlaka', karsiTarafPlaka)}>
              <span>{karsiTarafPlaka}</span>
            </DetailField>
            <DetailField label="Kaza Şehri:" show={showDetay('KazaSehri', kazaSehri)}>
              <span>{kazaSehri}</span>
            </DetailField>
            <DetailField label="Kaza Tarihi:" show={showDetay('KazaTarihi', kazaTarihi)}>
              <span>{formatDate(kazaTarihi)}</span>
            </DetailField>
        </DetailSection>

        <DetailSection title="Dosya Numaraları">
            <DetailField label="İcra Dosya No:" show={showDetay('IcraDosyaNo', icraDosyaNo)}>
              <span>{icraDosyaNo}</span>
            </DetailField>
            <DetailField label="Savcılık/Ceza Dosya No:" show={showDetay('SavcilikCezaDosyaNo', savcilikCezaDosyaNo)}>
              <span>{savcilikCezaDosyaNo}</span>
            </DetailField>
            <DetailField label="Tahkim Başvuru No:" show={showDetay('TahkimBasvuruNo', tahkimBasvuruNo)}>
              <span>{tahkimBasvuruNo}</span>
            </DetailField>
            <DetailField label="İtiraz Başvuru No:" show={showDetay('ItirazBasvuruNo', itirazBasvuruNo)}>
              <span>{itirazBasvuruNo}</span>
            </DetailField>
        </DetailSection>

        {showOdemeBolumu && (
        <DetailSection title="Süreç ve Ödeme Bilgileri">
            <DetailField label="Sulh Durumu:" show={showDetay('SulhDurumu', sulhDurumu)}><span>{sulhDurumu}</span></DetailField>
            <DetailField label="İbra Aşaması:" show={showDetay('IbraAsamasi', ibraAsamasi)}><span>{ibraAsamasi}</span></DetailField>
            <DetailField label="Gelen Ödeme Durumu:" show={showDetay('GelenOdemeDurumu', gelenOdemeDurumu)}><span>{gelenOdemeDurumu}</span></DetailField>
            <DetailField label="Giden Ödeme Durumu:" show={showDetay('GidenOdemeDurumu', gidenOdemeDurumu)}><span>{gidenOdemeDurumu}</span></DetailField>
            <DetailField label="SMM:" show={showDetay('SMM', smm)}><span>{smm}</span></DetailField>
        </DetailSection>
        )}
        </>
        )}

          {arabuluculukGoster && (
            <ArabuluculukBolumu
              caseFile={caseFile}
              caseFileId={parseInt(id, 10)}
              canEdit={canEdit}
              documents={documents}
              onPreview={handlePreview}
              onYenile={loadCaseFile}
              sonBelgeYukleme={sonBelgeYukleme}
              otomatikBelgeler={arabuluculukBelgeleri}
            />
          )}

          {/* Vekalet Bölümü — Son İşlem'den önce */}
          <div className="detail-section">
            <h2>Vekalet Bilgileri</h2>
            {loadingVekalet ? (
              <div className="loading">Yükleniyor...</div>
            ) : (
              <div className="detail-grid">
                {vekalet ? (
                  <>
                    <DetailField label="Vekalet Türü:" show={hasDetailValue(vekalet.VekaletTuru || vekalet.vekaletTuru)} always>
                      <span>{vekalet.VekaletTuru || vekalet.vekaletTuru}</span>
                    </DetailField>
                    {(vekalet.VekaletTuru === 'Süreli' || vekalet.vekaletTuru === 'Süreli') && (
                      <DetailField label="Vekalet Bitiş Tarihi:" show={!!(vekalet.VekaletBitisTarihi || vekalet.vekaletBitisTarihi)}>
                        <span>{formatDate(vekalet.VekaletBitisTarihi || vekalet.vekaletBitisTarihi)}</span>
                      </DetailField>
                    )}
                    <DetailField label="Vekaletin Aslı Var:" show={!!(vekalet.VekaletinAsliVar || vekalet.vekaletinAsliVar)}>
                      <span>Evet</span>
                    </DetailField>
                  </>
                ) : null}
                <DetailField label="Referans:" show={hasDetailValue(referans)}>
                  <span>{referans}</span>
                </DetailField>
              </div>
            )}
          </div>

        <div className="detail-section">
          <h2>Son İşlem ve Sistem Bilgileri</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Son İşlem Yapan:</label>
              <span>{sonIslemYapanAdi || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Oluşturma Tarihi:</label>
              <span>{createdDate ? formatDateTime(createdDate) : '-'}</span>
            </div>
            <div className="detail-item">
              <label>Güncelleme Tarihi:</label>
              <span>{updatedDate ? formatDateTime(updatedDate) : '-'}</span>
            </div>
          </div>
        </div>

        {/* ---- Diğer Bilgiler ----
             Dolu olduğu hâlde yukarıdaki bölümlerin hiçbirinde
             gösterilmeyen alanlar. Dava türüne göre dallanan görünümde
             bir alan unutulsa bile girilen bilgi burada görünür. */}
        {(() => {
          const gizliler = gizliKalanAlanlar();
          if (gizliler.length === 0) return null;
          return (
            <DetailSection title="Diğer Bilgiler">
              <div className="detail-grid">
                {gizliler.map(({ anahtar, etiket, deger }) => (
                  <DetailField key={anahtar} label={etiket + ':'} always>
                    <span>{deger}</span>
                  </DetailField>
                ))}
              </div>
            </DetailSection>
          );
        })()}
            </div>{/* cd-panel-detay */}
          </div>

          {/* Notlar Bölümü - Admin ve Dosya Sorumluları */}
          {canEdit && (
            <div className="detail-card detail-card-notes cd-panel cd-panel-notlar">
              <div className="detail-section">
                <div className="section-header">
                  <h2>Notlar</h2>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setEditingNote(null);
                      setShowNoteForm(true);
                    }}
                  >
                    + Yeni Not Ekle
                  </button>
                </div>
                {loadingNotes ? (
                  <p>Yükleniyor...</p>
                ) : notes.length === 0 ? (
                  <p style={{ color: '#666', fontStyle: 'italic' }}>Henüz not eklenmemiş.</p>
                ) : (
                  <div className="notes-table-container" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '400px' }}>
                    <table className="notes-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                      <thead>
                        <tr>
                          <th>Başlık</th>
                          <th>İçerik</th>
                          <th>Oluşturan</th>
                          <th>Tarih</th>
                          <th style={{ textAlign: 'center' }}>İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notes.map((note) => {
                          const noteId = note.Id ?? note.id;
                          const title = note.Title ?? note.title ?? '';
                          const content = note.Content ?? note.content ?? '';
                          const createdDate = note.CreatedDate ?? note.createdDate;
                          const userName = `${note.UserName ?? note.userName ?? ''} ${note.UserSurname ?? note.userSurname ?? ''}`.trim();
                          return (
                            <tr 
                              key={noteId}
                              style={{ 
                                borderBottom: '1px solid #e0e0e0',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = 'transparent'}
                            >
                              <td style={{ padding: '10px 12px', fontSize: '12px', color: '#333', fontWeight: '600' }}>
                                {title || '-'}
                              </td>
                              <td style={{ padding: '10px 12px', fontSize: '12px', color: '#333', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {content.substring(0, 80)}{content.length > 80 ? '...' : ''}
                              </td>
                              <td style={{ padding: '10px 12px', fontSize: '12px', color: '#666' }}>
                                {userName || 'Bilinmiyor'}
                              </td>
                              <td style={{ padding: '10px 12px', fontSize: '12px', color: '#666' }}>
                                {createdDate ? formatDateTime(createdDate) : '-'}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => {
                                      setEditingNote(note);
                                      setShowNoteForm(true);
                                    }}
                                    style={{ padding: '4px 8px', fontSize: '11px' }}
                                  >
                                    Düzenle
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={async () => {
                                      if (await onayla('Bu notu silmek istediğinizden emin misiniz?')) {
                                        try {
                                          await noteAPI.delete(noteId);
                                          await loadNotes();
                                        } catch (error) {
                                          alert('Not silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
                                        }
                                      }
                                    }}
                                    style={{ padding: '4px 8px', fontSize: '11px' }}
                                  >
                                    Sil
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Yapılacak İşler Bölümü */}
          <div className="detail-card detail-card-jobs cd-panel cd-panel-isler">
            <div className="detail-section">
              <div className="section-header">
                <h2>Yapılacak İşler</h2>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setEditingJob(null);
                    setShowJobForm(true);
                  }}
                >
                  + Yeni İş Ekle
                </button>
              </div>
              {loadingJobs ? (
                <p>Yükleniyor...</p>
              ) : jobs.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>Henüz iş eklenmemiş.</p>
              ) : (
                <div className="jobs-table-container" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '400px' }}>
                  <table className="jobs-table detail-jobs-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                    <thead>
                      <tr>
                        <th>Açıklama</th>
                        <th>Oluşturan</th>
                        <th>Oluşturulma</th>
                        <th>Son Tarih</th>
                        <th>Durum</th>
                        <th style={{ minWidth: '180px' }}>İşlemler</th>
                        <th>Tamamlayan / Tamamlanma</th>
                      </tr>
                    </thead>
                    <tbody>
                  {jobs.map((job) => {
                    const jobId = job.Id ?? job.id;
                    const description = job.Description ?? job.description ?? '';
                    const dueDate = job.DueDate ?? job.dueDate;
                    const isCompleted = job.IsCompleted ?? job.isCompleted ?? false;
                    const completedBy = `${job.CompletedByUserName ?? job.completedByUserName ?? ''} ${job.CompletedByUserSurname ?? job.completedByUserSurname ?? ''}`.trim();
                    const completedDate = job.CompletedDate ?? job.completedDate;
                    const createdBy = `${job.CreatedByUserName ?? job.createdByUserName ?? ''} ${job.CreatedByUserSurname ?? job.createdByUserSurname ?? ''}`.trim();
                    const createdDate = job.CreatedDate ?? job.createdDate;
                    
                    // isOverdue kontrolü: UTC+3 (Istanbul) timezone'a göre
                    let isOverdue = false;
                    if (dueDate && !isCompleted) {
                      try {
                        let dueDateObj;
                        if (typeof dueDate === 'string') {
                          if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{3})?$/.test(dueDate)) {
                            const parts = dueDate.split('T');
                            const datePart = parts[0].split('-');
                            const timePart = (parts[1] || '00:00:00').split(':');
                            const year = parseInt(datePart[0], 10);
                            const month = parseInt(datePart[1], 10) - 1;
                            const day = parseInt(datePart[2], 10);
                            const hours = parseInt(timePart[0] || 0, 10);
                            const minutes = parseInt(timePart[1] || 0, 10);
                            const seconds = parseInt(timePart[2] || 0, 10);
                            dueDateObj = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
                          } else {
                            dueDateObj = new Date(dueDate);
                          }
                        } else {
                          dueDateObj = new Date(dueDate);
                        }
                        const dueDateIstanbul = new Date(dueDateObj.getTime() + (3 * 60 * 60 * 1000));
                        const now = new Date();
                        const nowIstanbul = new Date(now.getTime() + (3 * 60 * 60 * 1000));
                        const dueDateOnly = new Date(dueDateIstanbul.getUTCFullYear(), dueDateIstanbul.getUTCMonth(), dueDateIstanbul.getUTCDate());
                        const nowDateOnly = new Date(nowIstanbul.getUTCFullYear(), nowIstanbul.getUTCMonth(), nowIstanbul.getUTCDate());
                        isOverdue = dueDateOnly < nowDateOnly;
                      } catch (e) {
                        isOverdue = false;
                      }
                    }
                    
                    const isAdmin = () => {
                      if (!user) return false;
                      const role = user.Role ?? user.role ?? '';
                      return role === 'Admin';
                    };
                    const openCompleteModal = () => {
                      setCompleteModalJob(job);
                      setCompleteComment('');
                    };
                    const handleEdit = async () => {
                      const currentUserId = user?.Id ?? user?.id;
                      const createdByUserId = job.CreatedByUserId ?? job.createdByUserId;
                      if (!isAdmin() && createdByUserId !== currentUserId) {
                        alert('Bu işi sadece oluşturan kullanıcı veya admin düzenleyebilir.');
                        return;
                      }
                      setEditingJob(job);
                      setShowJobForm(true);
                    };
                    const handleDelete = async () => {
                      const currentUserId = user?.Id ?? user?.id;
                      const createdByUserId = job.CreatedByUserId ?? job.createdByUserId;
                      if (!isAdmin() && createdByUserId !== currentUserId) {
                        alert('Bu işi sadece oluşturan kullanıcı veya admin silebilir.');
                        return;
                      }
                      if (!await onayla('Bu işi silmek istediğinize emin misiniz?')) return;
                      try {
                        await jobAPI.delete(jobId);
                        await loadJobs();
                        window.dispatchEvent(new CustomEvent('jobUpdated', { detail: { caseFileId: id } }));
                        alert('İş başarıyla silindi!');
                      } catch (error) {
                        alert('İş silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
                      }
                    };
                    
                    const currentUserId = user?.Id ?? user?.id;
                    const createdByUserId = job.CreatedByUserId ?? job.createdByUserId;
                    const canEditDelete = isAdmin() || createdByUserId === currentUserId;
                    
                    const completedInfo = isCompleted
                      ? [completedBy && `İşi yapan: ${completedBy}`, completedDate && formatDateTime(completedDate), (job.CompletedComment ?? job.completedComment) && `Yorum: ${job.CompletedComment ?? job.completedComment}`].filter(Boolean).join(' · ')
                      : '—';
                    
                    return (
                      <tr
                        key={jobId}
                        className={`detail-jobs-table-row ${isCompleted ? 'job-row-completed' : ''} ${isOverdue ? 'job-row-overdue' : ''}`}
                        style={{ borderBottom: '1px solid #eee' }}
                      >
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: '#333' }}>
                          <strong>{description}</strong>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: '#555' }}>{createdBy || 'Bilinmiyor'}</td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: '#555' }}>{formatDateTime(createdDate)}</td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: isOverdue ? '#dc3545' : '#555' }}>
                          {dueDate ? formatJobDueDateForDisplay(dueDate) : '—'}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                          {isCompleted && <span className="job-status completed-badge" style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: '#d4edda', color: '#155724', fontSize: '11px' }}>Tamamlandı</span>}
                          {isOverdue && !isCompleted && <span className="job-status overdue-badge" style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: '#f8d7da', color: '#721c24', fontSize: '11px' }}>Gecikmiş</span>}
                          {!isCompleted && !isOverdue && <span style={{ color: '#666', fontSize: '11px' }}>Beklemede</span>}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                          <div className="detail-jobs-table-actions" style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {canEditDelete && (
                              <>
                                <ActionIcon type="edit" title="Düzenle" onClick={(e) => { e.stopPropagation(); handleEdit(); }} />
                                <ActionIcon type="delete" title="Sil" onClick={(e) => { e.stopPropagation(); handleDelete(); }} />
                              </>
                            )}
                            {!isCompleted && (
                              <ActionIcon type="complete" title="Kontrole Gönder" onClick={(e) => { e.stopPropagation(); openCompleteModal(); }} />
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: '#555', maxWidth: '200px' }}>{completedInfo || '—'}</td>
                      </tr>
                    );
                  })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Görevler Bölümü */}
          <div className="detail-card detail-card-tasks cd-panel cd-panel-isler">
            <div className="detail-section">
              <div className="section-header">
                <h2>Görevler</h2>
              </div>
              {loadingTasks ? (
                <p>Yükleniyor...</p>
              ) : tasks.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>Bu dosyaya ait görev bulunmamaktadır.</p>
              ) : (
                <div className="tasks-table-container" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '400px' }}>
                  <table className="tasks-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                    <thead>
                      <tr>
                        <th>Açıklama</th>
                        <th>Atayan</th>
                        <th>Atanan</th>
                        <th>Son Tarih</th>
                        <th>Durum</th>
                        <th style={{ minWidth: '160px' }}>İşlemler</th>
                        <th>Yanıt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => {
                        const taskId = task.Id ?? task.id;
                        const description = task.Description ?? task.description ?? '';
                        const assignedByName = task.AssignedByUserName ?? task.assignedByUserName ?? '-';
                        const assignedToName = task.AssignedToUserName ?? task.assignedToUserName ?? '-';
                        const dueDate = task.DueDate ?? task.dueDate;
                        const status = parseInt(task.Status ?? task.status ?? 1);
                        const response = task.Response ?? task.response ?? '';
                        const rejectionReason = task.RejectionReason ?? task.rejectionReason ?? '';
                        
                        // Status labels - Backend'de TaskStatus enum: 1=InProgress, 2=UnderReview, 3=Completed, 4=Rejected
                        // TASK_STATUS_LABELS kullan (Tasks sayfası ile aynı)
                        const statusLabel = TASK_STATUS_LABELS[status] || 'Bilinmiyor';
                        const statusColors = {
                          1: '#17a2b8',  // İşlemde
                          2: '#fd7e14', // Kontrol Ediliyor
                          3: '#28a745', // Tamamlandı
                          4: '#dc3545'  // Reddedildi
                        };
                        const statusColor = statusColors[status] || '#6c757d';
                        const statusInfo = { label: statusLabel, color: statusColor };
                        
                        return (
                          <tr 
                            key={taskId}
                            style={{ 
                              borderBottom: '1px solid #e0e0e0',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = 'transparent'}
                          >
                            <td className="task-description-cell" style={{ padding: '10px 12px', fontSize: '12px', color: '#333' }}>
                              <span className="task-description-text" title={description || undefined}>
                                {description}
                              </span>
                              {rejectionReason && (
                                <div style={{ marginTop: '5px', padding: '5px', backgroundColor: '#ffebee', borderRadius: '4px', fontSize: '10px', color: '#c62828' }}>
                                  <strong>Red Nedeni:</strong> {rejectionReason}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#666' }}>
                              {assignedByName}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#666' }}>
                              {assignedToName}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#666' }}>
                              {dueDate ? formatDateForDisplay(dueDate) : '-'}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                              <span style={{ 
                                padding: '4px 8px', 
                                borderRadius: '4px', 
                                backgroundColor: statusInfo.color, 
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>
                                {statusInfo.label}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                              <div className="detail-tasks-table-actions" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {!isAdminForTask() && status === TASK_STATUS.InProgress && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => setEditingTask(task)}
                                    title="Kontrole Gönder"
                                  >
                                    ✓ Kontrole Gönder
                                  </button>
                                )}
                                {isAdminForTask() && status === TASK_STATUS.InProgress && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    onClick={() => { setEditingTaskMode('complete'); setEditingTask(task); }}
                                    title="Tamamlandı Yap"
                                  >
                                    ✓
                                  </button>
                                )}
                                {isAdminForTask() && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => { setEditingTaskMode('edit'); setEditingTask(task); }}
                                      title="Düzenle"
                                    >
                                      Düzenle
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleDeleteTask(task)}
                                      title="Sil"
                                    >
                                      Sil
                                    </button>
                                  </>
                                )}
                                {isAdminForTask() && status === TASK_STATUS.UnderReview && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-success"
                                      onClick={() => handleApproveTask(task)}
                                      title="Onayla"
                                    >
                                      ✓ Onayla
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => setRejectingTask(task)}
                                      title="Reddet"
                                    >
                                      ✗ Reddet
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#666', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {response || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Masraflar Bölümü - Sadece Admin (özel dosyalarda yok) */}
      {user && (user.Role === 'Admin' || user.role === 'Admin') && !isOzelFile && (
        <div className="detail-card detail-card-masraflar cd-panel cd-panel-masraflar">
          <div className="detail-section">
          <div className="section-header masraf-tabs-header">
            <div className="masraf-tabs">
              <button
                className={`masraf-tab ${masrafTab === 'masraflar' ? 'active' : ''}`}
                onClick={() => setMasrafTab('masraflar')}
              >
                <Icon name="wallet" size={17} /> Masraflar
              </button>
              {hesapDetayiGorebilir && (
                <button
                  className={`masraf-tab ${masrafTab === 'hesap' ? 'active' : ''}`}
                  onClick={() => setMasrafTab('hesap')}
                >
                  🧮 Hesap Detayı
                </button>
              )}
              <button
                className={`masraf-tab ${masrafTab === 'icra' ? 'active' : ''}`}
                onClick={() => setMasrafTab('icra')}
              >
                ⚖️ İcra Hesabı
              </button>
            </div>
            {masrafTab === 'masraflar' && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setEditingMasraf(null);
                  setShowMasrafForm(true);
                }}
              >
                + Yeni Masraf Ekle
              </button>
            )}
          </div>

        {masrafTab === 'icra' ? (
          <IcraHesap
            caseFile={caseFile}
            onUpdated={(updated) => {
              if (updated) setCaseFile(updated);
              loadMasraflar();
            }}
          />
        ) : hesapDetayiGorebilir && masrafTab === 'hesap' ? (
          <HesapDetayi
            caseFileId={id}
            caseFile={caseFile}
            masraflar={masraflar}
            loadingMasraflar={loadingMasraflar}
            onUpdated={(updated) => {
              if (updated) setCaseFile(updated);
            }}
            onMasraflarChanged={loadMasraflar}
          />
        ) : loadingMasraflar ? (
          <p>Yükleniyor...</p>
        ) : (() => {
          // Sadece yeni yapı masrafları (MasrafTuru olanlar)
          const yeniMasraflar = masraflar.filter(m => m.MasrafTuru || m.masrafTuru);
          const isBerileVerilen = (m) => (m.IsBerileVerilen || m.isBerileVerilen) || ['BERİLE VERİLEN', 'Berile Verilen'].includes(String(m.MasrafTuru || m.masrafTuru || '').trim());
          const berileVerilenList = yeniMasraflar.filter(m => isBerileVerilen(m));
          const gelirler = yeniMasraflar.filter(m => m.IsGelir || m.isGelir);
          const giderler = yeniMasraflar.filter(m => !(m.IsGelir || m.isGelir) && !isBerileVerilen(m));
          
          const formatCurrency = (amount) => {
            if (amount === null || amount === undefined) return '-';
            return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
          };
          
          const formatDate = (dateString) => {
            if (!dateString) return '-';
            // UTC+3 (Istanbul) timezone için sadece tarih göster (saat yok)
            return formatDateOnly(dateString);
          };
          
          if (yeniMasraflar.length === 0) {
            return <p style={{ color: '#666', fontStyle: 'italic' }}>Henüz masraf kaydı bulunmamaktadır.</p>;
          }
          
          return (
            <>
              {/* Gelirler */}
              {gelirler.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ marginBottom: '12px', color: '#2c3e50' }}><Icon name="income" size={16} /> Gelirler</h3>
                  <div className="masraflar-table-container">
                    <table className="masraflar-table">
                      <thead>
                        <tr>
                          <th>Masraf Türü</th>
                          <th>Tutar</th>
                          <th>Tarih</th>
                          <th>Açıklama</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gelirler.map((masraf) => (
                          <tr key={masraf.id || masraf.Id}>
                            <td>{masraf.MasrafTuru || masraf.masrafTuru || '-'}</td>
                            <td>{formatCurrency(masraf.Tutar || masraf.tutar)}</td>
                            <td>{formatDate(masraf.SonTarih || masraf.sonTarih || masraf.Tarih || masraf.tarih)}</td>
                            <td className="masraf-aciklama-cell">{masraf.Aciklama || masraf.aciklama || '-'}</td>
                            <td>
                              <div className="action-buttons">
                                {hesapDetayiGorebilir && (
                                  <button
                                    className="btn btn-sm btn-warning"
                                    onClick={() => {
                                      setEditingMasraf(masraf);
                                      setEditingMasrafRequest(null);
                                      setShowMasrafForm(true);
                                    }}
                                  >
                                    Düzenle
                                  </button>
                                )}
                                {user && (user.Role === 'Admin' || user.role === 'Admin') && (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={async () => {
                                      if (await onayla('Bu masrafı silmek istediğinize emin misiniz?')) {
                                        try {
                                          await masrafAPI.delete(masraf.id || masraf.Id);
                                          await loadMasraflar();
                                        } catch (error) {
                                          alert('Masraf silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
                                        }
                                      }
                                    }}
                                  >
                                    Sil
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Giderler */}
              {giderler.length > 0 && (
                <div>
                  <h3 style={{ marginBottom: '12px', color: '#2c3e50' }}><Icon name="expense" size={16} /> Giderler</h3>
                  <div className="masraflar-table-container">
                    <table className="masraflar-table">
                      <thead>
                        <tr>
                          <th>Masraf Türü</th>
                          <th>Tutar</th>
                          <th>Tarih</th>
                          <th>Açıklama</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {giderler.map((masraf) => (
                          <tr key={masraf.id || masraf.Id}>
                            <td>{masraf.MasrafTuru || masraf.masrafTuru || '-'}</td>
                            <td>{formatCurrency(masraf.Tutar || masraf.tutar)}</td>
                            <td>{formatDate(masraf.SonTarih || masraf.sonTarih || masraf.Tarih || masraf.tarih)}</td>
                            <td className="masraf-aciklama-cell">{masraf.Aciklama || masraf.aciklama || '-'}</td>
                            <td>
                              <div className="action-buttons">
                                {hesapDetayiGorebilir && (
                                  <button
                                    className="btn btn-sm btn-warning"
                                    onClick={() => {
                                      setEditingMasraf(masraf);
                                      setEditingMasrafRequest(null);
                                      setShowMasrafForm(true);
                                    }}
                                  >
                                    Düzenle
                                  </button>
                                )}
                                {user && (user.Role === 'Admin' || user.role === 'Admin') && (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={async () => {
                                      if (await onayla('Bu masrafı silmek istediğinize emin misiniz?')) {
                                        try {
                                          await masrafAPI.delete(masraf.id || masraf.Id);
                                          await loadMasraflar();
                                        } catch (error) {
                                          alert('Masraf silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
                                        }
                                      }
                                    }}
                                  >
                                    Sil
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Referansa Verilen Avanslar */}
              {(() => {
                const referansAvanslari = yeniMasraflar.filter(m => {
                  const masrafTuru = (m.MasrafTuru || m.masrafTuru || '').toLowerCase();
                  return masrafTuru === 'referansa verilen avans';
                });
                
                if (referansAvanslari.length === 0) return null;
                
                return (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '12px', color: '#2c3e50' }}><Icon name="expense" size={16} /> Referansa Verilen Avanslar</h3>
                    <div className="masraflar-table-container">
                      <table className="masraflar-table">
                        <thead>
                          <tr>
                            <th>Referans</th>
                            <th>Tutar</th>
                            <th>Tarih</th>
                            <th>Açıklama</th>
                            <th>İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {referansAvanslari.map((masraf) => (
                            <tr key={masraf.id || masraf.Id}>
                              <td>{masraf.ReferansName || masraf.referansName || '-'}</td>
                              <td>{formatCurrency(masraf.Tutar || masraf.tutar)}</td>
                              <td>{formatDate(masraf.SonTarih || masraf.sonTarih || masraf.Tarih || masraf.tarih)}</td>
                              <td className="masraf-aciklama-cell">{masraf.Aciklama || masraf.aciklama || '-'}</td>
                              <td>
                                <div className="action-buttons">
                                  {hesapDetayiGorebilir && (
                                    <button
                                      className="btn btn-sm btn-warning"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingMasraf(masraf);
                                        setEditingMasrafRequest(null);
                                        setShowMasrafForm(true);
                                      }}
                                    >
                                      Düzenle
                                    </button>
                                  )}
                                  {user && (user.Role === 'Admin' || user.role === 'Admin') && (
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (await onayla('Bu avansı silmek istediğinize emin misiniz?')) {
                                          try {
                                            await masrafAPI.delete(masraf.id || masraf.Id);
                                            await loadMasraflar();
                                          } catch (error) {
                                            alert('Avans silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
                                          }
                                        }
                                      }}
                                    >
                                      Sil
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Berile Verilen */}
              {(() => {
                if (berileVerilenList.length === 0) return null;
                const berileVerilenToplam = berileVerilenList.reduce((sum, m) => sum + (parseFloat(m.Tutar || m.tutar) || 0), 0);
                return (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '12px', color: '#2c3e50' }}><Icon name="expense" size={16} /> Berile Verilen</h3>
                    <div className="masraflar-table-container">
                      <table className="masraflar-table">
                        <thead>
                          <tr>
                            <th>Masraf Türü</th>
                            <th>Tutar</th>
                            <th>Tarih</th>
                            <th>Açıklama</th>
                            <th>İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {berileVerilenList.map((masraf) => (
                            <tr key={masraf.id || masraf.Id}>
                              <td>{masraf.MasrafTuru || masraf.masrafTuru || '-'}</td>
                              <td>{formatCurrency(masraf.Tutar || masraf.tutar)}</td>
                              <td>{formatDate(masraf.SonTarih || masraf.sonTarih || masraf.Tarih || masraf.tarih)}</td>
                              <td className="masraf-aciklama-cell">{masraf.Aciklama || masraf.aciklama || '-'}</td>
                              <td>
                                <div className="action-buttons">
                                  {hesapDetayiGorebilir && (
                                    <button
                                      className="btn btn-sm btn-warning"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingMasraf(masraf);
                                        setEditingMasrafRequest(null);
                                        setShowMasrafForm(true);
                                      }}
                                    >
                                      Düzenle
                                    </button>
                                  )}
                                  {user && (user.Role === 'Admin' || user.role === 'Admin') && (
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (await onayla('Bu masrafı silmek istediğinize emin misiniz?')) {
                                          try {
                                            await masrafAPI.delete(masraf.id || masraf.Id);
                                            await loadMasraflar();
                                          } catch (error) {
                                            alert('Masraf silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
                                          }
                                        }
                                      }}
                                    >
                                      Sil
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p style={{ marginTop: '8px', fontWeight: 600, color: '#2c3e50' }}>Berile verilen toplam: {formatCurrency(berileVerilenToplam)}</p>
                  </div>
                );
              })()}

              {/* Dosya Geliri Kartı */}
              {!loadingMasraflar && masraflar.length > 0 && (() => {
                const isBerileVerilenCalc = (m) => (m.IsBerileVerilen || m.isBerileVerilen) || ['BERİLE VERİLEN', 'Berile Verilen'].includes(String(m.MasrafTuru || m.masrafTuru || '').trim());
                const yeniMasraflarCalc = masraflar.filter(m => m.MasrafTuru || m.masrafTuru);
                const gelirlerCalc = yeniMasraflarCalc.filter(m => m.IsGelir || m.isGelir);
                const giderlerCalc = yeniMasraflarCalc.filter(m => !(m.IsGelir || m.isGelir) && !isBerileVerilenCalc(m));
                const berileVerilenToplam = (yeniMasraflarCalc.filter(m => isBerileVerilenCalc(m))).reduce((sum, m) => sum + (parseFloat(m.Tutar || m.tutar) || 0), 0);
                
                const toplamGelir = gelirlerCalc.reduce((sum, m) => sum + (parseFloat(m.Tutar || m.tutar) || 0), 0);
                const toplamGider = giderlerCalc.reduce((sum, m) => sum + (parseFloat(m.Tutar || m.tutar) || 0), 0);
                const netGelir = toplamGelir - toplamGider;
                
                const formatCurrencySummary = (amount) => {
                  return new Intl.NumberFormat('tr-TR', {
                    style: 'currency',
                    currency: 'TRY'
                  }).format(amount);
                };
                
                return (
                  <div className="dosya-geliri-summary" style={{ marginTop: '24px' }}>
                    <div className="gelir-summary-cards">
                      <div className="gelir-summary-card gelir-card">
                        <h3 className="gelir-summary-title">Toplam Gelir</h3>
                        <p className="gelir-summary-amount gelir-positive">{formatCurrencySummary(toplamGelir)}</p>
                      </div>
                      <div className="gelir-summary-card gider-card">
                        <h3 className="gelir-summary-title">Toplam Gider</h3>
                        <p className="gelir-summary-amount gider-negative">{formatCurrencySummary(toplamGider)}</p>
                      </div>
                      <div className="gelir-summary-card net-bakiye-card">
                        <h3 className="gelir-summary-title">Net Bakiye</h3>
                        <p className="gelir-summary-amount net-amount">{formatCurrencySummary(netGelir)}</p>
                      </div>
                    </div>
                    {berileVerilenToplam > 0 && (
                      <div style={{ marginTop: '16px', padding: '12px 16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <strong>Berile verilen:</strong> {formatCurrencySummary(berileVerilenToplam)}
                      </div>
                    )}
                    {(() => {
                      const isMuvekkileOdeme = (m) => {
                        const t = String(m.MasrafTuru || m.masrafTuru || '')
                          .trim()
                          .toLocaleUpperCase('tr-TR')
                          .replace(/İ/g, 'I')
                          .replace(/Ü/g, 'U')
                          .replace(/Ö/g, 'O');
                        return t.startsWith('MUVEKKILE YAPILAN ODEME');
                      };
                      const muvekkileOdemeToplam = yeniMasraflarCalc
                        .filter(isMuvekkileOdeme)
                        .reduce((sum, m) => sum + (parseFloat(m.Tutar || m.tutar) || 0), 0);
                      return (
                        <div className="gelir-summary-cards" style={{ marginTop: '16px', gridTemplateColumns: '1fr' }}>
                          <div className="gelir-summary-card muvekkil-odeme-card">
                            <h3 className="gelir-summary-title">Müvekkile Yapılan Ödemeler</h3>
                            <p className="gelir-summary-amount muvekkil-odeme-amount">{formatCurrencySummary(muvekkileOdemeToplam)}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </>
          );
        })()}

          {/* Masraf Form Modal */}
          {showMasrafForm && (
            <MasrafForm
              masraf={editingMasraf}
              caseFileId={parseInt(id)}
              caseFiles={masrafCaseFiles}
              caseFile={caseFile}
              allowMasrafEdit={hesapDetayiGorebilir}
              onSubmit={async () => {
                // Hem admin hem avukatlar için masrafları yükle
                await loadMasraflar();
                setShowMasrafForm(false);
                setEditingMasraf(null);
                setEditingMasrafRequest(null);
              }}
              onCancel={() => {
                setShowMasrafForm(false);
                setEditingMasraf(null);
                setEditingMasrafRequest(null);
              }}
            />
          )}
          </div>
        </div>
      )}

          {/* Masraflar Bölümü - Avukatlar (özel dosyalarda yok) */}
          {user && (user.Role !== 'Admin' && user.role !== 'Admin') && !isOzelFile && (
            <div className="detail-card detail-card-masraflar cd-panel cd-panel-masraflar">
              <div className="detail-section">
              <div className="section-header masraf-tabs-header">
                <div className="masraf-tabs">
                  <button
                    className={`masraf-tab ${masrafTab === 'masraflar' ? 'active' : ''}`}
                    onClick={() => setMasrafTab('masraflar')}
                  >
                    <Icon name="wallet" size={17} /> Masraflar
                  </button>
                  {hesapDetayiGorebilir && (
                    <button
                      className={`masraf-tab ${masrafTab === 'hesap' ? 'active' : ''}`}
                      onClick={() => setMasrafTab('hesap')}
                    >
                      🧮 Hesap Detayı
                    </button>
                  )}
                  <button
                    className={`masraf-tab ${masrafTab === 'icra' ? 'active' : ''}`}
                    onClick={() => setMasrafTab('icra')}
                  >
                    ⚖️ İcra Hesabı
                  </button>
                </div>
                {masrafTab === 'masraflar' && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setEditingMasraf(null);
                      setEditingMasrafRequest(null);
                      setShowMasrafForm(true);
                    }}
                  >
                    + Masraf Talebi Oluştur
                  </button>
                )}
              </div>

          {masrafTab === 'icra' ? (
            <IcraHesap
              caseFile={caseFile}
              onUpdated={(updated) => {
                if (updated) setCaseFile(updated);
                loadMasraflar();
              }}
            />
          ) : hesapDetayiGorebilir && masrafTab === 'hesap' ? (
            <HesapDetayi
              caseFileId={id}
              caseFile={caseFile}
              masraflar={masraflar}
              loadingMasraflar={loadingMasraflar}
              onUpdated={(updated) => {
                if (updated) setCaseFile(updated);
              }}
              onMasraflarChanged={loadMasraflar}
            />
          ) : loadingMasraflar ? (
            <p>Yükleniyor...</p>
          ) : (() => {
            const AVUKAT_GORMEYECEK_TURLER = [
              'BERİLE VERİLEN',
              'REFERANSA VERİLEN AVANS',
              'MÜVEKKİLE YAPILAN ÖDEME',
              'MÜVEKKİLE YAPILAN ÖDEME(BAŞVURU AŞAMASI)',
              'MÜVEKKİLE YAPILAN ÖDEME(İCRA)',
              'MÜVEKKİLE YAPILAN ÖDEME(TAHKİM AŞAMASI)'
            ];
            const normalizeTur = (str) => String(str || '').trim().replace(/İ/g, 'I').toUpperCase();
            const isAvukatGoremeyecek = (m) => {
              const tur = normalizeTur(m.MasrafTuru || m.masrafTuru);
              if (!tur) return false;
              return AVUKAT_GORMEYECEK_TURLER.some(h => {
                const hNorm = normalizeTur(h);
                return tur === hNorm || tur.startsWith(hNorm + '(') || tur.startsWith(hNorm + ' ');
              });
            };
            const yeniMasraflar = masraflar
              .filter(m => m.MasrafTuru || m.masrafTuru || m.CustomMasrafTuru || m.customMasrafTuru)
              .filter(m => !isAvukatGoremeyecek(m));
            const gelirler = yeniMasraflar.filter(m => m.IsGelir || m.isGelir);
            const giderler = yeniMasraflar.filter(m => !(m.IsGelir || m.isGelir));
            
            if (gelirler.length === 0 && giderler.length === 0) {
              return <p style={{ color: '#666', fontStyle: 'italic' }}>Henüz masraf bulunmamaktadır.</p>;
            }

            const formatCurrency = (amount) => {
              if (amount === null || amount === undefined) return '-';
              return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
            };
            
            const formatDate = (dateString) => {
              if (!dateString) return '-';
              return formatDateOnly(dateString);
            };

            const MasrafRow = ({ masraf }) => (
              <tr key={masraf.id || masraf.Id}>
                <td>{masraf.customMasrafTuru || masraf.CustomMasrafTuru || masraf.masrafTuru || masraf.MasrafTuru || '-'}</td>
                <td>{formatCurrency(masraf.tutar || masraf.Tutar)}</td>
                <td>{formatDate(masraf.sonTarih || masraf.SonTarih || masraf.tarih || masraf.Tarih || masraf.masrafIstenmeTarihi || masraf.MasrafIstenmeTarihi)}</td>
                <td className="masraf-aciklama-cell">{masraf.aciklama || masraf.Aciklama || '-'}</td>
                {hesapDetayiGorebilir && (
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => {
                          setEditingMasraf(masraf);
                          setEditingMasrafRequest(null);
                          setShowMasrafForm(true);
                        }}
                      >
                        Düzenle
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );

            return (
              <>
                {gelirler.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 className="detail-subsection-title" style={{ marginBottom: '10px', fontSize: '1rem' }}><Icon name="income" size={16} /> Gelirler</h3>
                    <div className="masraflar-table-container">
                      <table className="masraflar-table">
                        <thead>
                          <tr>
                            <th>Masraf Türü</th>
                            <th>Tutar</th>
                            <th>Tarih</th>
                            <th>Açıklama</th>
                            {hesapDetayiGorebilir && <th>İşlemler</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {gelirler.map((masraf) => <MasrafRow key={masraf.id || masraf.Id} masraf={masraf} />)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {giderler.length > 0 && (
                  <div>
                    <h3 className="detail-subsection-title" style={{ marginBottom: '10px', fontSize: '1rem' }}><Icon name="expense" size={16} /> Giderler</h3>
                    <div className="masraflar-table-container">
                      <table className="masraflar-table">
                        <thead>
                          <tr>
                            <th>Masraf Türü</th>
                            <th>Tutar</th>
                            <th>Tarih</th>
                            <th>Açıklama</th>
                            {hesapDetayiGorebilir && <th>İşlemler</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {giderler.map((masraf) => <MasrafRow key={masraf.id || masraf.Id} masraf={masraf} />)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* Masraf Form Modal - Avukat masraf talebi oluşturur */}
          {showMasrafForm && (
            <MasrafForm
              masraf={editingMasraf}
              masrafRequest={editingMasrafRequest}
              caseFileId={parseInt(id)}
              caseFiles={masrafCaseFiles}
              caseFile={caseFile}
              allowMasrafEdit={hesapDetayiGorebilir}
              onSubmit={async () => {
                await loadMasraflar();
                await loadMasrafTalepleri();
                setShowMasrafForm(false);
                setEditingMasraf(null);
                setEditingMasrafRequest(null);
              }}
              onCancel={() => {
                setShowMasrafForm(false);
                setEditingMasraf(null);
                setEditingMasrafRequest(null);
              }}
            />
          )}
              </div>
            </div>
          )}
        </div>

        {/* Sürüklenebilir bölme çubuğu */}
        <div
          className="detail-split-handle"
          onMouseDown={handleSplitMouseDown}
          onDoubleClick={() => setDetayPct(40)}
          title="Sürükleyerek genişliği ayarlayın (çift tıkla sıfırla)"
          role="separator"
          aria-orientation="vertical"
        >
          <span className="detail-split-grip" />
        </div>

        {/* Belgeler Bölümü - Sağ Taraf */}
        <div className="case-file-detail-documents-sidebar cd-panel cd-panel-belgeler">
          <div className="detail-card">
            <div className="detail-section">
              <div className="section-header">
                <h2>Belgeler{docReorderSaving ? <small style={{ marginLeft: 8, fontWeight: 400, color: '#64748b' }}>sıra kaydediliyor…</small> : null}</h2>
                <button onClick={belgeFormunuAcKapat} className="btn btn-primary btn-sm">
                  {Object.values(showUploadForm).some(v => v) ? 'İptal' : ekleDugmesiAdi}
                </button>
              </div>

              {odemeAlaniVar && (
                <BelgeAlaniSekmeleri
                  alan={belgeAlani}
                  onAlan={belgeAlaniDegistir}
                  belgeSayisi={documents.filter((d) => !odemeSekmesiOf(d)).length}
                  odemeSayisi={odemeSekmeleri.reduce((t, s) => t + s.sayi, 0)}
                />
              )}
              {odemeSekmesinde && (
                <OdemeSekmeleri
                  sekme={odemeSekmesi}
                  onSekme={(s) => { setOdemeSekmesi(s); setSelectedDocuments([]); setShowUploadForm({}); }}
                  sekmeler={odemeSekmeleri}
                />
              )}

              {/* Belgeler'de kalmış dekont / masraf belgeleri: tek tuşla kendi sekmelerine */}
              {odemeAlaniVar && belgeAlani === 'belgeler' && canEdit && odemeOnerileri.length > 0 && (
                <div className="fo-oneri" role="status">
                  <div className="fo-oneri-metin">
                    <strong>Masraflar ve Ödemeler'e taşınabilecek {odemeOnerileri.length} belge var:</strong>
                    <ul className="fo-oneri-liste">
                      {odemeOnerileri.slice(0, 6).map(({ belge, sekme }) => (
                        <li key={belge.Id ?? belge.id}>
                          <span className="fo-oneri-ad">{belge.FileName ?? belge.fileName}</span>
                          <span className="fo-oneri-ok" aria-hidden="true">→</span>
                          <span className="fo-oneri-hedef">{SEKME_ADI[sekme]}</span>
                        </li>
                      ))}
                    </ul>
                    {odemeOnerileri.length > 6 && <span>ve {odemeOnerileri.length - 6} belge daha</span>}
                  </div>
                  <div className="fo-oneri-dugmeler">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={topluTasiniyor}
                      onClick={odemeOnerileriniTasi}
                    >
                      {topluTasiniyor ? 'Taşınıyor…' : 'Hepsini taşı'}
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={odemeOnerileriniGizle}>
                      Gösterme
                    </button>
                  </div>
                </div>
              )}

              {/* Dosya Yükleme Formu */}
              {Object.values(showUploadForm).some(v => v) && (
                <div className="upload-form-container">
                  {[...GENEL_BELGE_TURLERI, ...(isOzelFile ? [] : Object.values(SEKME_BELGE_TURU))].map((docType) => {
                    const isUploading = uploading[docType] || false;
                    const selectedFile = selectedFiles[docType] || null;
                    const fileDescription = fileDescriptions[docType] || '';
                    const showForm = showUploadForm[docType] || false;

                    if (!showForm) return null;
                    // "Nereye Eklensin?": Belgeler ya da bir ödeme sekmesi (seçilmeden yüklenmez)
                    const hedef = turunSekmesi(docType) || 'belgeler';
                    const hedefSec = (yeniHedef) => {
                      setBelgeHedefiSecildi(true);
                      if (yeniHedef === 'belgeler') {
                        if (turunSekmesi(docType)) belgeTuruDegistir(docType, DOCUMENT_TYPE.Other);
                      } else {
                        belgeTuruDegistir(docType, SEKME_BELGE_TURU[yeniHedef]);
                      }
                    };

                    return (
                      <form key={docType} onSubmit={(e) => handleUpload(docType, e)} className="upload-form">
                        {docType === DOCUMENT_TYPE.Makbuz && (
                          <p className="mk-ipucu">
                            Makbuz PDF'ini seçin; <strong>makbuz tarihi</strong>, en alttaki <strong>Net Tutar</strong> ve
                            <strong> ödenecek KDV</strong> makbuzdan okunur. Birden çok makbuz seçerseniz her biri ayrı okunur.
                            Fotoğraf ya da taranmış makbuzda elle girersiniz.
                          </p>
                        )}
                        <div 
                          className={`form-group drag-drop-area ${dragActive[docType] ? 'drag-active' : ''}`}
                          onDragEnter={(e) => handleDrag(docType, e)}
                          onDragLeave={(e) => handleDrag(docType, e)}
                          onDragOver={(e) => handleDrag(docType, e)}
                          onDrop={(e) => handleDrop(docType, e)}
                        >
                          <label>Dosya Seç (Sürükle-Bırak veya Tıkla):</label>
                          <input
                            type="file"
                            multiple
                            onChange={(e) => handleFileSelect(docType, e)}
                            /* DİKKAT (2026-10-09): 07.10'da buraya .udf, .eyp, .zip eklenince
                               telefonda belge seçildikten sonra yükleme hiç başlamadı (5 Ekim'den
                               sonra telefondan tek yükleme yok). Liste önceki, çalıştığı bilinen
                               hâline döndürüldü; iOS'ta UDF seçimi ayrıca denenmeden eklenmesin. */
                            accept={isNativeApp() ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,image/*' : '*/*'}
                            disabled={isUploading}
                            style={{ display: 'none' }}
                            id={`file-input-${docType}`}
                            onClick={markNativeFilePicker}
                          />
                          <label 
                            htmlFor={`file-input-${docType}`}
                            className="drag-drop-label"
                          >
                            {dragActive[docType] ? (
                              <span>Dosyaları buraya bırakın...</span>
                            ) : (
                              <span>📁 Dosyaları sürükleyin veya tıklayarak seçin (PDF, evrak, fotoğraf…)</span>
                            )}
                          </label>
                          {kameraVarMi() && (
                            <button
                              type="button"
                              className="cfd-foto-cek"
                              onClick={() => fotografCek(docType)}
                              disabled={isUploading}
                            >
                              Fotoğraf Çek
                            </button>
                          )}
                          {selectedFile && (
                            <div className="selected-files-list">
                              {(Array.isArray(selectedFile) ? selectedFile : [selectedFile]).length > 1 && (
                                <p style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
                                  Sıra: listedeki 1. dosya yüklemeden sonra en üstte görünür. Sürükleyerek değiştirin.
                                </p>
                              )}
                              {(Array.isArray(selectedFile) ? selectedFile : [selectedFile]).map((file, index, arr) => (
                                <div
                                  key={`${file.name}-${file.size}-${index}`}
                                  className="pending-upload-item"
                                  draggable={arr.length > 1 && !isUploading && !dokunmatikEkran}
                                  onDragStart={(e) => {
                                    e.dataTransfer.setData('text/plain', String(index));
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
                                    if (!Number.isNaN(from) && from !== index) {
                                      movePendingUploadFile(docType, from, index);
                                    }
                                  }}
                                  style={{
                                    marginBottom: '10px',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    background: '#fff',
                                    cursor: arr.length > 1 ? 'grab' : 'default'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                                    {arr.length > 1 && (
                                      <span style={{ color: '#94a3b8', userSelect: 'none' }} title="Sürükleyerek sırayı değiştirin">⋮⋮</span>
                                    )}
                                    <p style={{ fontSize: '12px', color: '#666', margin: 0, fontWeight: 'bold', flex: 1 }}>
                                      {index + 1}. {file.name} ({formatFileSize(file.size)})
                                    </p>
                                    {arr.length > 1 && (
                                      <div style={{ display: 'flex', gap: '4px' }}>
                                        <button
                                          type="button"
                                          className="btn btn-danger btn-sm"
                                          title="Listeden çıkar"
                                          disabled={isUploading}
                                          onClick={() => removePendingUploadFile(docType, index)}
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  {docType === DOCUMENT_TYPE.Makbuz && (() => {
                                    const k = dosyaAnahtari(file);
                                    const b = makbuzBilgileri[k] || {};
                                    const degistir = (alan, deger) => setMakbuzBilgileri((o) => ({ ...o, [k]: { ...(o[k] || {}), [alan]: deger } }));
                                    const tutarSayi = parseFloat(b.tutar);
                                    const kdvSayi = parseFloat(b.kdv);
                                    return (
                                      <div className="mk-bilgi">
                                        <MakbuzOkumaDurumu bilgi={b} />
                                        <label>
                                          Makbuz tarihi *
                                          <input
                                            type="date"
                                            value={b.tarih || ''}
                                            onChange={(e) => degistir('tarih', e.target.value)}
                                            className={b.okunanTarih && b.tarih === b.okunanTarih ? 'mk-okundu' : ''}
                                            disabled={isUploading}
                                            required
                                          />
                                        </label>
                                        <label>
                                          <span>Net tutar (TL) *{tutarSayi > 0 && <span className="mk-tutar-yazi"> {paraYaz(tutarSayi)}</span>}</span>
                                          <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            inputMode="decimal"
                                            value={b.tutar || ''}
                                            onChange={(e) => degistir('tutar', e.target.value)}
                                            placeholder="Örn: 13674.04"
                                            className={b.okunanTutar != null && tutarSayi === b.okunanTutar ? 'mk-okundu' : ''}
                                            disabled={isUploading}
                                            required
                                          />
                                        </label>
                                        <label className="mk-kdv">
                                          <span>Ödenecek KDV (TL){b.kdv !== '' && kdvSayi >= 0 && <span className="mk-tutar-yazi"> {paraYaz(kdvSayi)}</span>}</span>
                                          <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            inputMode="decimal"
                                            value={b.kdv || ''}
                                            onChange={(e) => degistir('kdv', e.target.value)}
                                            placeholder="Makbuzdaki tahsil edilen KDV"
                                            className={b.okunanKdv != null && kdvSayi === b.okunanKdv ? 'mk-okundu' : ''}
                                            disabled={isUploading}
                                          />
                                          <small className="mk-kdv-not">Makbuzda okunur; boş bırakılırsa Kasa'da net tutardan tahmin edilir.</small>
                                        </label>
                                      </div>
                                    );
                                  })()}
                                  <textarea
                                    value={(fileDescriptions[docType] && typeof fileDescriptions[docType] === 'object' && !Array.isArray(fileDescriptions[docType]))
                                      ? (fileDescriptions[docType][index] || '')
                                      : (index === 0 ? (typeof fileDescriptions[docType] === 'string' ? fileDescriptions[docType] : '') : '')}
                                    onChange={(e) => {
                                      setFileDescriptions(prev => {
                                        const current = prev[docType] || {};
                                        const newDescs = typeof current === 'object' && !Array.isArray(current)
                                          ? { ...current }
                                          : { 0: typeof current === 'string' ? current : '' };
                                        newDescs[index] = e.target.value;
                                        return { ...prev, [docType]: newDescs };
                                      });
                                    }}
                                    rows="2"
                                    placeholder={`${file.name} için açıklama (opsiyonel)...`}
                                    disabled={isUploading}
                                    style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px' }}
                                  />
                                </div>
                              ))}
                              <p style={{ fontSize: '11px', color: '#28a745', marginTop: '5px', fontWeight: 'bold' }}>
                                {(Array.isArray(selectedFile) ? selectedFile : [selectedFile]).length} dosya seçildi
                              </p>
                            </div>
                          )}
                        </div>
                        {!isOzelFile && (
                          <BelgeHedefSecici
                            secili={belgeHedefiSecildi ? hedef : null}
                            onSec={hedefSec}
                            yonetici={kullaniciAdmin}
                            disabled={isUploading}
                            oneri={(() => {
                              // Seçilen dosyaların hepsi aynı sekmeye benziyorsa ("müvekkile yapılan ödeme
                              // dekontu" → Müvekkile Ödenen, "bilirkişi ücreti" → Masraflar) o yer önerilir
                              const liste = Array.isArray(selectedFile) ? selectedFile : (selectedFile ? [selectedFile] : []);
                              const yerler = [...new Set(liste.map((f) => odemeBelgesiSekmesi(f.name)))];
                              const yer = yerler.length === 1 ? yerler[0] : null;
                              return yer && (kullaniciAdmin || yer !== 'muvekkile') ? yer : null;
                            })()}
                          />
                        )}
                        {(isOzelFile || (belgeHedefiSecildi && hedef === 'belgeler')) && (
                          <div className="form-group">
                            <label>Belge türü</label>
                            <select
                              value={docType}
                              onChange={(e) => belgeTuruDegistir(docType, parseInt(e.target.value, 10))}
                              className="form-control"
                              disabled={isUploading}
                            >
                              {GENEL_BELGE_TURLERI.map((t) => (
                                <option key={t} value={t}>{DOCUMENT_TYPE_LABELS[t]}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <button
                          type="submit"
                          className="btn btn-success"
                          disabled={isUploading || !selectedFile || (Array.isArray(selectedFile) ? selectedFile.length === 0 : false) || (!isOzelFile && !belgeHedefiSecildi)}
                        >
                          {isUploading
                            ? 'Kuyruğa ekleniyor...'
                            : (!isOzelFile && !belgeHedefiSecildi)
                              ? 'Önce nereye ekleneceğini seçin'
                              : `Yükle${selectedFile ? ` (${Array.isArray(selectedFile) ? selectedFile.length : 1} dosya)` : ''}`}
                        </button>
                      </form>
                    );
                  })}
                </div>
              )}

              {/* Toplu İndirme Butonu — yalnız görünen belgeler seçilir (diğer alandakiler değil) */}
              {sortedDocuments.length > 0 && (
                <div className="bulk-actions-bar">
                  <label className="select-all-checkbox">
                    <input
                      type="checkbox"
                      checked={sortedDocuments.every(doc => selectedDocuments.includes(doc.Id ?? doc.id))}
                      onChange={() => handleSelectAll(sortedDocuments)}
                    />
                    <span>Tümünü Seç</span>
                  </label>
                  {selectedDocuments.length > 0 && (
                    <>
                      <button
                        onClick={handleBulkDownload}
                        className="btn btn-success btn-sm"
                      >
                        📥 Seçili Dosyaları İndir ({selectedDocuments.length})
                      </button>
                      <button
                        onClick={handleBulkDelete}
                        className="btn btn-danger btn-sm"
                        style={{ marginLeft: '10px' }}
                      >
                        🗑️ Seçili Dosyaları Sil ({selectedDocuments.length})
                      </button>
                      {/* Eski dekont / fatura gibi belgeleri kendi sekmesine taşır */}
                      {odemeAlaniVar && canEdit && (
                        <label className="toplu-tasi">
                          <span>Taşı:</span>
                          <select
                            value=""
                            disabled={topluTasiniyor}
                            onChange={(e) => e.target.value && seciliBelgeleriTasi(e.target.value)}
                          >
                            <option value="">{topluTasiniyor ? 'Taşınıyor…' : 'Nereye?'}</option>
                            {odemeSekmesinde && <option value="belgeler">Belgeler</option>}
                            {['masraf', 'gelen', ...(kullaniciAdmin ? ['muvekkile'] : [])]
                              .filter((s) => !(odemeSekmesinde && odemeSekmesi === s))
                              .map((s) => (
                                <option key={s} value={s}>
                                  {{ masraf: 'Masraflar', gelen: 'Gelen Ödemeler', muvekkile: 'Müvekkile Ödenen' }[s]}
                                </option>
                              ))}
                          </select>
                        </label>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Belgeler Arama */}
              {alanBelgeleri.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="🔍 Belgelerde ara (dosya adı, açıklama, belge türü, yükleyen)..."
                      value={documentSearchTerm}
                      onChange={(e) => setDocumentSearchTerm(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                    {documentSearchTerm && (
                      <button
                        onClick={() => setDocumentSearchTerm('')}
                        className="btn btn-secondary btn-sm"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Temizle
                      </button>
                    )}
                  </div>
                  {documentSearchTerm && (
                    <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                      {sortedDocuments.length} belge bulundu (toplam {alanBelgeleri.length} belgeden)
                    </p>
                  )}
                </div>
              )}

              {/* Belgeler Tablosu */}
              {loadingDocuments ? (
                <p>Yükleniyor...</p>
              ) : alanBelgeleri.length === 0 ? (
                odemeSekmesinde ? (
                  <div className="fo-bos-kutu">
                    <p className="fo-bos">
                      {{
                        makbuz: 'Bu dosyada henüz makbuz yok.',
                        gelen: 'Bu sekmede henüz gelen ödeme dekontu yok.',
                        muvekkile: 'Bu sekmede henüz müvekkile ödeme dekontu yok.',
                        masraf: 'Bu sekmede henüz masraf belgesi (fatura, fiş, harç makbuzu) yok.',
                      }[odemeSekmesi]}
                    </p>
                    <p className="fo-not">
                      {odemeSekmesi === 'makbuz'
                        ? '"+ Makbuz Ekle" ile makbuz PDF\'ini seçin; tarih ve net tutar makbuzdan okunur.'
                        : `"${ekleDugmesiAdi}" ile ekleyin. Belgeler'deki eski belgeleri seçip "Taşı" ile buraya alabilirsiniz.`}
                    </p>
                  </div>
                ) : YENI_TASARIM
                  ? <BosDurum simge="belge" baslik="Henüz belge yok" metin="Bu dosyaya eklenen evraklar burada listelenir." />
                  : <p style={{ color: '#666', fontStyle: 'italic' }}>Henüz belge eklenmemiş.</p>
              ) : sortedDocuments.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                  Arama kriterlerinize uygun belge bulunamadı.
                </p>
              ) : (
                <YatayKaydirma className="documents-table-container">
                  <table className="documents-table">
                    <thead>
                      <tr>
                        <th style={{ width: '36px' }} title="Sıralamak için sürükleyin">⋮⋮</th>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={sortedDocuments.length > 0 && sortedDocuments.every(doc => selectedDocuments.includes(doc.Id ?? doc.id))}
                            onChange={() => handleSelectAll(sortedDocuments)}
                          />
                        </th>
                        <th>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }} onClick={() => {
                            if (documentSortColumn === 'fileName') {
                              setDocumentSortDirection(documentSortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setDocumentSortColumn('fileName');
                              setDocumentSortDirection('asc');
                            }
                          }}>
                            Dosya Adı
                            {documentSortColumn === 'fileName' && (
                              documentSortDirection === 'asc' ? ' ▲' : ' ▼'
                            )}
                            {documentSortColumn !== 'fileName' && ' ⇅'}
                          </div>
                        </th>
                        <th>Açıklama</th>
                        <th>Belge Türü</th>
                        <th>İşlemler</th>
                        <th>Boyut</th>
                        <th>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }} onClick={() => {
                            if (documentSortColumn === 'uploadDate') {
                              setDocumentSortDirection(documentSortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setDocumentSortColumn('uploadDate');
                              setDocumentSortDirection('asc');
                            }
                          }}>
                            Yüklenme Tarihi
                            {documentSortColumn === 'uploadDate' && (
                              documentSortDirection === 'asc' ? ' ▲' : ' ▼'
                            )}
                            {documentSortColumn !== 'uploadDate' && ' ⇅'}
                          </div>
                        </th>
                        <th>Yükleyen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDocuments.map((doc, sira) => {
                        const docId = doc.Id ?? doc.id;
                        const isSelected = selectedDocuments.includes(docId);
                        const docType = doc.DocumentType ?? doc.documentType ?? 0;
                        return (
                          <tr
                            key={docId}
                            className={`${isSelected ? 'selected' : ''}${draggingDocId === docId ? ' doc-row-dragging' : ''}`}
                            onDoubleClick={() => handlePreview(doc)}
                            style={{ cursor: 'pointer' }}
                            title="Önizlemek için çift tıklayın; sırayı değiştirmek için ⋮⋮ sürükleyin"
                            draggable={canEdit && !dokunmatikEkran}
                            onDragStart={(e) => handleDocDragStart(e, docId)}
                            onDragOver={handleDocDragOver}
                            onDrop={(e) => handleDocDrop(e, docId)}
                            onDragEnd={handleDocDragEnd}
                          >
                            <td
                              className="doc-drag-handle"
                              onDoubleClick={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              title={dokunmatikEkran ? undefined : 'Sürükleyerek sırayı değiştirin'}
                              style={{ cursor: canEdit && !dokunmatikEkran ? 'grab' : 'default', textAlign: 'center', color: '#94a3b8', userSelect: 'none' }}
                            >
                              {/* Telefonda surukleme yok: sira oklarla degisir.
                                  Parmak tablonun kaydirilmasina kalir. */}
                              {dokunmatikEkran ? (
                                canEdit ? (
                                  <div className="doc-sira-oklari">
                                    <button
                                      type="button"
                                      aria-label="Yukarı taşı"
                                      disabled={sira === 0 || docReorderSaving}
                                      onClick={(e) => { e.stopPropagation(); moveDocumentByOffset(docId, -1); }}
                                    >▲</button>
                                    <button
                                      type="button"
                                      aria-label="Aşağı taşı"
                                      disabled={sira === sortedDocuments.length - 1 || docReorderSaving}
                                      onClick={(e) => { e.stopPropagation(); moveDocumentByOffset(docId, 1); }}
                                    >▼</button>
                                  </div>
                                ) : null
                              ) : '⋮⋮'}
                            </td>
                            <td onDoubleClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleDocumentSelect(docId)}
                              />
                            </td>
                            <td onDoubleClick={(e) => e.stopPropagation()}>
                              {canEdit && editingDocNameId === docId ? (
                                <div className="doc-rename">
                                  <input
                                    className="form-control"
                                    value={docNameDraft}
                                    autoFocus
                                    onChange={(e) => setDocNameDraft(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); saveDocumentName(docId); }
                                      if (e.key === 'Escape') { e.preventDefault(); cancelEditDocumentName(); }
                                    }}
                                  />
                                  <div className="doc-rename-actions">
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      disabled={savingDocName}
                                      onClick={(e) => { e.stopPropagation(); saveDocumentName(docId); }}
                                    >
                                      Kaydet
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-secondary btn-sm"
                                      disabled={savingDocName}
                                      onClick={(e) => { e.stopPropagation(); cancelEditDocumentName(); }}
                                    >
                                      İptal
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <strong
                                  onClick={(e) => {
                                    if (!canEdit) return;
                                    e.stopPropagation();
                                    startEditDocumentName(doc);
                                  }}
                                  style={{
                                    cursor: canEdit ? 'pointer' : 'default',
                                    textDecoration: canEdit ? 'underline dotted' : 'none',
                                  }}
                                  title={canEdit ? 'Adı değiştirmek için tıklayın' : undefined}
                                >
                                  {doc.FileName ?? doc.fileName}
                                </strong>
                              )}
                            </td>
                            <td>
                              {doc.Description ? (
                                <div className="document-description" style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                                  {doc.Description}
                                </div>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                              )}
                            </td>
                            <td
                              onDoubleClick={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canEdit && editingDocTypeId !== docId) {
                                  startEditDocumentType(doc);
                                }
                              }}
                              style={{ cursor: canEdit ? 'pointer' : 'default', minWidth: '160px' }}
                              title={canEdit ? 'Tür değiştirmek için tıklayın' : undefined}
                            >
                              {canEdit && editingDocTypeId === docId ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  <select
                                    value={docTypeDraft}
                                    onChange={(e) => setDocTypeDraft(parseInt(e.target.value, 10))}
                                    className="form-control"
                                    style={{ fontSize: '13px', padding: '4px 6px' }}
                                    disabled={savingDocType}
                                  >
                                    <option value={DOCUMENT_TYPE.Other}>{DOCUMENT_TYPE_LABELS[DOCUMENT_TYPE.Other]}</option>
                                    <option value={DOCUMENT_TYPE.Vekaletname}>{DOCUMENT_TYPE_LABELS[DOCUMENT_TYPE.Vekaletname]}</option>
                                    <option value={DOCUMENT_TYPE.KazaTespitTutanagi}>{DOCUMENT_TYPE_LABELS[DOCUMENT_TYPE.KazaTespitTutanagi]}</option>
                                    {!isOzelFile && (
                                      <option value={DOCUMENT_TYPE.Makbuz}>{DOCUMENT_TYPE_LABELS[DOCUMENT_TYPE.Makbuz]}</option>
                                    )}
                                    <option value={DOCUMENT_TYPE.IbranameMuvafakatname}>{DOCUMENT_TYPE_LABELS[DOCUMENT_TYPE.IbranameMuvafakatname]}</option>
                                    {/* Bu türler "Masraflar ve Ödemeler" sekmelerine taşınır */}
                                    {!isOzelFile && (
                                      <>
                                        <option value={DOCUMENT_TYPE.MasrafBelgesi}>{DOCUMENT_TYPE_LABELS[DOCUMENT_TYPE.MasrafBelgesi]}</option>
                                        <option value={DOCUMENT_TYPE.GelenOdemeDekontu}>{DOCUMENT_TYPE_LABELS[DOCUMENT_TYPE.GelenOdemeDekontu]}</option>
                                        {kullaniciAdmin && (
                                          <option value={DOCUMENT_TYPE.MuvekkileOdemeDekontu}>{DOCUMENT_TYPE_LABELS[DOCUMENT_TYPE.MuvekkileOdemeDekontu]}</option>
                                        )}
                                      </>
                                    )}
                                  </select>
                                  {parseInt(docTypeDraft, 10) === DOCUMENT_TYPE.Makbuz && (
                                    <>
                                      <input
                                        type="date"
                                        className="form-control"
                                        value={docTypeMakbuzTarihi}
                                        onChange={(e) => setDocTypeMakbuzTarihi(e.target.value)}
                                        disabled={savingDocType}
                                        style={{ fontSize: '13px', padding: '4px 6px' }}
                                        title="Makbuz tarihi"
                                      />
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        placeholder="Net tutar"
                                        value={docTypeMakbuzTutar}
                                        onChange={(e) => setDocTypeMakbuzTutar(e.target.value)}
                                        disabled={savingDocType}
                                        style={{ fontSize: '13px', padding: '4px 6px' }}
                                      />
                                    </>
                                  )}
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      disabled={savingDocType}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        saveDocumentType(docId);
                                      }}
                                    >
                                      Kaydet
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-secondary btn-sm"
                                      disabled={savingDocType}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        cancelEditDocumentType();
                                      }}
                                    >
                                      İptal
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <span style={{ textDecoration: canEdit ? 'underline dotted' : 'none' }}>
                                    {DOCUMENT_TYPE_LABELS[docType] || 'Diğer'}
                                  </span>
                                  {docType === DOCUMENT_TYPE.Makbuz && (doc.FaturaTarihi ?? doc.faturaTarihi) && (
                                    <small className="doc-makbuz-bilgi">
                                      {formatDateOnly(doc.FaturaTarihi ?? doc.faturaTarihi)} · {paraYaz(doc.Tutar ?? doc.tutar)}
                                      {(doc.OdenecekKdv ?? doc.odenecekKdv) != null && ` · KDV ${paraYaz(doc.OdenecekKdv ?? doc.odenecekKdv)}`}
                                    </small>
                                  )}
                                </>
                              )}
                            </td>
                            <td onDoubleClick={(e) => e.stopPropagation()}>
                              <div className="document-actions">
                                <button
                                  onClick={() => handlePreview(doc)}
                                  className="btn btn-info btn-sm"
                                  title="Önizle"
                                >
                                  👁️
                                </button>
                                <button
                                  onClick={() => handleDownload(docId, doc.FileName ?? doc.fileName, doc.ContentType ?? doc.contentType)}
                                  className="btn btn-primary btn-sm"
                                  title="İndir"
                                >
                                  📥
                                </button>
                                <button
                                  onClick={() => handleDownloadAsPdf(doc)}
                                  className="btn btn-secondary btn-sm"
                                  title="PDF olarak indir"
                                >
                                  PDF
                                </button>
                                {canEdit && (
                                  <>
                                    <button
                                      onClick={() => handleDeleteDocument(docId)}
                                      className="btn btn-danger btn-sm"
                                      title="Sil"
                                    >
                                      🗑️
                                    </button>
                                    {/* Müvekkil portalı görünürlüğü — müvekkilin
                                        kendi yüklediği belge zaten ona açıktır. */}
                                    {!(doc.MuvekkilYukledi ?? doc.muvekkilYukledi) && (
                                      <label
                                        className="doc-muvekkil-onay"
                                        title="İşaretlenirse müvekkil bu belgeyi portalda görebilir"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={!!(doc.MuvekkilGorebilir ?? doc.muvekkilGorebilir)}
                                          disabled={muvekkilOnaySaving === docId}
                                          onChange={(e) => toggleMuvekkilGorunurluk(docId, e.target.checked)}
                                        />
                                        <span>Müvekkil</span>
                                      </label>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                            <td>{formatFileSize(doc.FileSize ?? doc.fileSize ?? 0)}</td>
                            <td>{(doc.CreatedDate ?? doc.createdDate) ? formatDateTime(doc.CreatedDate ?? doc.createdDate) : '-'}</td>
                            <td>
                              {/* Muvekkilin yukledigi belgede sorumlu avukatin adi
                                  yaziliyordu; kimin yukledigi anlasilmiyordu. */}
                              {(doc.MuvekkilYukledi ?? doc.muvekkilYukledi)
                                ? <span className="doc-yukleyen-muvekkil">Müvekkil</span>
                                : (doc.UploadedByUserName ?? doc.uploadedByUserName ?? '-')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </YatayKaydirma>
              )}

              {/* Ödeme sekmelerinde Masraflar bölümünde girilen tutarlar da görünür */}
              {odemeSekmesinde && odemeSekmesi !== 'makbuz' && (
                <OdemeListesi
                  sekme={odemeSekmesi}
                  satirlar={odemeGruplari[odemeSekmesi] || []}
                  yukleniyor={loadingMasraflar}
                  onMasraflaraGit={masraflaraGit}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Task Assignment Form Modal */}
      {showTaskForm && caseFile && (
        <TaskForm
            task={null}
            caseFiles={[]}
            users={users}
            hideCaseFileSelection={true}
            caseFileId={parseInt(id)}
            onSubmit={async (taskData) => {
              setLoadingTask(true);
              try {
                await taskAPI.create(taskData);
                setShowTaskForm(false);
                await loadTasks();
                window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: null } }));
                alert('Görev başarıyla oluşturuldu');
              } catch (error) {
                console.error('Görev oluşturulurken hata:', error);
                alert('Görev oluşturulurken hata oluştu: ' + (error.response?.data?.message || error.message));
              } finally {
                setLoadingTask(false);
              }
            }}
            onCancel={() => setShowTaskForm(false)}
            loading={loadingTask}
          />
      )}

      {/* Task Complete/Edit Form Modal */}
      {editingTask && (
        <TaskForm
          task={editingTask}
          caseFiles={[]}
          users={users}
          onSubmit={handleTaskUpdate}
          onCancel={() => { setEditingTask(null); setEditingTaskMode('complete'); }}
          loading={loadingTask}
          editMode={editingTaskMode === 'edit'}
        />
      )}

      {/* Task Reject Modal */}
      {rejectingTask && (
        <div className="task-form-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="task-form" style={{ background: 'white', borderRadius: '8px', padding: '24px', maxWidth: '500px', width: '90%' }}>
            <h2>Görevi Reddet</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleRejectTask(); }}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Reddedilme Sebebi *</label>
                <textarea
                  value={rejectTaskForm.rejectionReason}
                  onChange={(e) => setRejectTaskForm({ ...rejectTaskForm, rejectionReason: e.target.value })}
                  className="form-control"
                  rows="4"
                  placeholder="Görevin neden reddedildiğini açıklayın..."
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Yeni Görev Son Tarihi</label>
                <input
                  type="date"
                  value={rejectTaskForm.newDueDate}
                  onChange={(e) => setRejectTaskForm({ ...rejectTaskForm, newDueDate: e.target.value })}
                  className="form-control"
                  min={getTodayDateString()}
                />
              </div>
              <div className="form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" onClick={() => { setRejectingTask(null); setRejectTaskForm({ rejectionReason: '', newDueDate: '' }); }} className="btn btn-secondary">İptal</button>
                <button type="submit" className="btn btn-danger" disabled={loadingTask}>
                  {loadingTask ? 'Reddediliyor...' : 'Reddet ve Yeni Görev Ata'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Case File Edit Form Modal */}
      {showEditForm && caseFile && (
        <CaseFileForm
            caseFile={caseFile}
            onSubmit={handleUpdate}
            onCancel={() => {
              setShowEditForm(false);
            }}
            onDelete={handleDelete}
            onArchiveRequest={handleSendArchiveRequest}
            loading={loading}
          />
      )}

      {/* Önizleme Modal */}
      {muvekkilGorunumuAcik && (
        <MuvekkilGorunumu
          caseFileId={caseFile.Id ?? caseFile.id}
          onKapat={muvekkilGorunumunuKapat}
        />
      )}

      {previewDocument && createPortal((() => {
        const currentIndex = documents.findIndex(d => (d.Id ?? d.id) === (previewDocument.Id ?? previewDocument.id));
        const hasPrevious = currentIndex > 0;
        const hasNext = currentIndex < documents.length - 1;
        const currentNumber = currentIndex + 1;
        const totalNumber = documents.length;
        
        return (
          <div className="preview-modal-overlay" onClick={closePreview}>
            <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="preview-modal-header">
                <h3>
                  {previewDocument.FileName ?? previewDocument.fileName ?? 'Dosya Önizleme'}
                  {totalNumber > 1 && (
                    <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                      ({currentNumber} / {totalNumber})
                    </span>
                  )}
                </h3>
                <button
                  type="button"
                  onClick={closePreview}
                  className="btn btn-secondary btn-sm preview-modal-close"
                  style={{ padding: '5px 10px', fontSize: '14px' }}
                >
                  ✕ Kapat
                </button>
              </div>
              <div className="preview-modal-body" style={{ position: 'relative' }}>
                {hasPrevious && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviousDocument();
                    }}
                    className="preview-nav-button preview-nav-button-left"
                    title="Önceki dosya (←)"
                  >
                    ‹
                  </button>
                )}
                {hasNext && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextDocument();
                    }}
                    className="preview-nav-button preview-nav-button-right"
                    title="Sonraki dosya (→)"
                  >
                    ›
                  </button>
                )}
                {previewLoading ? (
                  <div className="preview-loading">Yükleniyor...</div>
                ) : previewUrl ? (
                  (() => {
                    const fileName = previewDocument.FileName ?? previewDocument.fileName ?? '';
                    const contentType = previewDocument.ContentType ?? previewDocument.contentType ?? '';
                    const ext = getFileExtension(fileName);
                    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext) || contentType?.startsWith('image/');
                    const isPdf = ext === 'pdf' || contentType === 'application/pdf';
                    
                    if (isImage) {
                      return (
                        <div className="preview-image-container">
                          <img src={previewUrl} alt={fileName} className="preview-image" />
                        </div>
                      );
                    } else if (isPdf) {
                      return (
                        <div className="preview-pdf-container">
                          {/* iframe yerine pdf.js: iOS iframe'deki PDF'in yalnizca
                              ilk sayfasini gosterir ve kaydirmaya izin vermez. */}
                          <PdfGoruntuleyici kaynak={previewUrl} dosyaAdi={fileName} />
                        </div>
                      );
                    } else if (previewText && previewText.metin) {
                      // Word (.docx), UYAP (.udf) ve düz metin türleri
                      const turAdi = { docx: 'Word', udf: 'UYAP UDF', metin: 'Metin' }[previewText.tur] || 'Belge';
                      return (
                        <div className="preview-text-container">
                          <div className="preview-text-bar">
                            <span className="preview-text-tur">{turAdi} · metin önizlemesi</span>
                            <div className="preview-text-actions">
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => metniPdfOlarakYazdir(fileName || 'Belge', previewText.metin)}
                                title="Yazdırma penceresinde 'PDF olarak kaydet' seçin"
                              >
                                PDF olarak indir
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(previewUrl);
                                    await downloadBlob(await res.blob(), fileName || 'belge');
                                  } catch (e) { alert('İndirme başarısız: ' + (e.message || e)); }
                                }}
                              >
                                Özgün hâlini indir
                              </button>
                            </div>
                          </div>
                          <pre className="preview-text">{previewText.metin}</pre>
                          <p className="preview-text-not">
                            Bu bir metin önizlemesidir; özgün belgedeki biçimlendirme (tablo, yazı tipi, resim)
                            görünmez. Belgenin aslı için “Özgün hâlini indir”i kullanın.
                          </p>
                        </div>
                      );
                    } else {
                      return (
                        <div className="preview-unsupported">
                          <p>{previewText?.hata || 'Bu dosya türü önizlenemez.'}</p>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                // previewUrl blob URL ise fetch edip paylaş/indir
                                const res = await fetch(previewUrl);
                                const b = await res.blob();
                                await downloadBlob(b, fileName || 'belge');
                              } catch (e) {
                                alert('İndirme başarısız: ' + (e.message || e));
                              }
                            }}
                            className="btn btn-primary"
                          >
                            İndir
                          </button>
                        </div>
                      );
                    }
                  })()
                ) : (
                  <div className="preview-error">Dosya yüklenemedi.</div>
                )}
              </div>
            </div>
          </div>
        );
      })(), document.body)}

      {/* Not Form Modal */}
      {showNoteForm && (
        <NoteForm
          note={editingNote}
          onSubmit={async (noteData) => {
            try {
              if (editingNote) {
                await noteAPI.update(editingNote.Id || editingNote.id, {
                  ...noteData,
                  CaseFileId: parseInt(id)
                });
                alert('Not başarıyla güncellendi');
              } else {
                await noteAPI.create({
                  ...noteData,
                  CaseFileId: parseInt(id)
                });
                alert('Not başarıyla oluşturuldu');
              }
              setShowNoteForm(false);
              setEditingNote(null);
              await loadNotes();
            } catch (error) {
              alert('Not kaydedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
            }
          }}
          onDelete={async () => {
            if (!editingNote) return;
            try {
              await noteAPI.delete(editingNote.Id || editingNote.id);
              alert('Not başarıyla silindi');
              setShowNoteForm(false);
              setEditingNote(null);
              await loadNotes();
            } catch (error) {
              alert('Not silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
            }
          }}
          onCancel={() => {
            setShowNoteForm(false);
            setEditingNote(null);
          }}
          loading={loadingNotes}
        />
      )}

      {/* Masraf Talepleri Bölümü - Dosya detay en altı (Admin onay/red, avukat görüntüleme) */}
      {user && (
        <div className="detail-card">
          <div className="detail-section">
            <div className="section-header">
              <h2>Masraf Talepleri</h2>
            </div>

            {loadingMasrafTalepleri ? (
              <p>Yükleniyor...</p>
            ) : masrafTalepleri.length === 0 ? (
              <p className="text-muted" style={{ fontStyle: 'italic' }}>Bu dosyaya ait masraf talebi bulunmuyor.</p>
            ) : (
              <div className="masraflar-table-container">
                <table className="masraflar-table">
                  <thead>
                    <tr>
                      <th>Masraf Türü</th>
                      <th>Tutar</th>
                      <th>Tarih</th>
                      <th>Açıklama</th>
                      <th>Gelir/Gider</th>
                      <th>Durum</th>
                      {(user.Role === 'Admin' || user.role === 'Admin') && <th>İşlemler</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {masrafTalepleri.map((talep) => {
                      const formatCurrency = (amount) => {
                        if (amount === null || amount === undefined) return '-';
                        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
                      };
                      
                      const formatDate = (dateString) => {
                        if (!dateString) return '-';
                        try {
                          const date = new Date(dateString);
                          return date.toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                          });
                        } catch {
                          return dateString;
                        }
                      };

                      const getStatusBadge = (status) => {
                        const statusValue = status ?? 'Pending';
                        if (statusValue === null || statusValue === undefined || statusValue === '' || statusValue === 'Pending' || statusValue === 0) {
                          return <span className="status-badge status-pending">Talep Edildi</span>;
                        } else if (statusValue === 'Approved' || statusValue === 1) {
                          return <span className="status-badge status-approved">Onaylandı</span>;
                        } else if (statusValue === 'Rejected' || statusValue === 2) {
                          return <span className="status-badge status-rejected">Reddedildi</span>;
                        }
                        return <span className="status-badge">Talep Edildi</span>;
                      };

                      const isPending = (status) => {
                        const statusValue = status ?? 'Pending';
                        return statusValue === null || statusValue === undefined || statusValue === '' || statusValue === 'Pending' || statusValue === 0;
                      };

                      const handleApprove = async (e, talepId) => {
                        e.stopPropagation();
                        try {
                          await masrafRequestAPI.approve(talepId);
                          await loadMasrafTalepleri();
                          await loadMasraflar();
                        } catch (error) {
                          console.error('Masraf talebi onaylanırken hata:', error);
                          alert('Masraf talebi onaylanırken hata oluştu: ' + (error.response?.data?.message || error.message));
                        }
                      };

                      const handleReject = async (e, talepId) => {
                        e.stopPropagation();
                        const rejectionReason = window.prompt('Red nedeni giriniz:');
                        if (rejectionReason === null) {
                          return;
                        }
                        if (!rejectionReason.trim()) {
                          alert('Red nedeni boş olamaz!');
                          return;
                        }
                        try {
                          await masrafRequestAPI.reject(talepId, rejectionReason);
                          await loadMasrafTalepleri();
                          alert('Masraf talebi reddedildi!');
                        } catch (error) {
                          console.error('Masraf talebi reddedilirken hata:', error);
                          alert('Masraf talebi reddedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
                        }
                      };

                      const talepId = talep.id || talep.Id;
                      const talepStatus = talep.status || talep.Status;

                      return (
                        <tr key={talepId}>
                          <td>{talep.customMasrafTuru || talep.CustomMasrafTuru || talep.masrafTuru || talep.MasrafTuru || '-'}</td>
                          <td>{formatCurrency(talep.tutar || talep.Tutar)}</td>
                          <td>{formatDate(talep.tarih || talep.Tarih)}</td>
                          <td className="masraf-aciklama-cell">{talep.aciklama || talep.Aciklama || '-'}</td>
                          <td>{(talep.isGelir || talep.IsGelir) ? 'Gelir' : 'Gider'}</td>
                          <td>{getStatusBadge(talepStatus)}</td>
                          {(user.Role === 'Admin' || user.role === 'Admin') && (
                            <td>
                              {isPending(talepStatus) && (
                                <>
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={(e) => handleApprove(e, talepId)}
                                    style={{ marginRight: '5px' }}
                                  >
                                    Onayla
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={(e) => handleReject(e, talepId)}
                                  >
                                    Reddet
                                  </button>
                                </>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* İşi Tamamla Modal */}
      {completeModalJob && (
        <div className="modal-overlay" onClick={() => { setCompleteModalJob(null); setCompleteComment(''); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%', background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>İşi Tamamla</h3>
              <button type="button" onClick={() => { setCompleteModalJob(null); setCompleteComment(''); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#555' }}>Yorum (Opsiyonel)</label>
              <textarea
                value={completeComment}
                onChange={(e) => setCompleteComment(e.target.value)}
                rows={4}
                placeholder="Tamamlanırken eklemek istediğiniz yorum..."
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => { setCompleteModalJob(null); setCompleteComment(''); }}>İptal</button>
              <button type="button" className="btn btn-primary" onClick={handleCompleteWithComment} style={{ backgroundColor: '#28a745' }}>Tamamla</button>
            </div>
          </div>
        </div>
      )}

      {/* Job Form Modal */}
      {showJobForm && (
        <JobForm
          job={editingJob}
          onSubmit={async (jobData) => {
            try {
              if (editingJob) {
                // Güncelleme
                await jobAPI.update({
                  JobId: editingJob.Id || editingJob.id,
                  Description: jobData.Description,
                  DueDate: jobData.DueDate || null
                });
                alert('İş başarıyla güncellendi!');
              } else {
                // Yeni iş oluşturma
                await jobAPI.create({
                  Description: jobData.Description,
                  DueDate: jobData.DueDate || null,
                  CaseFileId: parseInt(id)
                });
                alert('İş başarıyla eklendi!');
              }
              setShowJobForm(false);
              setEditingJob(null);
              await loadJobs();
              // Custom event gönder - Dashboard sayfası dinleyebilir
              window.dispatchEvent(new CustomEvent('jobUpdated', { 
                detail: { caseFileId: parseInt(id) } 
              }));
            } catch (error) {
              console.error('İş kaydedilirken hata:', error);
              alert('İş kaydedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
            }
          }}
          onCancel={() => {
            setShowJobForm(false);
            setEditingJob(null);
          }}
          onDelete={async () => {
            if (!editingJob) return;
            try {
              await jobAPI.delete(editingJob.Id || editingJob.id);
              alert('İş başarıyla silindi!');
              setShowJobForm(false);
              setEditingJob(null);
              await loadJobs();
              // Custom event gönder - Dashboard sayfası dinleyebilir
              window.dispatchEvent(new CustomEvent('jobUpdated', { 
                detail: { caseFileId: parseInt(id) } 
              }));
            } catch (error) {
              console.error('İş silinirken hata:', error);
              alert('İş silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
            }
          }}
        />
      )}
    </div>
  );
};

export default CaseFileDetail;

